import type { IconSvgObject } from './types';

/**
 * Dynamic Icon Loader - stroke-rounded (5439 icons)
 * 
 * Load icons dynamically by name without importing all icons upfront.
 * Uses static import maps for maximum bundler compatibility.
 * 
 * @example
 * import { loadIcon } from '@hugeicons-pro/core-stroke-rounded/loader';
 * 
 * const icon = await loadIcon('Home01Icon');
 * // Use icon with @hugeicons/react or any other renderer
 */

// Static path map for Node/SSR
const paths: Record<string, string> = {
  FirstBracketCircleIcon: './FirstBracketCircleIcon.js',
  FirstBracketSquareIcon: './FirstBracketSquareIcon.js',
  FirstBracketIcon: './FirstBracketIcon.js',
  TwentyFourHoursClockIcon: './TwentyFourHoursClockIcon.js',
  SecondBracketCircleIcon: './SecondBracketCircleIcon.js',
  SecondBracketSquareIcon: './SecondBracketSquareIcon.js',
  SecondBracketIcon: './SecondBracketIcon.js',
  ThreeDViewIcon: './ThreeDViewIcon.js',
  ThreeDMoveIcon: './ThreeDMoveIcon.js',
  ThreeDPrinterIcon: './ThreeDPrinterIcon.js',
  ThreeDRotateIcon: './ThreeDRotateIcon.js',
  ThreeDScaleIcon: './ThreeDScaleIcon.js',
  ThirdBracketCircleIcon: './ThirdBracketCircleIcon.js',
  ThirdBracketSquareIcon: './ThirdBracketSquareIcon.js',
  ThirdBracketIcon: './ThirdBracketIcon.js',
  FourKIcon: './FourKIcon.js',
  SevenZ01Icon: './SevenZ01Icon.js',
  SevenZ02Icon: './SevenZ02Icon.js',
  AArrowDownIcon: './AArrowDownIcon.js',
  AArrowUpIcon: './AArrowUpIcon.js',
  ALargeSmallIcon: './ALargeSmallIcon.js',
  AbacusIcon: './AbacusIcon.js',
  AbsoluteIcon: './AbsoluteIcon.js',
  AccelerationIcon: './AccelerationIcon.js',
  AccessIcon: './AccessIcon.js',
  AccessibilityIcon: './AccessibilityIcon.js',
  AccidentIcon: './AccidentIcon.js',
  AccountRecoveryIcon: './AccountRecoveryIcon.js',
  AccountSetting01Icon: './AccountSetting01Icon.js',
  AccountSetting02Icon: './AccountSetting02Icon.js',
  AccountSetting03Icon: './AccountSetting03Icon.js',
  Activity01Icon: './Activity01Icon.js',
  Activity02Icon: './Activity02Icon.js',
  Activity03Icon: './Activity03Icon.js',
  Activity04Icon: './Activity04Icon.js',
  Activity05Icon: './Activity05Icon.js',
  ActivityCircleIcon: './ActivityCircleIcon.js',
  ActivitySparkIcon: './ActivitySparkIcon.js',
  AcuteIcon: './AcuteIcon.js',
  Add01Icon: './Add01Icon.js',
  Add02Icon: './Add02Icon.js',
  AddCircleHalfDotIcon: './AddCircleHalfDotIcon.js',
  AddCircleIcon: './AddCircleIcon.js',
  AddFemaleIcon: './AddFemaleIcon.js',
  AddInvoiceIcon: './AddInvoiceIcon.js',
  AddMaleIcon: './AddMaleIcon.js',
  AddMoneyCircleIcon: './AddMoneyCircleIcon.js',
  AddSquareIcon: './AddSquareIcon.js',
  AddTeam02Icon: './AddTeam02Icon.js',
  AddTeamIcon: './AddTeamIcon.js',
  AddToListIcon: './AddToListIcon.js',
  AddressBookIcon: './AddressBookIcon.js',
  AdjustPositionIcon: './AdjustPositionIcon.js',
  AdobeAfterEffectIcon: './AdobeAfterEffectIcon.js',
  AdobeIllustratorIcon: './AdobeIllustratorIcon.js',
  AdobeIndesignIcon: './AdobeIndesignIcon.js',
  AdobePhotoshopIcon: './AdobePhotoshopIcon.js',
  AdobePremierIcon: './AdobePremierIcon.js',
  AdobeXdIcon: './AdobeXdIcon.js',
  AdventureIcon: './AdventureIcon.js',
  AdvertisimentIcon: './AdvertisimentIcon.js',
  AdzanIcon: './AdzanIcon.js',
  AffiliateIcon: './AffiliateIcon.js',
  Agreement01Icon: './Agreement01Icon.js',
  Agreement02Icon: './Agreement02Icon.js',
  Agreement03Icon: './Agreement03Icon.js',
  AiAudioIcon: './AiAudioIcon.js',
  AiBeautifyIcon: './AiBeautifyIcon.js',
  AiBookIcon: './AiBookIcon.js',
  AiBrain01Icon: './AiBrain01Icon.js',
  AiBrain02Icon: './AiBrain02Icon.js',
  AiBrain03Icon: './AiBrain03Icon.js',
  AiBrain04Icon: './AiBrain04Icon.js',
  AiBrain05Icon: './AiBrain05Icon.js',
  AiBrowserIcon: './AiBrowserIcon.js',
  AiChat01Icon: './AiChat01Icon.js',
  AiChat02Icon: './AiChat02Icon.js',
  AiChemistry01Icon: './AiChemistry01Icon.js',
  AiChemistry02Icon: './AiChemistry02Icon.js',
  AiChemistry03Icon: './AiChemistry03Icon.js',
  AiChipIcon: './AiChipIcon.js',
  AiCloud01Icon: './AiCloud01Icon.js',
  AiCloud02Icon: './AiCloud02Icon.js',
  AiCloudIcon: './AiCloudIcon.js',
  AiComputerIcon: './AiComputerIcon.js',
  AiContentGenerator01Icon: './AiContentGenerator01Icon.js',
  AiContentGenerator02Icon: './AiContentGenerator02Icon.js',
  AiDnaIcon: './AiDnaIcon.js',
  AiEditingIcon: './AiEditingIcon.js',
  AiEraserIcon: './AiEraserIcon.js',
  AiFileIcon: './AiFileIcon.js',
  AiFolder01Icon: './AiFolder01Icon.js',
  AiFolder02Icon: './AiFolder02Icon.js',
  AiGameIcon: './AiGameIcon.js',
  AiGenerativeIcon: './AiGenerativeIcon.js',
  AiIdeaIcon: './AiIdeaIcon.js',
  AiImageIcon: './AiImageIcon.js',
  AiInnovation01Icon: './AiInnovation01Icon.js',
  AiInnovation02Icon: './AiInnovation02Icon.js',
  AiInnovation03Icon: './AiInnovation03Icon.js',
  AiLaptopIcon: './AiLaptopIcon.js',
  AiLearningIcon: './AiLearningIcon.js',
  AiLockIcon: './AiLockIcon.js',
  AiMagicIcon: './AiMagicIcon.js',
  AiMail01Icon: './AiMail01Icon.js',
  AiMail02Icon: './AiMail02Icon.js',
  AiMailIcon: './AiMailIcon.js',
  AiMicIcon: './AiMicIcon.js',
  AiNetworkIcon: './AiNetworkIcon.js',
  AiPhone01Icon: './AiPhone01Icon.js',
  AiPhone02Icon: './AiPhone02Icon.js',
  AiProgrammingIcon: './AiProgrammingIcon.js',
  AiScanIcon: './AiScanIcon.js',
  AiSchedulingIcon: './AiSchedulingIcon.js',
  AiSearch02Icon: './AiSearch02Icon.js',
  AiSearchIcon: './AiSearchIcon.js',
  AiSecurity01Icon: './AiSecurity01Icon.js',
  AiSecurity02Icon: './AiSecurity02Icon.js',
  AiSecurity03Icon: './AiSecurity03Icon.js',
  AiSettingIcon: './AiSettingIcon.js',
  AiSheetsIcon: './AiSheetsIcon.js',
  AiSmartwatchIcon: './AiSmartwatchIcon.js',
  AiUserIcon: './AiUserIcon.js',
  AiVideoIcon: './AiVideoIcon.js',
  AiViewIcon: './AiViewIcon.js',
  AiVisionRecognitionIcon: './AiVisionRecognitionIcon.js',
  AiVoiceGeneratorIcon: './AiVoiceGeneratorIcon.js',
  AiVoiceIcon: './AiVoiceIcon.js',
  AiWebBrowsingIcon: './AiWebBrowsingIcon.js',
  AidsIcon: './AidsIcon.js',
  AirVentIcon: './AirVentIcon.js',
  AirbnbIcon: './AirbnbIcon.js',
  AircraftGameIcon: './AircraftGameIcon.js',
  AirdropIcon: './AirdropIcon.js',
  Airplane01Icon: './Airplane01Icon.js',
  Airplane02Icon: './Airplane02Icon.js',
  AirplaneLanding01Icon: './AirplaneLanding01Icon.js',
  AirplaneLanding02Icon: './AirplaneLanding02Icon.js',
  AirplaneModeOffIcon: './AirplaneModeOffIcon.js',
  AirplaneModeIcon: './AirplaneModeIcon.js',
  AirplaneSeat02Icon: './AirplaneSeat02Icon.js',
  AirplaneSeatIcon: './AirplaneSeatIcon.js',
  AirplaneTakeOff01Icon: './AirplaneTakeOff01Icon.js',
  AirplaneTakeOff02Icon: './AirplaneTakeOff02Icon.js',
  AirplayLineIcon: './AirplayLineIcon.js',
  AirplayIcon: './AirplayIcon.js',
  Airpod01Icon: './Airpod01Icon.js',
  Airpod02Icon: './Airpod02Icon.js',
  Airpod03Icon: './Airpod03Icon.js',
  Airport02Icon: './Airport02Icon.js',
  AirportIcon: './AirportIcon.js',
  AirportTowerIcon: './AirportTowerIcon.js',
  AlAqsaMosqueIcon: './AlAqsaMosqueIcon.js',
  AlarmClockCheckIcon: './AlarmClockCheckIcon.js',
  AlarmClockMinusIcon: './AlarmClockMinusIcon.js',
  AlarmClockOffIcon: './AlarmClockOffIcon.js',
  AlarmClockPlusIcon: './AlarmClockPlusIcon.js',
  AlarmClockIcon: './AlarmClockIcon.js',
  AlarmSmokeIcon: './AlarmSmokeIcon.js',
  Album01Icon: './Album01Icon.js',
  Album02Icon: './Album02Icon.js',
  AlbumNotFound01Icon: './AlbumNotFound01Icon.js',
  AlbumNotFound02Icon: './AlbumNotFound02Icon.js',
  AlbumIcon: './AlbumIcon.js',
  Alert01Icon: './Alert01Icon.js',
  Alert02Icon: './Alert02Icon.js',
  AlertCircleIcon: './AlertCircleIcon.js',
  AlertDiamondIcon: './AlertDiamondIcon.js',
  AlertSquareIcon: './AlertSquareIcon.js',
  AlgorithmIcon: './AlgorithmIcon.js',
  Alien01Icon: './Alien01Icon.js',
  Alien02Icon: './Alien02Icon.js',
  AlignBottomIcon: './AlignBottomIcon.js',
  AlignBoxBottomCenterIcon: './AlignBoxBottomCenterIcon.js',
  AlignBoxBottomLeftIcon: './AlignBoxBottomLeftIcon.js',
  AlignBoxBottomRightIcon: './AlignBoxBottomRightIcon.js',
  AlignBoxMiddleCenterIcon: './AlignBoxMiddleCenterIcon.js',
  AlignBoxMiddleLeftIcon: './AlignBoxMiddleLeftIcon.js',
  AlignBoxMiddleRightIcon: './AlignBoxMiddleRightIcon.js',
  AlignBoxTopCenterIcon: './AlignBoxTopCenterIcon.js',
  AlignBoxTopLeftIcon: './AlignBoxTopLeftIcon.js',
  AlignBoxTopRightIcon: './AlignBoxTopRightIcon.js',
  AlignEndHorizontalIcon: './AlignEndHorizontalIcon.js',
  AlignEndVerticalIcon: './AlignEndVerticalIcon.js',
  AlignHorizontalCenterIcon: './AlignHorizontalCenterIcon.js',
  AlignHorizontalDistributeCenterIcon: './AlignHorizontalDistributeCenterIcon.js',
  AlignHorizontalDistributeEndIcon: './AlignHorizontalDistributeEndIcon.js',
  AlignHorizontalDistributeStartIcon: './AlignHorizontalDistributeStartIcon.js',
  AlignHorizontalJustifyCenterIcon: './AlignHorizontalJustifyCenterIcon.js',
  AlignHorizontalJustifyEndIcon: './AlignHorizontalJustifyEndIcon.js',
  AlignHorizontalJustifyStartIcon: './AlignHorizontalJustifyStartIcon.js',
  AlignHorizontalSpaceAroundIcon: './AlignHorizontalSpaceAroundIcon.js',
  AlignHorizontalSpaceBetweenIcon: './AlignHorizontalSpaceBetweenIcon.js',
  AlignKeyObjectIcon: './AlignKeyObjectIcon.js',
  AlignLeftIcon: './AlignLeftIcon.js',
  AlignRightIcon: './AlignRightIcon.js',
  AlignSelectionIcon: './AlignSelectionIcon.js',
  AlignStartHorizontalIcon: './AlignStartHorizontalIcon.js',
  AlignStartVerticalIcon: './AlignStartVerticalIcon.js',
  AlignTopIcon: './AlignTopIcon.js',
  AlignVerticalCenterIcon: './AlignVerticalCenterIcon.js',
  AlignVerticalDistributeCenterIcon: './AlignVerticalDistributeCenterIcon.js',
  AlignVerticalDistributeEndIcon: './AlignVerticalDistributeEndIcon.js',
  AlignVerticalDistributeStartIcon: './AlignVerticalDistributeStartIcon.js',
  AlignVerticalJustifyCenterIcon: './AlignVerticalJustifyCenterIcon.js',
  AlignVerticalJustifyEndIcon: './AlignVerticalJustifyEndIcon.js',
  AlignVerticalJustifyStartIcon: './AlignVerticalJustifyStartIcon.js',
  AlignVerticalSpaceAroundIcon: './AlignVerticalSpaceAroundIcon.js',
  AlignVerticalSpaceBetweenIcon: './AlignVerticalSpaceBetweenIcon.js',
  AllBookmarkIcon: './AllBookmarkIcon.js',
  AllahIcon: './AllahIcon.js',
  AlmsIcon: './AlmsIcon.js',
  AlphaCircleIcon: './AlphaCircleIcon.js',
  AlphaSquareIcon: './AlphaSquareIcon.js',
  AlphaIcon: './AlphaIcon.js',
  AlphabetArabicIcon: './AlphabetArabicIcon.js',
  AlphabetBanglaIcon: './AlphabetBanglaIcon.js',
  AlphabetChineseIcon: './AlphabetChineseIcon.js',
  AlphabetGreekIcon: './AlphabetGreekIcon.js',
  AlphabetHebrewIcon: './AlphabetHebrewIcon.js',
  AlphabetHindiIcon: './AlphabetHindiIcon.js',
  AlphabetJapaneseIcon: './AlphabetJapaneseIcon.js',
  AlphabetKoreanIcon: './AlphabetKoreanIcon.js',
  AlphabetThaiIcon: './AlphabetThaiIcon.js',
  AmazonIcon: './AmazonIcon.js',
  AmbulanceIcon: './AmbulanceIcon.js',
  AmericanFootballIcon: './AmericanFootballIcon.js',
  AmieIcon: './AmieIcon.js',
  AmmoIcon: './AmmoIcon.js',
  AmpersandIcon: './AmpersandIcon.js',
  AmpersandsIcon: './AmpersandsIcon.js',
  AmphoraIcon: './AmphoraIcon.js',
  AmpouleIcon: './AmpouleIcon.js',
  AnalysisTextLinkIcon: './AnalysisTextLinkIcon.js',
  Analytics01Icon: './Analytics01Icon.js',
  Analytics02Icon: './Analytics02Icon.js',
  Analytics03Icon: './Analytics03Icon.js',
  AnalyticsDownIcon: './AnalyticsDownIcon.js',
  AnalyticsUpIcon: './AnalyticsUpIcon.js',
  AnchorPointIcon: './AnchorPointIcon.js',
  AnchorIcon: './AnchorIcon.js',
  AndroidIcon: './AndroidIcon.js',
  AngelIcon: './AngelIcon.js',
  Angle01Icon: './Angle01Icon.js',
  AngleIcon: './AngleIcon.js',
  AngryBirdIcon: './AngryBirdIcon.js',
  AngryIcon: './AngryIcon.js',
  AnnoyedIcon: './AnnoyedIcon.js',
  AnonymousIcon: './AnonymousIcon.js',
  AntennaIcon: './AntennaIcon.js',
  AnvilIcon: './AnvilIcon.js',
  ApartmentIcon: './ApartmentIcon.js',
  ApertureIcon: './ApertureIcon.js',
  ApiGatewayIcon: './ApiGatewayIcon.js',
  ApiIcon: './ApiIcon.js',
  AppStoreIcon: './AppStoreIcon.js',
  AppWindowMacIcon: './AppWindowMacIcon.js',
  AppWindowIcon: './AppWindowIcon.js',
  Apple01Icon: './Apple01Icon.js',
  AppleFinderIcon: './AppleFinderIcon.js',
  AppleIntelligenceIcon: './AppleIntelligenceIcon.js',
  AppleMusicIcon: './AppleMusicIcon.js',
  AppleNewsIcon: './AppleNewsIcon.js',
  ApplePieIcon: './ApplePieIcon.js',
  AppleReminderIcon: './AppleReminderIcon.js',
  AppleStocksIcon: './AppleStocksIcon.js',
  AppleIcon: './AppleIcon.js',
  AppleVisionProIcon: './AppleVisionProIcon.js',
  Appointment01Icon: './Appointment01Icon.js',
  Appointment02Icon: './Appointment02Icon.js',
  ApproximatelyEqualCircleIcon: './ApproximatelyEqualCircleIcon.js',
  ApproximatelyEqualSquareIcon: './ApproximatelyEqualSquareIcon.js',
  ApproximatelyEqualIcon: './ApproximatelyEqualIcon.js',
  ApricotIcon: './ApricotIcon.js',
  ApronIcon: './ApronIcon.js',
  ArcBrowserIcon: './ArcBrowserIcon.js',
  ArcherIcon: './ArcherIcon.js',
  Archive01Icon: './Archive01Icon.js',
  Archive02Icon: './Archive02Icon.js',
  Archive03Icon: './Archive03Icon.js',
  Archive04Icon: './Archive04Icon.js',
  ArchiveArrowDownIcon: './ArchiveArrowDownIcon.js',
  ArchiveArrowUpIcon: './ArchiveArrowUpIcon.js',
  ArchiveOff03Icon: './ArchiveOff03Icon.js',
  ArchiveOff04Icon: './ArchiveOff04Icon.js',
  ArchiveRestoreIcon: './ArchiveRestoreIcon.js',
  ArchiveIcon: './ArchiveIcon.js',
  ArchiveXIcon: './ArchiveXIcon.js',
  ArmchairIcon: './ArmchairIcon.js',
  ArmoredBootIcon: './ArmoredBootIcon.js',
  ArrangeByLettersAZIcon: './ArrangeByLettersAZIcon.js',
  ArrangeByLettersZAIcon: './ArrangeByLettersZAIcon.js',
  ArrangeByNumbersOneNineIcon: './ArrangeByNumbersOneNineIcon.js',
  ArrangeByNumbersNineOneIcon: './ArrangeByNumbersNineOneIcon.js',
  ArrangeIcon: './ArrangeIcon.js',
  ArrowAllDirectionIcon: './ArrowAllDirectionIcon.js',
  ArrowBigDownDashIcon: './ArrowBigDownDashIcon.js',
  ArrowBigLeftDashIcon: './ArrowBigLeftDashIcon.js',
  ArrowBigRightDashIcon: './ArrowBigRightDashIcon.js',
  ArrowBigUpDashIcon: './ArrowBigUpDashIcon.js',
  ArrowDataTransferDiagonalIcon: './ArrowDataTransferDiagonalIcon.js',
  ArrowDataTransferHorizontalIcon: './ArrowDataTransferHorizontalIcon.js',
  ArrowDataTransferVerticalIcon: './ArrowDataTransferVerticalIcon.js',
  ArrowDiagonalIcon: './ArrowDiagonalIcon.js',
  ArrowDownZeroOneIcon: './ArrowDownZeroOneIcon.js',
  ArrowDown01Icon: './ArrowDown01Icon.js',
  ArrowDown02Icon: './ArrowDown02Icon.js',
  ArrowDown03Icon: './ArrowDown03Icon.js',
  ArrowDown04Icon: './ArrowDown04Icon.js',
  ArrowDown05Icon: './ArrowDown05Icon.js',
  ArrowDownOneZeroIcon: './ArrowDownOneZeroIcon.js',
  ArrowDownAZIcon: './ArrowDownAZIcon.js',
  ArrowDownAzIcon: './ArrowDownAzIcon.js',
  ArrowDownBigIcon: './ArrowDownBigIcon.js',
  ArrowDownDoubleIcon: './ArrowDownDoubleIcon.js',
  ArrowDownFromLineIcon: './ArrowDownFromLineIcon.js',
  ArrowDownLeft01Icon: './ArrowDownLeft01Icon.js',
  ArrowDownLeft02Icon: './ArrowDownLeft02Icon.js',
  ArrowDownNarrowWideIcon: './ArrowDownNarrowWideIcon.js',
  ArrowDownRight01Icon: './ArrowDownRight01Icon.js',
  ArrowDownRight02Icon: './ArrowDownRight02Icon.js',
  ArrowDownToDotIcon: './ArrowDownToDotIcon.js',
  ArrowDownToLineIcon: './ArrowDownToLineIcon.js',
  ArrowDownWideNarrowIcon: './ArrowDownWideNarrowIcon.js',
  ArrowDownZaIcon: './ArrowDownZaIcon.js',
  ArrowExpand01Icon: './ArrowExpand01Icon.js',
  ArrowExpand02Icon: './ArrowExpand02Icon.js',
  ArrowExpandDiagonal01Icon: './ArrowExpandDiagonal01Icon.js',
  ArrowExpandDiagonal02Icon: './ArrowExpandDiagonal02Icon.js',
  ArrowExpandIcon: './ArrowExpandIcon.js',
  ArrowHorizontalIcon: './ArrowHorizontalIcon.js',
  ArrowLeft01Icon: './ArrowLeft01Icon.js',
  ArrowLeft02Icon: './ArrowLeft02Icon.js',
  ArrowLeft03Icon: './ArrowLeft03Icon.js',
  ArrowLeft04Icon: './ArrowLeft04Icon.js',
  ArrowLeft05Icon: './ArrowLeft05Icon.js',
  ArrowLeftBigIcon: './ArrowLeftBigIcon.js',
  ArrowLeftDoubleIcon: './ArrowLeftDoubleIcon.js',
  ArrowLeftFromLineIcon: './ArrowLeftFromLineIcon.js',
  ArrowLeftRightIcon: './ArrowLeftRightIcon.js',
  ArrowLeftToLineIcon: './ArrowLeftToLineIcon.js',
  ArrowMoveDownLeftIcon: './ArrowMoveDownLeftIcon.js',
  ArrowMoveDownRightIcon: './ArrowMoveDownRightIcon.js',
  ArrowMoveLeftDownIcon: './ArrowMoveLeftDownIcon.js',
  ArrowMoveRightDownIcon: './ArrowMoveRightDownIcon.js',
  ArrowMoveUpLeftIcon: './ArrowMoveUpLeftIcon.js',
  ArrowMoveUpRightIcon: './ArrowMoveUpRightIcon.js',
  ArrowReloadHorizontalIcon: './ArrowReloadHorizontalIcon.js',
  ArrowReloadVerticalIcon: './ArrowReloadVerticalIcon.js',
  ArrowRight01Icon: './ArrowRight01Icon.js',
  ArrowRight02Icon: './ArrowRight02Icon.js',
  ArrowRight03Icon: './ArrowRight03Icon.js',
  ArrowRight04Icon: './ArrowRight04Icon.js',
  ArrowRight05Icon: './ArrowRight05Icon.js',
  ArrowRightBigIcon: './ArrowRightBigIcon.js',
  ArrowRightDoubleIcon: './ArrowRightDoubleIcon.js',
  ArrowRightFromLineIcon: './ArrowRightFromLineIcon.js',
  ArrowRightToLineIcon: './ArrowRightToLineIcon.js',
  ArrowShrink01Icon: './ArrowShrink01Icon.js',
  ArrowShrink02Icon: './ArrowShrink02Icon.js',
  ArrowShrinkIcon: './ArrowShrinkIcon.js',
  ArrowTurnBackwardIcon: './ArrowTurnBackwardIcon.js',
  ArrowTurnDownIcon: './ArrowTurnDownIcon.js',
  ArrowTurnForwardIcon: './ArrowTurnForwardIcon.js',
  ArrowTurnUpIcon: './ArrowTurnUpIcon.js',
  ArrowUpZeroOneIcon: './ArrowUpZeroOneIcon.js',
  ArrowUp01Icon: './ArrowUp01Icon.js',
  ArrowUp02Icon: './ArrowUp02Icon.js',
  ArrowUp03Icon: './ArrowUp03Icon.js',
  ArrowUp04Icon: './ArrowUp04Icon.js',
  ArrowUp05Icon: './ArrowUp05Icon.js',
  ArrowUpOneZeroIcon: './ArrowUpOneZeroIcon.js',
  ArrowUpAzIcon: './ArrowUpAzIcon.js',
  ArrowUpBigIcon: './ArrowUpBigIcon.js',
  ArrowUpDoubleIcon: './ArrowUpDoubleIcon.js',
  ArrowUpDownIcon: './ArrowUpDownIcon.js',
  ArrowUpFromDotIcon: './ArrowUpFromDotIcon.js',
  ArrowUpFromLineIcon: './ArrowUpFromLineIcon.js',
  ArrowUpLeft01Icon: './ArrowUpLeft01Icon.js',
  ArrowUpLeft02Icon: './ArrowUpLeft02Icon.js',
  ArrowUpNarrowWideIcon: './ArrowUpNarrowWideIcon.js',
  ArrowUpRight01Icon: './ArrowUpRight01Icon.js',
  ArrowUpRight02Icon: './ArrowUpRight02Icon.js',
  ArrowUpRight03Icon: './ArrowUpRight03Icon.js',
  ArrowUpRightStackIcon: './ArrowUpRightStackIcon.js',
  ArrowUpToLineIcon: './ArrowUpToLineIcon.js',
  ArrowUpWideNarrowIcon: './ArrowUpWideNarrowIcon.js',
  ArrowUpZAIcon: './ArrowUpZAIcon.js',
  ArrowUpZaIcon: './ArrowUpZaIcon.js',
  ArrowVerticalIcon: './ArrowVerticalIcon.js',
  ArrowsUpFromLineIcon: './ArrowsUpFromLineIcon.js',
  ArtboardIcon: './ArtboardIcon.js',
  ArtboardToolIcon: './ArtboardToolIcon.js',
  ArtificialIntelligence01Icon: './ArtificialIntelligence01Icon.js',
  ArtificialIntelligence02Icon: './ArtificialIntelligence02Icon.js',
  ArtificialIntelligence03Icon: './ArtificialIntelligence03Icon.js',
  ArtificialIntelligence04Icon: './ArtificialIntelligence04Icon.js',
  ArtificialIntelligence05Icon: './ArtificialIntelligence05Icon.js',
  ArtificialIntelligence06Icon: './ArtificialIntelligence06Icon.js',
  ArtificialIntelligence07Icon: './ArtificialIntelligence07Icon.js',
  ArtificialIntelligence08Icon: './ArtificialIntelligence08Icon.js',
  AspectRatioIcon: './AspectRatioIcon.js',
  AssignmentsIcon: './AssignmentsIcon.js',
  Asterisk02Icon: './Asterisk02Icon.js',
  AsteriskIcon: './AsteriskIcon.js',
  Asteroid01Icon: './Asteroid01Icon.js',
  Asteroid02Icon: './Asteroid02Icon.js',
  Astronaut01Icon: './Astronaut01Icon.js',
  Astronaut02Icon: './Astronaut02Icon.js',
  AtSignIcon: './AtSignIcon.js',
  AtIcon: './AtIcon.js',
  Atm01Icon: './Atm01Icon.js',
  Atm02Icon: './Atm02Icon.js',
  Atom01Icon: './Atom01Icon.js',
  Atom02Icon: './Atom02Icon.js',
  AtomicPowerIcon: './AtomicPowerIcon.js',
  Attachment01Icon: './Attachment01Icon.js',
  Attachment02Icon: './Attachment02Icon.js',
  AttachmentCircleIcon: './AttachmentCircleIcon.js',
  AttachmentSquareIcon: './AttachmentSquareIcon.js',
  AttachmentIcon: './AttachmentIcon.js',
  AuctionIcon: './AuctionIcon.js',
  AudioBook01Icon: './AudioBook01Icon.js',
  AudioBook02Icon: './AudioBook02Icon.js',
  AudioBook03Icon: './AudioBook03Icon.js',
  AudioBook04Icon: './AudioBook04Icon.js',
  AudioLinesIcon: './AudioLinesIcon.js',
  AudioWave01Icon: './AudioWave01Icon.js',
  AudioWave02Icon: './AudioWave02Icon.js',
  AudioWaveformIcon: './AudioWaveformIcon.js',
  Audit01Icon: './Audit01Icon.js',
  Audit02Icon: './Audit02Icon.js',
  AugmentedRealityArIcon: './AugmentedRealityArIcon.js',
  AuthorizedIcon: './AuthorizedIcon.js',
  AutoConversationsIcon: './AutoConversationsIcon.js',
  AutomotiveBattery01Icon: './AutomotiveBattery01Icon.js',
  AutomotiveBattery02Icon: './AutomotiveBattery02Icon.js',
  AvalancheIcon: './AvalancheIcon.js',
  AvocadoIcon: './AvocadoIcon.js',
  Award01Icon: './Award01Icon.js',
  Award02Icon: './Award02Icon.js',
  Award03Icon: './Award03Icon.js',
  Award04Icon: './Award04Icon.js',
  Award05Icon: './Award05Icon.js',
  AwsLambdaIcon: './AwsLambdaIcon.js',
  AxeIcon: './AxeIcon.js',
  AxisThreeDIcon: './AxisThreeDIcon.js',
  Baby01Icon: './Baby01Icon.js',
  Baby02Icon: './Baby02Icon.js',
  BabyBed01Icon: './BabyBed01Icon.js',
  BabyBed02Icon: './BabyBed02Icon.js',
  BabyBottleIcon: './BabyBottleIcon.js',
  BabyBoyDressIcon: './BabyBoyDressIcon.js',
  BabyGirlDressIcon: './BabyGirlDressIcon.js',
  BackMuscleBodyIcon: './BackMuscleBodyIcon.js',
  BackgroundIcon: './BackgroundIcon.js',
  Backpack01Icon: './Backpack01Icon.js',
  Backpack02Icon: './Backpack02Icon.js',
  Backpack03Icon: './Backpack03Icon.js',
  Backward01Icon: './Backward01Icon.js',
  Backward02Icon: './Backward02Icon.js',
  BacteriaIcon: './BacteriaIcon.js',
  BadgeAlertIcon: './BadgeAlertIcon.js',
  BadgeCentIcon: './BadgeCentIcon.js',
  BadgeCheckIcon: './BadgeCheckIcon.js',
  BadgeDollarSignIcon: './BadgeDollarSignIcon.js',
  BadgeEuroIcon: './BadgeEuroIcon.js',
  BadgeIndianRupeeIcon: './BadgeIndianRupeeIcon.js',
  BadgeInfoIcon: './BadgeInfoIcon.js',
  BadgeJapaneseYenIcon: './BadgeJapaneseYenIcon.js',
  BadgeMinusIcon: './BadgeMinusIcon.js',
  BadgePercentIcon: './BadgePercentIcon.js',
  BadgePlusIcon: './BadgePlusIcon.js',
  BadgePoundSterlingIcon: './BadgePoundSterlingIcon.js',
  BadgeQuestionMarkIcon: './BadgeQuestionMarkIcon.js',
  BadgeRussianRubleIcon: './BadgeRussianRubleIcon.js',
  BadgeIcon: './BadgeIcon.js',
  BadgeSwissFrancIcon: './BadgeSwissFrancIcon.js',
  BadgeTurkishLiraIcon: './BadgeTurkishLiraIcon.js',
  BadgeXIcon: './BadgeXIcon.js',
  BadmintonShuttleIcon: './BadmintonShuttleIcon.js',
  BadmintonIcon: './BadmintonIcon.js',
  BaggageClaimIcon: './BaggageClaimIcon.js',
  BalanceScaleIcon: './BalanceScaleIcon.js',
  BalloonIcon: './BalloonIcon.js',
  BalloonsIcon: './BalloonsIcon.js',
  BanIcon: './BanIcon.js',
  BananaIcon: './BananaIcon.js',
  BandageIcon: './BandageIcon.js',
  BankIcon: './BankIcon.js',
  BanknoteArrowDownIcon: './BanknoteArrowDownIcon.js',
  BanknoteArrowUpIcon: './BanknoteArrowUpIcon.js',
  BanknoteIcon: './BanknoteIcon.js',
  BanknoteXIcon: './BanknoteXIcon.js',
  BarChartHorizontalIcon: './BarChartHorizontalIcon.js',
  BarChartIcon: './BarChartIcon.js',
  BarCode01Icon: './BarCode01Icon.js',
  BarCode02Icon: './BarCode02Icon.js',
  BarcodeScanIcon: './BarcodeScanIcon.js',
  BarcodeIcon: './BarcodeIcon.js',
  BarnsIcon: './BarnsIcon.js',
  BarrelIcon: './BarrelIcon.js',
  BaseballBatIcon: './BaseballBatIcon.js',
  BaseballHelmetIcon: './BaseballHelmetIcon.js',
  BaseballIcon: './BaseballIcon.js',
  BaselineIcon: './BaselineIcon.js',
  BashIcon: './BashIcon.js',
  Basketball01Icon: './Basketball01Icon.js',
  Basketball02Icon: './Basketball02Icon.js',
  BasketballHoopIcon: './BasketballHoopIcon.js',
  BathIcon: './BathIcon.js',
  Bathtub01Icon: './Bathtub01Icon.js',
  Bathtub02Icon: './Bathtub02Icon.js',
  BatteriesEnergyIcon: './BatteriesEnergyIcon.js',
  BatteryCharging01Icon: './BatteryCharging01Icon.js',
  BatteryCharging02Icon: './BatteryCharging02Icon.js',
  BatteryEcoChargingIcon: './BatteryEcoChargingIcon.js',
  BatteryEmptyIcon: './BatteryEmptyIcon.js',
  BatteryFullIcon: './BatteryFullIcon.js',
  BatteryLowIcon: './BatteryLowIcon.js',
  BatteryMedium01Icon: './BatteryMedium01Icon.js',
  BatteryMedium02Icon: './BatteryMedium02Icon.js',
  BatteryPlusIcon: './BatteryPlusIcon.js',
  BatteryIcon: './BatteryIcon.js',
  BatteryWarningIcon: './BatteryWarningIcon.js',
  BbqGrillIcon: './BbqGrillIcon.js',
  Beach02Icon: './Beach02Icon.js',
  BeachIcon: './BeachIcon.js',
  BeakerIcon: './BeakerIcon.js',
  BeanOffIcon: './BeanOffIcon.js',
  BeanIcon: './BeanIcon.js',
  BeaterIcon: './BeaterIcon.js',
  BeboIcon: './BeboIcon.js',
  BedBunkIcon: './BedBunkIcon.js',
  BedDoubleIcon: './BedDoubleIcon.js',
  BedSingle01Icon: './BedSingle01Icon.js',
  BedSingle02Icon: './BedSingle02Icon.js',
  BedIcon: './BedIcon.js',
  Bedug01Icon: './Bedug01Icon.js',
  Bedug02Icon: './Bedug02Icon.js',
  BeefOffIcon: './BeefOffIcon.js',
  BeefIcon: './BeefIcon.js',
  BeerOffIcon: './BeerOffIcon.js',
  BeerIcon: './BeerIcon.js',
  Behance01Icon: './Behance01Icon.js',
  Behance02Icon: './Behance02Icon.js',
  BellDotIcon: './BellDotIcon.js',
  BellElectricIcon: './BellElectricIcon.js',
  BellMinusIcon: './BellMinusIcon.js',
  BellOffIcon: './BellOffIcon.js',
  BellPlusIcon: './BellPlusIcon.js',
  BellRingIcon: './BellRingIcon.js',
  BellIcon: './BellIcon.js',
  BeltIcon: './BeltIcon.js',
  BendToolIcon: './BendToolIcon.js',
  BerlinIcon: './BerlinIcon.js',
  BerlinTowerIcon: './BerlinTowerIcon.js',
  BetaIcon: './BetaIcon.js',
  BetweenHorizontalEndIcon: './BetweenHorizontalEndIcon.js',
  BetweenHorizontalStartIcon: './BetweenHorizontalStartIcon.js',
  BetweenVerticalEndIcon: './BetweenVerticalEndIcon.js',
  BetweenVerticalStartIcon: './BetweenVerticalStartIcon.js',
  BicepsFlexedIcon: './BicepsFlexedIcon.js',
  Bicycle01Icon: './Bicycle01Icon.js',
  BicycleIcon: './BicycleIcon.js',
  BikeIcon: './BikeIcon.js',
  Billiard01Icon: './Billiard01Icon.js',
  Billiard02Icon: './Billiard02Icon.js',
  BinaryCodeIcon: './BinaryCodeIcon.js',
  BinaryIcon: './BinaryIcon.js',
  BingIcon: './BingIcon.js',
  BinocularsIcon: './BinocularsIcon.js',
  BioEnergyIcon: './BioEnergyIcon.js',
  BiohazardIcon: './BiohazardIcon.js',
  BiomassEnergyIcon: './BiomassEnergyIcon.js',
  BiometricAccessIcon: './BiometricAccessIcon.js',
  BiometricDeviceIcon: './BiometricDeviceIcon.js',
  BirdIcon: './BirdIcon.js',
  BirdhouseIcon: './BirdhouseIcon.js',
  BirthdayCakeIcon: './BirthdayCakeIcon.js',
  BiscuitIcon: './BiscuitIcon.js',
  Bitcoin01Icon: './Bitcoin01Icon.js',
  Bitcoin02Icon: './Bitcoin02Icon.js',
  Bitcoin03Icon: './Bitcoin03Icon.js',
  Bitcoin04Icon: './Bitcoin04Icon.js',
  BitcoinBagIcon: './BitcoinBagIcon.js',
  BitcoinCircleIcon: './BitcoinCircleIcon.js',
  BitcoinCloudIcon: './BitcoinCloudIcon.js',
  BitcoinCpuIcon: './BitcoinCpuIcon.js',
  BitcoinCreditCardIcon: './BitcoinCreditCardIcon.js',
  BitcoinDatabaseIcon: './BitcoinDatabaseIcon.js',
  BitcoinDown01Icon: './BitcoinDown01Icon.js',
  BitcoinDown02Icon: './BitcoinDown02Icon.js',
  BitcoinEllipseIcon: './BitcoinEllipseIcon.js',
  BitcoinEyeIcon: './BitcoinEyeIcon.js',
  BitcoinFilterIcon: './BitcoinFilterIcon.js',
  BitcoinFlashdiskIcon: './BitcoinFlashdiskIcon.js',
  BitcoinGraphIcon: './BitcoinGraphIcon.js',
  BitcoinInvoiceIcon: './BitcoinInvoiceIcon.js',
  BitcoinKeyIcon: './BitcoinKeyIcon.js',
  BitcoinLocationIcon: './BitcoinLocationIcon.js',
  BitcoinLockIcon: './BitcoinLockIcon.js',
  BitcoinMailIcon: './BitcoinMailIcon.js',
  BitcoinMindIcon: './BitcoinMindIcon.js',
  BitcoinMoney01Icon: './BitcoinMoney01Icon.js',
  BitcoinMoney02Icon: './BitcoinMoney02Icon.js',
  BitcoinPieChartIcon: './BitcoinPieChartIcon.js',
  BitcoinPiggyBankIcon: './BitcoinPiggyBankIcon.js',
  BitcoinPresentationIcon: './BitcoinPresentationIcon.js',
  BitcoinReceiptIcon: './BitcoinReceiptIcon.js',
  BitcoinReceiveIcon: './BitcoinReceiveIcon.js',
  BitcoinRectangleIcon: './BitcoinRectangleIcon.js',
  BitcoinSafeIcon: './BitcoinSafeIcon.js',
  BitcoinSearchIcon: './BitcoinSearchIcon.js',
  BitcoinSendIcon: './BitcoinSendIcon.js',
  BitcoinSettingIcon: './BitcoinSettingIcon.js',
  BitcoinShieldIcon: './BitcoinShieldIcon.js',
  BitcoinShoppingIcon: './BitcoinShoppingIcon.js',
  BitcoinSmartphone01Icon: './BitcoinSmartphone01Icon.js',
  BitcoinSmartphone02Icon: './BitcoinSmartphone02Icon.js',
  BitcoinSquareIcon: './BitcoinSquareIcon.js',
  BitcoinStoreIcon: './BitcoinStoreIcon.js',
  BitcoinIcon: './BitcoinIcon.js',
  BitcoinTagIcon: './BitcoinTagIcon.js',
  BitcoinTargetIcon: './BitcoinTargetIcon.js',
  BitcoinTransactionIcon: './BitcoinTransactionIcon.js',
  BitcoinUp01Icon: './BitcoinUp01Icon.js',
  BitcoinUp02Icon: './BitcoinUp02Icon.js',
  BitcoinWalletIcon: './BitcoinWalletIcon.js',
  BitcoinWithdrawIcon: './BitcoinWithdrawIcon.js',
  BlackHole01Icon: './BlackHole01Icon.js',
  BlackHoleIcon: './BlackHoleIcon.js',
  BlendIcon: './BlendIcon.js',
  BlenderIcon: './BlenderIcon.js',
  BlindsIcon: './BlindsIcon.js',
  BlockGameIcon: './BlockGameIcon.js',
  Blockchain01Icon: './Blockchain01Icon.js',
  Blockchain02Icon: './Blockchain02Icon.js',
  Blockchain03Icon: './Blockchain03Icon.js',
  Blockchain04Icon: './Blockchain04Icon.js',
  Blockchain05Icon: './Blockchain05Icon.js',
  Blockchain06Icon: './Blockchain06Icon.js',
  Blockchain07Icon: './Blockchain07Icon.js',
  BlockedIcon: './BlockedIcon.js',
  BlocksIcon: './BlocksIcon.js',
  BloggerIcon: './BloggerIcon.js',
  BloodBagIcon: './BloodBagIcon.js',
  BloodBottleIcon: './BloodBottleIcon.js',
  BloodPressureIcon: './BloodPressureIcon.js',
  BloodIcon: './BloodIcon.js',
  BloodTypeIcon: './BloodTypeIcon.js',
  BlueskyIcon: './BlueskyIcon.js',
  BluetoothCircleIcon: './BluetoothCircleIcon.js',
  BluetoothConnectedIcon: './BluetoothConnectedIcon.js',
  BluetoothNotConnectedIcon: './BluetoothNotConnectedIcon.js',
  BluetoothOffIcon: './BluetoothOffIcon.js',
  BluetoothSearchIcon: './BluetoothSearchIcon.js',
  BluetoothSearchingIcon: './BluetoothSearchingIcon.js',
  BluetoothSquareIcon: './BluetoothSquareIcon.js',
  BluetoothIcon: './BluetoothIcon.js',
  BlurIcon: './BlurIcon.js',
  BlushBrush01Icon: './BlushBrush01Icon.js',
  BlushBrush02Icon: './BlushBrush02Icon.js',
  BoardMathIcon: './BoardMathIcon.js',
  BoatIcon: './BoatIcon.js',
  BodyArmorIcon: './BodyArmorIcon.js',
  BodyPartLegIcon: './BodyPartLegIcon.js',
  BodyPartMuscleIcon: './BodyPartMuscleIcon.js',
  BodyPartSixPackIcon: './BodyPartSixPackIcon.js',
  BodySoapIcon: './BodySoapIcon.js',
  BodyWeightIcon: './BodyWeightIcon.js',
  BoldIcon: './BoldIcon.js',
  BoltIcon: './BoltIcon.js',
  BombIcon: './BombIcon.js',
  Bone01Icon: './Bone01Icon.js',
  Bone02Icon: './Bone02Icon.js',
  Book01Icon: './Book01Icon.js',
  Book02Icon: './Book02Icon.js',
  Book03Icon: './Book03Icon.js',
  Book04Icon: './Book04Icon.js',
  BookAIcon: './BookAIcon.js',
  BookAlertIcon: './BookAlertIcon.js',
  BookBookmark01Icon: './BookBookmark01Icon.js',
  BookBookmark02Icon: './BookBookmark02Icon.js',
  BookCheckIcon: './BookCheckIcon.js',
  BookCopyIcon: './BookCopyIcon.js',
  BookDashedIcon: './BookDashedIcon.js',
  BookDownIcon: './BookDownIcon.js',
  BookDownloadIcon: './BookDownloadIcon.js',
  BookEditIcon: './BookEditIcon.js',
  BookHeadphonesIcon: './BookHeadphonesIcon.js',
  BookHeartIcon: './BookHeartIcon.js',
  BookImageIcon: './BookImageIcon.js',
  BookKeyIcon: './BookKeyIcon.js',
  BookLockIcon: './BookLockIcon.js',
  BookMarkedIcon: './BookMarkedIcon.js',
  BookMinusIcon: './BookMinusIcon.js',
  BookOpen01Icon: './BookOpen01Icon.js',
  BookOpen02Icon: './BookOpen02Icon.js',
  BookOpenCheckIcon: './BookOpenCheckIcon.js',
  BookOpenTextIcon: './BookOpenTextIcon.js',
  BookPlusIcon: './BookPlusIcon.js',
  BookSearchIcon: './BookSearchIcon.js',
  BookTextIcon: './BookTextIcon.js',
  BookTypeIcon: './BookTypeIcon.js',
  BookUpTwoIcon: './BookUpTwoIcon.js',
  BookUploadIcon: './BookUploadIcon.js',
  BookUserIcon: './BookUserIcon.js',
  BookXIcon: './BookXIcon.js',
  Bookmark01Icon: './Bookmark01Icon.js',
  Bookmark02Icon: './Bookmark02Icon.js',
  Bookmark03Icon: './Bookmark03Icon.js',
  BookmarkAdd01Icon: './BookmarkAdd01Icon.js',
  BookmarkAdd02Icon: './BookmarkAdd02Icon.js',
  BookmarkBlock01Icon: './BookmarkBlock01Icon.js',
  BookmarkBlock02Icon: './BookmarkBlock02Icon.js',
  BookmarkCheck01Icon: './BookmarkCheck01Icon.js',
  BookmarkCheck02Icon: './BookmarkCheck02Icon.js',
  BookmarkMinus01Icon: './BookmarkMinus01Icon.js',
  BookmarkMinus02Icon: './BookmarkMinus02Icon.js',
  BookmarkOff01Icon: './BookmarkOff01Icon.js',
  BookmarkOff02Icon: './BookmarkOff02Icon.js',
  BookmarkPlusIcon: './BookmarkPlusIcon.js',
  BookmarkRemove01Icon: './BookmarkRemove01Icon.js',
  BookmarkRemove02Icon: './BookmarkRemove02Icon.js',
  BookmarkXIcon: './BookmarkXIcon.js',
  Books01Icon: './Books01Icon.js',
  Books02Icon: './Books02Icon.js',
  Bookshelf01Icon: './Bookshelf01Icon.js',
  Bookshelf02Icon: './Bookshelf02Icon.js',
  Bookshelf03Icon: './Bookshelf03Icon.js',
  BoomBoxIcon: './BoomBoxIcon.js',
  BootstrapIcon: './BootstrapIcon.js',
  BorderAll01Icon: './BorderAll01Icon.js',
  BorderAll02Icon: './BorderAll02Icon.js',
  BorderBottom01Icon: './BorderBottom01Icon.js',
  BorderBottom02Icon: './BorderBottom02Icon.js',
  BorderFullIcon: './BorderFullIcon.js',
  BorderHorizontalIcon: './BorderHorizontalIcon.js',
  BorderInnerIcon: './BorderInnerIcon.js',
  BorderLeft01Icon: './BorderLeft01Icon.js',
  BorderLeft02Icon: './BorderLeft02Icon.js',
  BorderNone01Icon: './BorderNone01Icon.js',
  BorderNone02Icon: './BorderNone02Icon.js',
  BorderRight01Icon: './BorderRight01Icon.js',
  BorderRight02Icon: './BorderRight02Icon.js',
  BorderTop01Icon: './BorderTop01Icon.js',
  BorderTop02Icon: './BorderTop02Icon.js',
  BorderVerticalIcon: './BorderVerticalIcon.js',
  BorobudurIcon: './BorobudurIcon.js',
  BotMessageSquareIcon: './BotMessageSquareIcon.js',
  BotOffIcon: './BotOffIcon.js',
  BotIcon: './BotIcon.js',
  BounceLeftIcon: './BounceLeftIcon.js',
  BounceRightIcon: './BounceRightIcon.js',
  BoundingBoxIcon: './BoundingBoxIcon.js',
  BowArrowIcon: './BowArrowIcon.js',
  BowTieIcon: './BowTieIcon.js',
  BowlingBallIcon: './BowlingBallIcon.js',
  BowlingPinsIcon: './BowlingPinsIcon.js',
  BowlingIcon: './BowlingIcon.js',
  BoxIcon: './BoxIcon.js',
  BoxerIcon: './BoxerIcon.js',
  BoxesIcon: './BoxesIcon.js',
  BoxingBagIcon: './BoxingBagIcon.js',
  BoxingGlove01Icon: './BoxingGlove01Icon.js',
  BoxingGloveIcon: './BoxingGloveIcon.js',
  BracesIcon: './BracesIcon.js',
  BracketsIcon: './BracketsIcon.js',
  Brain01Icon: './Brain01Icon.js',
  Brain02Icon: './Brain02Icon.js',
  Brain03Icon: './Brain03Icon.js',
  BrainCircuitIcon: './BrainCircuitIcon.js',
  BrainCogIcon: './BrainCogIcon.js',
  BrainIcon: './BrainIcon.js',
  BrandfetchIcon: './BrandfetchIcon.js',
  Bread01Icon: './Bread01Icon.js',
  Bread02Icon: './Bread02Icon.js',
  Bread03Icon: './Bread03Icon.js',
  Bread04Icon: './Bread04Icon.js',
  BreastPumpIcon: './BreastPumpIcon.js',
  BrickWallFireIcon: './BrickWallFireIcon.js',
  BrickWallShieldIcon: './BrickWallShieldIcon.js',
  BrickWallIcon: './BrickWallIcon.js',
  BridgeIcon: './BridgeIcon.js',
  Briefcase01Icon: './Briefcase01Icon.js',
  Briefcase02Icon: './Briefcase02Icon.js',
  Briefcase03Icon: './Briefcase03Icon.js',
  Briefcase04Icon: './Briefcase04Icon.js',
  Briefcase05Icon: './Briefcase05Icon.js',
  Briefcase06Icon: './Briefcase06Icon.js',
  Briefcase07Icon: './Briefcase07Icon.js',
  Briefcase08Icon: './Briefcase08Icon.js',
  Briefcase09Icon: './Briefcase09Icon.js',
  BriefcaseBusinessIcon: './BriefcaseBusinessIcon.js',
  BriefcaseConveyorBeltIcon: './BriefcaseConveyorBeltIcon.js',
  BriefcaseDollarIcon: './BriefcaseDollarIcon.js',
  BriefcaseMedicalIcon: './BriefcaseMedicalIcon.js',
  BringToFrontIcon: './BringToFrontIcon.js',
  BroccoliIcon: './BroccoliIcon.js',
  BrochureIcon: './BrochureIcon.js',
  BrokenBoneIcon: './BrokenBoneIcon.js',
  BrowserIcon: './BrowserIcon.js',
  BrushCleaningIcon: './BrushCleaningIcon.js',
  BrushIcon: './BrushIcon.js',
  BubbleChatAddIcon: './BubbleChatAddIcon.js',
  BubbleChatBlockedIcon: './BubbleChatBlockedIcon.js',
  BubbleChatCancelIcon: './BubbleChatCancelIcon.js',
  BubbleChatDelayIcon: './BubbleChatDelayIcon.js',
  BubbleChatDoneIcon: './BubbleChatDoneIcon.js',
  BubbleChatDownload01Icon: './BubbleChatDownload01Icon.js',
  BubbleChatDownload02Icon: './BubbleChatDownload02Icon.js',
  BubbleChatEditIcon: './BubbleChatEditIcon.js',
  BubbleChatFavouriteIcon: './BubbleChatFavouriteIcon.js',
  BubbleChatIncomeIcon: './BubbleChatIncomeIcon.js',
  BubbleChatLockIcon: './BubbleChatLockIcon.js',
  BubbleChatNotificationIcon: './BubbleChatNotificationIcon.js',
  BubbleChatOutcomeIcon: './BubbleChatOutcomeIcon.js',
  BubbleChatPreviewIcon: './BubbleChatPreviewIcon.js',
  BubbleChatQuestionIcon: './BubbleChatQuestionIcon.js',
  BubbleChatSearchIcon: './BubbleChatSearchIcon.js',
  BubbleChatSecureIcon: './BubbleChatSecureIcon.js',
  BubbleChatSpark01Icon: './BubbleChatSpark01Icon.js',
  BubbleChatSparkIcon: './BubbleChatSparkIcon.js',
  BubbleChatIcon: './BubbleChatIcon.js',
  BubbleChatTemporaryIcon: './BubbleChatTemporaryIcon.js',
  BubbleChatTranslateIcon: './BubbleChatTranslateIcon.js',
  BubbleChatUnlockIcon: './BubbleChatUnlockIcon.js',
  BubbleChatUploadIcon: './BubbleChatUploadIcon.js',
  BubbleChatUserIcon: './BubbleChatUserIcon.js',
  BubbleTea01Icon: './BubbleTea01Icon.js',
  BubbleTea02Icon: './BubbleTea02Icon.js',
  BubblesIcon: './BubblesIcon.js',
  BucketIcon: './BucketIcon.js',
  Bug01Icon: './Bug01Icon.js',
  Bug02Icon: './Bug02Icon.js',
  Building01Icon: './Building01Icon.js',
  Building02Icon: './Building02Icon.js',
  Building03Icon: './Building03Icon.js',
  Building04Icon: './Building04Icon.js',
  Building05Icon: './Building05Icon.js',
  Building06Icon: './Building06Icon.js',
  BulbChargingIcon: './BulbChargingIcon.js',
  BulbIcon: './BulbIcon.js',
  BulletIcon: './BulletIcon.js',
  BulletproofVestIcon: './BulletproofVestIcon.js',
  BurjAlArabIcon: './BurjAlArabIcon.js',
  BurningCdIcon: './BurningCdIcon.js',
  Bus01Icon: './Bus01Icon.js',
  Bus02Icon: './Bus02Icon.js',
  Bus03Icon: './Bus03Icon.js',
  BusFrontIcon: './BusFrontIcon.js',
  CIcon: './CIcon.js',
  CProgrammingIcon: './CProgrammingIcon.js',
  Cabinet01Icon: './Cabinet01Icon.js',
  Cabinet02Icon: './Cabinet02Icon.js',
  Cabinet03Icon: './Cabinet03Icon.js',
  Cabinet04Icon: './Cabinet04Icon.js',
  CableCarIcon: './CableCarIcon.js',
  CableIcon: './CableIcon.js',
  CactusIcon: './CactusIcon.js',
  CaduceusIcon: './CaduceusIcon.js',
  CafeIcon: './CafeIcon.js',
  CakeSliceIcon: './CakeSliceIcon.js',
  CakeIcon: './CakeIcon.js',
  CalculateIcon: './CalculateIcon.js',
  Calculator01Icon: './Calculator01Icon.js',
  CalculatorIcon: './CalculatorIcon.js',
  Calendar01Icon: './Calendar01Icon.js',
  Calendar02Icon: './Calendar02Icon.js',
  Calendar03Icon: './Calendar03Icon.js',
  Calendar04Icon: './Calendar04Icon.js',
  Calendar05Icon: './Calendar05Icon.js',
  CalendarAdd01Icon: './CalendarAdd01Icon.js',
  CalendarAdd02Icon: './CalendarAdd02Icon.js',
  CalendarAnalysisIcon: './CalendarAnalysisIcon.js',
  CalendarArrowDownIcon: './CalendarArrowDownIcon.js',
  CalendarArrowUpIcon: './CalendarArrowUpIcon.js',
  CalendarBlock01Icon: './CalendarBlock01Icon.js',
  CalendarBlock02Icon: './CalendarBlock02Icon.js',
  CalendarCheckIn01Icon: './CalendarCheckIn01Icon.js',
  CalendarCheckIn02Icon: './CalendarCheckIn02Icon.js',
  CalendarCheckOut01Icon: './CalendarCheckOut01Icon.js',
  CalendarCheckOut02Icon: './CalendarCheckOut02Icon.js',
  CalendarCheckIcon: './CalendarCheckIcon.js',
  CalendarClockIcon: './CalendarClockIcon.js',
  CalendarCogIcon: './CalendarCogIcon.js',
  CalendarDaysIcon: './CalendarDaysIcon.js',
  CalendarDownload01Icon: './CalendarDownload01Icon.js',
  CalendarDownload02Icon: './CalendarDownload02Icon.js',
  CalendarFavorite01Icon: './CalendarFavorite01Icon.js',
  CalendarFavorite02Icon: './CalendarFavorite02Icon.js',
  CalendarFoldIcon: './CalendarFoldIcon.js',
  CalendarHeartIcon: './CalendarHeartIcon.js',
  CalendarLock01Icon: './CalendarLock01Icon.js',
  CalendarLock02Icon: './CalendarLock02Icon.js',
  CalendarLove01Icon: './CalendarLove01Icon.js',
  CalendarLove02Icon: './CalendarLove02Icon.js',
  CalendarMinus01Icon: './CalendarMinus01Icon.js',
  CalendarMinus02Icon: './CalendarMinus02Icon.js',
  CalendarMortarboardIcon: './CalendarMortarboardIcon.js',
  CalendarOffIcon: './CalendarOffIcon.js',
  CalendarPlusIcon: './CalendarPlusIcon.js',
  CalendarRangeIcon: './CalendarRangeIcon.js',
  CalendarRemove01Icon: './CalendarRemove01Icon.js',
  CalendarRemove02Icon: './CalendarRemove02Icon.js',
  CalendarSearchIcon: './CalendarSearchIcon.js',
  CalendarSetting01Icon: './CalendarSetting01Icon.js',
  CalendarSetting02Icon: './CalendarSetting02Icon.js',
  CalendarSyncIcon: './CalendarSyncIcon.js',
  CalendarUpload01Icon: './CalendarUpload01Icon.js',
  CalendarUpload02Icon: './CalendarUpload02Icon.js',
  CalendarUserIcon: './CalendarUserIcon.js',
  CalendarXIcon: './CalendarXIcon.js',
  CalendarsIcon: './CalendarsIcon.js',
  Call02Icon: './Call02Icon.js',
  CallAdd02Icon: './CallAdd02Icon.js',
  CallAddIcon: './CallAddIcon.js',
  CallBlocked02Icon: './CallBlocked02Icon.js',
  CallBlockedIcon: './CallBlockedIcon.js',
  CallDisabled02Icon: './CallDisabled02Icon.js',
  CallDisabledIcon: './CallDisabledIcon.js',
  CallDone02Icon: './CallDone02Icon.js',
  CallDoneIcon: './CallDoneIcon.js',
  CallEnd01Icon: './CallEnd01Icon.js',
  CallEnd02Icon: './CallEnd02Icon.js',
  CallEnd03Icon: './CallEnd03Icon.js',
  CallEnd04Icon: './CallEnd04Icon.js',
  CallIncoming01Icon: './CallIncoming01Icon.js',
  CallIncoming02Icon: './CallIncoming02Icon.js',
  CallIncoming03Icon: './CallIncoming03Icon.js',
  CallIncoming04Icon: './CallIncoming04Icon.js',
  CallInternal02Icon: './CallInternal02Icon.js',
  CallInternalIcon: './CallInternalIcon.js',
  CallLocked02Icon: './CallLocked02Icon.js',
  CallLockedIcon: './CallLockedIcon.js',
  CallMinus02Icon: './CallMinus02Icon.js',
  CallMinusIcon: './CallMinusIcon.js',
  CallMissed01Icon: './CallMissed01Icon.js',
  CallMissed02Icon: './CallMissed02Icon.js',
  CallMissed03Icon: './CallMissed03Icon.js',
  CallMissed04Icon: './CallMissed04Icon.js',
  CallOutgoing01Icon: './CallOutgoing01Icon.js',
  CallOutgoing02Icon: './CallOutgoing02Icon.js',
  CallOutgoing03Icon: './CallOutgoing03Icon.js',
  CallOutgoing04Icon: './CallOutgoing04Icon.js',
  CallPaused02Icon: './CallPaused02Icon.js',
  CallPausedIcon: './CallPausedIcon.js',
  CallReceived02Icon: './CallReceived02Icon.js',
  CallReceivedIcon: './CallReceivedIcon.js',
  CallRinging01Icon: './CallRinging01Icon.js',
  CallRinging02Icon: './CallRinging02Icon.js',
  CallRinging03Icon: './CallRinging03Icon.js',
  CallRinging04Icon: './CallRinging04Icon.js',
  CallSpark01Icon: './CallSpark01Icon.js',
  CallSpark02Icon: './CallSpark02Icon.js',
  CallIcon: './CallIcon.js',
  CallUnlocked02Icon: './CallUnlocked02Icon.js',
  CallUnlockedIcon: './CallUnlockedIcon.js',
  Calling02Icon: './Calling02Icon.js',
  CallingIcon: './CallingIcon.js',
  CamelIcon: './CamelIcon.js',
  Camera01Icon: './Camera01Icon.js',
  Camera02Icon: './Camera02Icon.js',
  Camera03Icon: './Camera03Icon.js',
  CameraAdd01Icon: './CameraAdd01Icon.js',
  CameraAdd02Icon: './CameraAdd02Icon.js',
  CameraAdd03Icon: './CameraAdd03Icon.js',
  CameraAiIcon: './CameraAiIcon.js',
  CameraAutomatically01Icon: './CameraAutomatically01Icon.js',
  CameraAutomatically02Icon: './CameraAutomatically02Icon.js',
  CameraLensIcon: './CameraLensIcon.js',
  CameraMicrophone01Icon: './CameraMicrophone01Icon.js',
  CameraMicrophone02Icon: './CameraMicrophone02Icon.js',
  CameraNightMode01Icon: './CameraNightMode01Icon.js',
  CameraNightMode02Icon: './CameraNightMode02Icon.js',
  CameraOff01Icon: './CameraOff01Icon.js',
  CameraOff02Icon: './CameraOff02Icon.js',
  CameraRotated01Icon: './CameraRotated01Icon.js',
  CameraRotated02Icon: './CameraRotated02Icon.js',
  CameraSmile01Icon: './CameraSmile01Icon.js',
  CameraSmile02Icon: './CameraSmile02Icon.js',
  CameraTripodIcon: './CameraTripodIcon.js',
  CameraVideoIcon: './CameraVideoIcon.js',
  CamperIcon: './CamperIcon.js',
  CampfireIcon: './CampfireIcon.js',
  Cancel01Icon: './Cancel01Icon.js',
  Cancel02Icon: './Cancel02Icon.js',
  CancelCircleHalfDotIcon: './CancelCircleHalfDotIcon.js',
  CancelCircleIcon: './CancelCircleIcon.js',
  CancelSquareIcon: './CancelSquareIcon.js',
  Candelier01Icon: './Candelier01Icon.js',
  Candelier02Icon: './Candelier02Icon.js',
  CandyCaneIcon: './CandyCaneIcon.js',
  CandyOffIcon: './CandyOffIcon.js',
  CandyIcon: './CandyIcon.js',
  CannabisOffIcon: './CannabisOffIcon.js',
  CannabisIcon: './CannabisIcon.js',
  CanvasIcon: './CanvasIcon.js',
  CapProjectingIcon: './CapProjectingIcon.js',
  CapRoundIcon: './CapRoundIcon.js',
  CapStraightIcon: './CapStraightIcon.js',
  CapIcon: './CapIcon.js',
  CapcutRectangleIcon: './CapcutRectangleIcon.js',
  CapcutIcon: './CapcutIcon.js',
  CaptionsOffIcon: './CaptionsOffIcon.js',
  CaptionsIcon: './CaptionsIcon.js',
  Car01Icon: './Car01Icon.js',
  Car02Icon: './Car02Icon.js',
  Car03Icon: './Car03Icon.js',
  Car04Icon: './Car04Icon.js',
  Car05Icon: './Car05Icon.js',
  CarAlertIcon: './CarAlertIcon.js',
  CarFrontIcon: './CarFrontIcon.js',
  CarParking01Icon: './CarParking01Icon.js',
  CarParking02Icon: './CarParking02Icon.js',
  CarSignalIcon: './CarSignalIcon.js',
  CarTaxiFrontIcon: './CarTaxiFrontIcon.js',
  CarTimeIcon: './CarTimeIcon.js',
  CaravanIcon: './CaravanIcon.js',
  CardExchange01Icon: './CardExchange01Icon.js',
  CardExchange02Icon: './CardExchange02Icon.js',
  CardSimIcon: './CardSimIcon.js',
  CardiganIcon: './CardiganIcon.js',
  Cardiogram01Icon: './Cardiogram01Icon.js',
  Cardiogram02Icon: './Cardiogram02Icon.js',
  Cards01Icon: './Cards01Icon.js',
  Cards02Icon: './Cards02Icon.js',
  CargoShipIcon: './CargoShipIcon.js',
  CarouselHorizontal02Icon: './CarouselHorizontal02Icon.js',
  CarouselHorizontalIcon: './CarouselHorizontalIcon.js',
  CarouselVerticalIcon: './CarouselVerticalIcon.js',
  CarrotIcon: './CarrotIcon.js',
  CaseLowerIcon: './CaseLowerIcon.js',
  CaseSensitiveIcon: './CaseSensitiveIcon.js',
  CaseUpperIcon: './CaseUpperIcon.js',
  Cash01Icon: './Cash01Icon.js',
  Cash02Icon: './Cash02Icon.js',
  CashbackBitcoinIcon: './CashbackBitcoinIcon.js',
  CashbackEuroIcon: './CashbackEuroIcon.js',
  CashbackPoundIcon: './CashbackPoundIcon.js',
  CashbackIcon: './CashbackIcon.js',
  CashbackYenIcon: './CashbackYenIcon.js',
  Cashier02Icon: './Cashier02Icon.js',
  CashierIcon: './CashierIcon.js',
  CassetteTapeIcon: './CassetteTapeIcon.js',
  CastIcon: './CastIcon.js',
  CastboxIcon: './CastboxIcon.js',
  Castle01Icon: './Castle01Icon.js',
  Castle02Icon: './Castle02Icon.js',
  CastleIcon: './CastleIcon.js',
  CatIcon: './CatIcon.js',
  CatalogueIcon: './CatalogueIcon.js',
  CayanTowerIcon: './CayanTowerIcon.js',
  CctvCameraIcon: './CctvCameraIcon.js',
  CctvOffIcon: './CctvOffIcon.js',
  CctvIcon: './CctvIcon.js',
  CdIcon: './CdIcon.js',
  CellsIcon: './CellsIcon.js',
  CellularNetworkOfflineIcon: './CellularNetworkOfflineIcon.js',
  CellularNetworkIcon: './CellularNetworkIcon.js',
  CelsiusIcon: './CelsiusIcon.js',
  CenterFocusIcon: './CenterFocusIcon.js',
  CentralShaheedMinarIcon: './CentralShaheedMinarIcon.js',
  CentralizedIcon: './CentralizedIcon.js',
  Certificate01Icon: './Certificate01Icon.js',
  Certificate02Icon: './Certificate02Icon.js',
  Chair01Icon: './Chair01Icon.js',
  Chair02Icon: './Chair02Icon.js',
  Chair03Icon: './Chair03Icon.js',
  Chair04Icon: './Chair04Icon.js',
  Chair05Icon: './Chair05Icon.js',
  ChairBarberIcon: './ChairBarberIcon.js',
  ChampionIcon: './ChampionIcon.js',
  ChangeScreenModeIcon: './ChangeScreenModeIcon.js',
  CharacterPhoneticIcon: './CharacterPhoneticIcon.js',
  CharityIcon: './CharityIcon.js',
  Chart01Icon: './Chart01Icon.js',
  Chart02Icon: './Chart02Icon.js',
  Chart03Icon: './Chart03Icon.js',
  ChartAnalysisIcon: './ChartAnalysisIcon.js',
  ChartAreaIcon: './ChartAreaIcon.js',
  ChartAverageIcon: './ChartAverageIcon.js',
  ChartBarBigIcon: './ChartBarBigIcon.js',
  ChartBarDecreasingIcon: './ChartBarDecreasingIcon.js',
  ChartBarIncreasingIcon: './ChartBarIncreasingIcon.js',
  ChartBarLineIcon: './ChartBarLineIcon.js',
  ChartBarStackedIcon: './ChartBarStackedIcon.js',
  ChartBreakoutCircleIcon: './ChartBreakoutCircleIcon.js',
  ChartBreakoutSquareIcon: './ChartBreakoutSquareIcon.js',
  ChartBubble01Icon: './ChartBubble01Icon.js',
  ChartBubble02Icon: './ChartBubble02Icon.js',
  ChartCandleIcon: './ChartCandleIcon.js',
  ChartCandlestickIcon: './ChartCandlestickIcon.js',
  ChartColumnBigIcon: './ChartColumnBigIcon.js',
  ChartColumnDecreasingIcon: './ChartColumnDecreasingIcon.js',
  ChartColumnIncreasingIcon: './ChartColumnIncreasingIcon.js',
  ChartColumnStackedIcon: './ChartColumnStackedIcon.js',
  ChartColumnIcon: './ChartColumnIcon.js',
  ChartDecreaseIcon: './ChartDecreaseIcon.js',
  ChartDownIcon: './ChartDownIcon.js',
  ChartEvaluationIcon: './ChartEvaluationIcon.js',
  ChartGanttIcon: './ChartGanttIcon.js',
  ChartHighLowIcon: './ChartHighLowIcon.js',
  ChartHistogramIcon: './ChartHistogramIcon.js',
  ChartIncreaseIcon: './ChartIncreaseIcon.js',
  ChartLineData01Icon: './ChartLineData01Icon.js',
  ChartLineData02Icon: './ChartLineData02Icon.js',
  ChartLineData03Icon: './ChartLineData03Icon.js',
  ChartLineIcon: './ChartLineIcon.js',
  ChartMaximumIcon: './ChartMaximumIcon.js',
  ChartMediumIcon: './ChartMediumIcon.js',
  ChartMinimumIcon: './ChartMinimumIcon.js',
  ChartNetworkIcon: './ChartNetworkIcon.js',
  ChartNoAxesColumnDecreasingIcon: './ChartNoAxesColumnDecreasingIcon.js',
  ChartNoAxesColumnIncreasingIcon: './ChartNoAxesColumnIncreasingIcon.js',
  ChartNoAxesColumnIcon: './ChartNoAxesColumnIcon.js',
  ChartNoAxesCombinedIcon: './ChartNoAxesCombinedIcon.js',
  ChartNoAxesGanttIcon: './ChartNoAxesGanttIcon.js',
  ChartRadarIcon: './ChartRadarIcon.js',
  ChartRelationshipIcon: './ChartRelationshipIcon.js',
  ChartRingIcon: './ChartRingIcon.js',
  ChartRoseIcon: './ChartRoseIcon.js',
  ChartScatterIcon: './ChartScatterIcon.js',
  ChartSplineIcon: './ChartSplineIcon.js',
  ChartIcon: './ChartIcon.js',
  ChartUpIcon: './ChartUpIcon.js',
  Chat01Icon: './Chat01Icon.js',
  ChatAdd01Icon: './ChatAdd01Icon.js',
  ChatAddIcon: './ChatAddIcon.js',
  ChatBlocked01Icon: './ChatBlocked01Icon.js',
  ChatBlockedIcon: './ChatBlockedIcon.js',
  ChatBotIcon: './ChatBotIcon.js',
  ChatCancel01Icon: './ChatCancel01Icon.js',
  ChatCancelIcon: './ChatCancelIcon.js',
  ChatDelay01Icon: './ChatDelay01Icon.js',
  ChatDelayIcon: './ChatDelayIcon.js',
  ChatDone01Icon: './ChatDone01Icon.js',
  ChatDoneIcon: './ChatDoneIcon.js',
  ChatDownload01Icon: './ChatDownload01Icon.js',
  ChatDownloadIcon: './ChatDownloadIcon.js',
  ChatEdit01Icon: './ChatEdit01Icon.js',
  ChatEditIcon: './ChatEditIcon.js',
  ChatFavourite01Icon: './ChatFavourite01Icon.js',
  ChatFavouriteIcon: './ChatFavouriteIcon.js',
  ChatFeedback01Icon: './ChatFeedback01Icon.js',
  ChatFeedbackIcon: './ChatFeedbackIcon.js',
  ChatGptIcon: './ChatGptIcon.js',
  ChatIncome01Icon: './ChatIncome01Icon.js',
  ChatIncomeIcon: './ChatIncomeIcon.js',
  ChatLock01Icon: './ChatLock01Icon.js',
  ChatLockIcon: './ChatLockIcon.js',
  ChatNotification01Icon: './ChatNotification01Icon.js',
  ChatNotificationIcon: './ChatNotificationIcon.js',
  ChatOutcome01Icon: './ChatOutcome01Icon.js',
  ChatOutcomeIcon: './ChatOutcomeIcon.js',
  ChatPreview01Icon: './ChatPreview01Icon.js',
  ChatPreviewIcon: './ChatPreviewIcon.js',
  ChatQuestion01Icon: './ChatQuestion01Icon.js',
  ChatQuestionIcon: './ChatQuestionIcon.js',
  ChatSearch01Icon: './ChatSearch01Icon.js',
  ChatSearchIcon: './ChatSearchIcon.js',
  ChatSecure01Icon: './ChatSecure01Icon.js',
  ChatSecureIcon: './ChatSecureIcon.js',
  ChatSpark01Icon: './ChatSpark01Icon.js',
  ChatSparkIcon: './ChatSparkIcon.js',
  ChatIcon: './ChatIcon.js',
  ChatTranslate01Icon: './ChatTranslate01Icon.js',
  ChatTranslateIcon: './ChatTranslateIcon.js',
  ChatUnlock01Icon: './ChatUnlock01Icon.js',
  ChatUnlockIcon: './ChatUnlockIcon.js',
  ChatUpload01Icon: './ChatUpload01Icon.js',
  ChatUploadIcon: './ChatUploadIcon.js',
  ChatUser01Icon: './ChatUser01Icon.js',
  ChatUserIcon: './ChatUserIcon.js',
  Chatting01Icon: './Chatting01Icon.js',
  CheckCheckIcon: './CheckCheckIcon.js',
  CheckLineIcon: './CheckLineIcon.js',
  CheckListIcon: './CheckListIcon.js',
  CheckIcon: './CheckIcon.js',
  CheckUnread01Icon: './CheckUnread01Icon.js',
  CheckUnread02Icon: './CheckUnread02Icon.js',
  CheckUnread03Icon: './CheckUnread03Icon.js',
  CheckUnread04Icon: './CheckUnread04Icon.js',
  CheckmarkBadge01Icon: './CheckmarkBadge01Icon.js',
  CheckmarkBadge02Icon: './CheckmarkBadge02Icon.js',
  CheckmarkBadge03Icon: './CheckmarkBadge03Icon.js',
  CheckmarkBadge04Icon: './CheckmarkBadge04Icon.js',
  CheckmarkCircle01Icon: './CheckmarkCircle01Icon.js',
  CheckmarkCircle02Icon: './CheckmarkCircle02Icon.js',
  CheckmarkCircle03Icon: './CheckmarkCircle03Icon.js',
  CheckmarkCircle04Icon: './CheckmarkCircle04Icon.js',
  CheckmarkSquare01Icon: './CheckmarkSquare01Icon.js',
  CheckmarkSquare02Icon: './CheckmarkSquare02Icon.js',
  CheckmarkSquare03Icon: './CheckmarkSquare03Icon.js',
  CheckmarkSquare04Icon: './CheckmarkSquare04Icon.js',
  CheeseCake01Icon: './CheeseCake01Icon.js',
  CheeseCake02Icon: './CheeseCake02Icon.js',
  CheeseIcon: './CheeseIcon.js',
  ChefHatIcon: './ChefHatIcon.js',
  ChefIcon: './ChefIcon.js',
  Chemistry01Icon: './Chemistry01Icon.js',
  Chemistry02Icon: './Chemistry02Icon.js',
  Chemistry03Icon: './Chemistry03Icon.js',
  CherryIcon: './CherryIcon.js',
  Chess01Icon: './Chess01Icon.js',
  Chess02Icon: './Chess02Icon.js',
  ChessBishopIcon: './ChessBishopIcon.js',
  ChessKingIcon: './ChessKingIcon.js',
  ChessKnightIcon: './ChessKnightIcon.js',
  ChessPawnIcon: './ChessPawnIcon.js',
  ChessQueenIcon: './ChessQueenIcon.js',
  ChessRookIcon: './ChessRookIcon.js',
  ChevronDoubleCloseIcon: './ChevronDoubleCloseIcon.js',
  ChevronDownIcon: './ChevronDownIcon.js',
  ChevronFirstIcon: './ChevronFirstIcon.js',
  ChevronLastIcon: './ChevronLastIcon.js',
  ChevronLeftIcon: './ChevronLeftIcon.js',
  ChevronRightIcon: './ChevronRightIcon.js',
  ChevronUpIcon: './ChevronUpIcon.js',
  ChevronsDownIcon: './ChevronsDownIcon.js',
  ChevronsDownUpIcon: './ChevronsDownUpIcon.js',
  ChevronsLeftRightEllipsisIcon: './ChevronsLeftRightEllipsisIcon.js',
  ChevronsLeftRightIcon: './ChevronsLeftRightIcon.js',
  ChevronsLeftIcon: './ChevronsLeftIcon.js',
  ChevronsRightLeftIcon: './ChevronsRightLeftIcon.js',
  ChevronsRightIcon: './ChevronsRightIcon.js',
  ChevronsUpIcon: './ChevronsUpIcon.js',
  ChickenThighsIcon: './ChickenThighsIcon.js',
  ChildIcon: './ChildIcon.js',
  ChimneyIcon: './ChimneyIcon.js',
  ChinaTempleIcon: './ChinaTempleIcon.js',
  Chip02Icon: './Chip02Icon.js',
  ChipIcon: './ChipIcon.js',
  ChocolateIcon: './ChocolateIcon.js',
  ChopsticksIcon: './ChopsticksIcon.js',
  ChromeIcon: './ChromeIcon.js',
  ChryslerIcon: './ChryslerIcon.js',
  ChurchIcon: './ChurchIcon.js',
  CigaretteOffIcon: './CigaretteOffIcon.js',
  CigaretteIcon: './CigaretteIcon.js',
  CinnamonRollIcon: './CinnamonRollIcon.js',
  CircleArrowDataTransferDiagonalIcon: './CircleArrowDataTransferDiagonalIcon.js',
  CircleArrowDataTransferHorizontalIcon: './CircleArrowDataTransferHorizontalIcon.js',
  CircleArrowDataTransferVerticalIcon: './CircleArrowDataTransferVerticalIcon.js',
  CircleArrowDiagonal01Icon: './CircleArrowDiagonal01Icon.js',
  CircleArrowDiagonal02Icon: './CircleArrowDiagonal02Icon.js',
  CircleArrowDown01Icon: './CircleArrowDown01Icon.js',
  CircleArrowDown02Icon: './CircleArrowDown02Icon.js',
  CircleArrowDown03Icon: './CircleArrowDown03Icon.js',
  CircleArrowDownDoubleIcon: './CircleArrowDownDoubleIcon.js',
  CircleArrowDownLeftIcon: './CircleArrowDownLeftIcon.js',
  CircleArrowDownRightIcon: './CircleArrowDownRightIcon.js',
  CircleArrowExpand01Icon: './CircleArrowExpand01Icon.js',
  CircleArrowExpand02Icon: './CircleArrowExpand02Icon.js',
  CircleArrowHorizontalIcon: './CircleArrowHorizontalIcon.js',
  CircleArrowLeft01Icon: './CircleArrowLeft01Icon.js',
  CircleArrowLeft02Icon: './CircleArrowLeft02Icon.js',
  CircleArrowLeft03Icon: './CircleArrowLeft03Icon.js',
  CircleArrowLeftDoubleIcon: './CircleArrowLeftDoubleIcon.js',
  CircleArrowLeftRightIcon: './CircleArrowLeftRightIcon.js',
  CircleArrowMoveDownLeftIcon: './CircleArrowMoveDownLeftIcon.js',
  CircleArrowMoveDownRightIcon: './CircleArrowMoveDownRightIcon.js',
  CircleArrowMoveLeftDownIcon: './CircleArrowMoveLeftDownIcon.js',
  CircleArrowMoveRightDownIcon: './CircleArrowMoveRightDownIcon.js',
  CircleArrowMoveUpLeftIcon: './CircleArrowMoveUpLeftIcon.js',
  CircleArrowMoveUpRightIcon: './CircleArrowMoveUpRightIcon.js',
  CircleArrowOutDownLeftIcon: './CircleArrowOutDownLeftIcon.js',
  CircleArrowOutDownRightIcon: './CircleArrowOutDownRightIcon.js',
  CircleArrowOutUpLeftIcon: './CircleArrowOutUpLeftIcon.js',
  CircleArrowOutUpRightIcon: './CircleArrowOutUpRightIcon.js',
  CircleArrowReload01Icon: './CircleArrowReload01Icon.js',
  CircleArrowReload02Icon: './CircleArrowReload02Icon.js',
  CircleArrowRight01Icon: './CircleArrowRight01Icon.js',
  CircleArrowRight02Icon: './CircleArrowRight02Icon.js',
  CircleArrowRight03Icon: './CircleArrowRight03Icon.js',
  CircleArrowRightDoubleIcon: './CircleArrowRightDoubleIcon.js',
  CircleArrowShrink01Icon: './CircleArrowShrink01Icon.js',
  CircleArrowShrink02Icon: './CircleArrowShrink02Icon.js',
  CircleArrowUp01Icon: './CircleArrowUp01Icon.js',
  CircleArrowUp02Icon: './CircleArrowUp02Icon.js',
  CircleArrowUp03Icon: './CircleArrowUp03Icon.js',
  CircleArrowUpDoubleIcon: './CircleArrowUpDoubleIcon.js',
  CircleArrowUpDownIcon: './CircleArrowUpDownIcon.js',
  CircleArrowUpLeftIcon: './CircleArrowUpLeftIcon.js',
  CircleArrowUpRight02Icon: './CircleArrowUpRight02Icon.js',
  CircleArrowUpRightIcon: './CircleArrowUpRightIcon.js',
  CircleArrowVerticalIcon: './CircleArrowVerticalIcon.js',
  CircleCheckBigIcon: './CircleCheckBigIcon.js',
  CircleCheckIcon: './CircleCheckIcon.js',
  CircleChevronDownIcon: './CircleChevronDownIcon.js',
  CircleChevronLeftIcon: './CircleChevronLeftIcon.js',
  CircleChevronRightIcon: './CircleChevronRightIcon.js',
  CircleChevronUpIcon: './CircleChevronUpIcon.js',
  CircleDashedIcon: './CircleDashedIcon.js',
  CircleDollarSignIcon: './CircleDollarSignIcon.js',
  CircleDotDashedIcon: './CircleDotDashedIcon.js',
  CircleDotIcon: './CircleDotIcon.js',
  CircleEllipsisIcon: './CircleEllipsisIcon.js',
  CircleFadingArrowUpIcon: './CircleFadingArrowUpIcon.js',
  CircleFadingPlusIcon: './CircleFadingPlusIcon.js',
  CircleGaugeIcon: './CircleGaugeIcon.js',
  CircleLock01Icon: './CircleLock01Icon.js',
  CircleLock02Icon: './CircleLock02Icon.js',
  CircleLockAdd01Icon: './CircleLockAdd01Icon.js',
  CircleLockAdd02Icon: './CircleLockAdd02Icon.js',
  CircleLockCheck01Icon: './CircleLockCheck01Icon.js',
  CircleLockCheck02Icon: './CircleLockCheck02Icon.js',
  CircleLockMinus01Icon: './CircleLockMinus01Icon.js',
  CircleLockMinus02Icon: './CircleLockMinus02Icon.js',
  CircleLockRemove01Icon: './CircleLockRemove01Icon.js',
  CircleLockRemove02Icon: './CircleLockRemove02Icon.js',
  CircleMinusIcon: './CircleMinusIcon.js',
  CircleOffIcon: './CircleOffIcon.js',
  CircleParkingOffIcon: './CircleParkingOffIcon.js',
  CircleParkingIcon: './CircleParkingIcon.js',
  CirclePasswordIcon: './CirclePasswordIcon.js',
  CirclePileIcon: './CirclePileIcon.js',
  CirclePlusIcon: './CirclePlusIcon.js',
  CirclePoundSterlingIcon: './CirclePoundSterlingIcon.js',
  CirclePowerIcon: './CirclePowerIcon.js',
  CircleQuestionMarkIcon: './CircleQuestionMarkIcon.js',
  CircleSlashTwoIcon: './CircleSlashTwoIcon.js',
  CircleSmallIcon: './CircleSmallIcon.js',
  CircleIcon: './CircleIcon.js',
  CircleUnlock01Icon: './CircleUnlock01Icon.js',
  CircleUnlock02Icon: './CircleUnlock02Icon.js',
  CircleXIcon: './CircleXIcon.js',
  CircuitBoardIcon: './CircuitBoardIcon.js',
  CitrusIcon: './CitrusIcon.js',
  City01Icon: './City01Icon.js',
  City02Icon: './City02Icon.js',
  City03Icon: './City03Icon.js',
  ClapperboardIcon: './ClapperboardIcon.js',
  Clapping01Icon: './Clapping01Icon.js',
  Clapping02Icon: './Clapping02Icon.js',
  ClaudeIcon: './ClaudeIcon.js',
  CleanIcon: './CleanIcon.js',
  CleaningBucketIcon: './CleaningBucketIcon.js',
  ClinicIcon: './ClinicIcon.js',
  ClipIcon: './ClipIcon.js',
  ClipartsIcon: './ClipartsIcon.js',
  ClipboardCheckIcon: './ClipboardCheckIcon.js',
  ClipboardClockIcon: './ClipboardClockIcon.js',
  ClipboardCopyIcon: './ClipboardCopyIcon.js',
  ClipboardListIcon: './ClipboardListIcon.js',
  ClipboardMinusIcon: './ClipboardMinusIcon.js',
  ClipboardPasteIcon: './ClipboardPasteIcon.js',
  ClipboardPenLineIcon: './ClipboardPenLineIcon.js',
  ClipboardPenIcon: './ClipboardPenIcon.js',
  ClipboardPlusIcon: './ClipboardPlusIcon.js',
  ClipboardIcon: './ClipboardIcon.js',
  ClipboardTypeIcon: './ClipboardTypeIcon.js',
  ClipboardXIcon: './ClipboardXIcon.js',
  Clock01Icon: './Clock01Icon.js',
  Clock02Icon: './Clock02Icon.js',
  Clock03Icon: './Clock03Icon.js',
  Clock04Icon: './Clock04Icon.js',
  Clock05Icon: './Clock05Icon.js',
  ClockAddIcon: './ClockAddIcon.js',
  ClockAlertIcon: './ClockAlertIcon.js',
  ClockArrowDownIcon: './ClockArrowDownIcon.js',
  ClockArrowUpIcon: './ClockArrowUpIcon.js',
  ClockCheckIcon: './ClockCheckIcon.js',
  ClockFadingIcon: './ClockFadingIcon.js',
  ClockPlusIcon: './ClockPlusIcon.js',
  ClosedCaptionAltIcon: './ClosedCaptionAltIcon.js',
  ClosedCaptionIcon: './ClosedCaptionIcon.js',
  ClothesIcon: './ClothesIcon.js',
  CloudAlertIcon: './CloudAlertIcon.js',
  CloudAngledRainIcon: './CloudAngledRainIcon.js',
  CloudAngledRainZapIcon: './CloudAngledRainZapIcon.js',
  CloudAngledZapIcon: './CloudAngledZapIcon.js',
  CloudBackupIcon: './CloudBackupIcon.js',
  CloudBigRainIcon: './CloudBigRainIcon.js',
  CloudCheckIcon: './CloudCheckIcon.js',
  CloudCogIcon: './CloudCogIcon.js',
  CloudDownloadIcon: './CloudDownloadIcon.js',
  CloudDrizzleIcon: './CloudDrizzleIcon.js',
  CloudFastWindIcon: './CloudFastWindIcon.js',
  CloudFogIcon: './CloudFogIcon.js',
  CloudHailIcon: './CloudHailIcon.js',
  CloudHailstoneIcon: './CloudHailstoneIcon.js',
  CloudLightningIcon: './CloudLightningIcon.js',
  CloudLittleRainIcon: './CloudLittleRainIcon.js',
  CloudLittleSnowIcon: './CloudLittleSnowIcon.js',
  CloudLoadingIcon: './CloudLoadingIcon.js',
  CloudMidRainIcon: './CloudMidRainIcon.js',
  CloudMidSnowIcon: './CloudMidSnowIcon.js',
  CloudMoonRainIcon: './CloudMoonRainIcon.js',
  CloudOffIcon: './CloudOffIcon.js',
  CloudRainIcon: './CloudRainIcon.js',
  CloudRainWindIcon: './CloudRainWindIcon.js',
  CloudSavingDone01Icon: './CloudSavingDone01Icon.js',
  CloudSavingDone02Icon: './CloudSavingDone02Icon.js',
  CloudServerIcon: './CloudServerIcon.js',
  CloudSlowWindIcon: './CloudSlowWindIcon.js',
  CloudSnowIcon: './CloudSnowIcon.js',
  CloudIcon: './CloudIcon.js',
  CloudSunRainIcon: './CloudSunRainIcon.js',
  CloudSyncIcon: './CloudSyncIcon.js',
  CloudUploadIcon: './CloudUploadIcon.js',
  CloudyIcon: './CloudyIcon.js',
  CloverIcon: './CloverIcon.js',
  ClubIcon: './ClubIcon.js',
  Clubs01Icon: './Clubs01Icon.js',
  Clubs02Icon: './Clubs02Icon.js',
  CoPresentIcon: './CoPresentIcon.js',
  CodeCircleIcon: './CodeCircleIcon.js',
  CodeFolderIcon: './CodeFolderIcon.js',
  CodeSimpleIcon: './CodeSimpleIcon.js',
  CodeSquareIcon: './CodeSquareIcon.js',
  CodeIcon: './CodeIcon.js',
  CodeXmlIcon: './CodeXmlIcon.js',
  CodepenIcon: './CodepenIcon.js',
  CodesandboxIcon: './CodesandboxIcon.js',
  Coffee01Icon: './Coffee01Icon.js',
  Coffee02Icon: './Coffee02Icon.js',
  Coffee03Icon: './Coffee03Icon.js',
  Coffee04Icon: './Coffee04Icon.js',
  CoffeeBeansIcon: './CoffeeBeansIcon.js',
  CogIcon: './CogIcon.js',
  CoinbaseIcon: './CoinbaseIcon.js',
  Coins01Icon: './Coins01Icon.js',
  Coins02Icon: './Coins02Icon.js',
  CoinsBitcoinIcon: './CoinsBitcoinIcon.js',
  CoinsDollarIcon: './CoinsDollarIcon.js',
  CoinsEuroIcon: './CoinsEuroIcon.js',
  CoinsPoundIcon: './CoinsPoundIcon.js',
  CoinsSwapIcon: './CoinsSwapIcon.js',
  CoinsYenIcon: './CoinsYenIcon.js',
  CollapseIcon: './CollapseIcon.js',
  CollectionsBookmarkIcon: './CollectionsBookmarkIcon.js',
  ColorPickerIcon: './ColorPickerIcon.js',
  ColorsIcon: './ColorsIcon.js',
  ColosseumIcon: './ColosseumIcon.js',
  ColumnDeleteIcon: './ColumnDeleteIcon.js',
  ColumnInsertIcon: './ColumnInsertIcon.js',
  ColumnsThreeCogIcon: './ColumnsThreeCogIcon.js',
  CombineIcon: './CombineIcon.js',
  Comet01Icon: './Comet01Icon.js',
  Comet02Icon: './Comet02Icon.js',
  ComingSoon01Icon: './ComingSoon01Icon.js',
  ComingSoon02Icon: './ComingSoon02Icon.js',
  CommandLineIcon: './CommandLineIcon.js',
  CommandIcon: './CommandIcon.js',
  CommentAdd03Icon: './CommentAdd03Icon.js',
  CommentBlock03Icon: './CommentBlock03Icon.js',
  CommentRemove03Icon: './CommentRemove03Icon.js',
  Comment01Icon: './Comment01Icon.js',
  Comment02Icon: './Comment02Icon.js',
  Comment03Icon: './Comment03Icon.js',
  CommentAdd01Icon: './CommentAdd01Icon.js',
  CommentAdd02Icon: './CommentAdd02Icon.js',
  CommentBlock01Icon: './CommentBlock01Icon.js',
  CommentBlock02Icon: './CommentBlock02Icon.js',
  CommentRemove01Icon: './CommentRemove01Icon.js',
  CommentRemove02Icon: './CommentRemove02Icon.js',
  Compass01Icon: './Compass01Icon.js',
  CompassIcon: './CompassIcon.js',
  ComplaintIcon: './ComplaintIcon.js',
  ComponentIcon: './ComponentIcon.js',
  ComputerActivityIcon: './ComputerActivityIcon.js',
  ComputerAddIcon: './ComputerAddIcon.js',
  ComputerArrowDownIcon: './ComputerArrowDownIcon.js',
  ComputerArrowUpIcon: './ComputerArrowUpIcon.js',
  ComputerChartDownIcon: './ComputerChartDownIcon.js',
  ComputerChartUpIcon: './ComputerChartUpIcon.js',
  ComputerCheckIcon: './ComputerCheckIcon.js',
  ComputerCloudIcon: './ComputerCloudIcon.js',
  ComputerDesk01Icon: './ComputerDesk01Icon.js',
  ComputerDesk02Icon: './ComputerDesk02Icon.js',
  ComputerDesk03Icon: './ComputerDesk03Icon.js',
  ComputerDollarIcon: './ComputerDollarIcon.js',
  ComputerEthernetIcon: './ComputerEthernetIcon.js',
  ComputerPhoneSyncIcon: './ComputerPhoneSyncIcon.js',
  ComputerProgramming01Icon: './ComputerProgramming01Icon.js',
  ComputerProgramming02Icon: './ComputerProgramming02Icon.js',
  ComputerProtectionIcon: './ComputerProtectionIcon.js',
  ComputerRemoveIcon: './ComputerRemoveIcon.js',
  ComputerScreenShareIcon: './ComputerScreenShareIcon.js',
  ComputerSettingsIcon: './ComputerSettingsIcon.js',
  ComputerIcon: './ComputerIcon.js',
  ComputerTerminal01Icon: './ComputerTerminal01Icon.js',
  ComputerTerminal02Icon: './ComputerTerminal02Icon.js',
  ComputerUserIcon: './ComputerUserIcon.js',
  ComputerVideoCallIcon: './ComputerVideoCallIcon.js',
  ComputerVideoIcon: './ComputerVideoIcon.js',
  ConciergeBellIcon: './ConciergeBellIcon.js',
  Cone01Icon: './Cone01Icon.js',
  Cone02Icon: './Cone02Icon.js',
  ConferenceIcon: './ConferenceIcon.js',
  Configuration01Icon: './Configuration01Icon.js',
  Configuration02Icon: './Configuration02Icon.js',
  ConfusedIcon: './ConfusedIcon.js',
  CongruentToCircleIcon: './CongruentToCircleIcon.js',
  CongruentToSquareIcon: './CongruentToSquareIcon.js',
  CongruentToIcon: './CongruentToIcon.js',
  ConnectIcon: './ConnectIcon.js',
  ConsoleIcon: './ConsoleIcon.js',
  ConstellationIcon: './ConstellationIcon.js',
  ConstructionIcon: './ConstructionIcon.js',
  Contact01Icon: './Contact01Icon.js',
  Contact02Icon: './Contact02Icon.js',
  ContactBookIcon: './ContactBookIcon.js',
  ContactRoundIcon: './ContactRoundIcon.js',
  ContactIcon: './ContactIcon.js',
  ContainerIcon: './ContainerIcon.js',
  ContainerTruck01Icon: './ContainerTruck01Icon.js',
  ContainerTruck02Icon: './ContainerTruck02Icon.js',
  ContainerTruckIcon: './ContainerTruckIcon.js',
  ContentWritingIcon: './ContentWritingIcon.js',
  ContractsIcon: './ContractsIcon.js',
  ContrastIcon: './ContrastIcon.js',
  ConversationIcon: './ConversationIcon.js',
  CookBookIcon: './CookBookIcon.js',
  CookieIcon: './CookieIcon.js',
  CookingPotIcon: './CookingPotIcon.js',
  Coordinate01Icon: './Coordinate01Icon.js',
  Coordinate02Icon: './Coordinate02Icon.js',
  CopilotIcon: './CopilotIcon.js',
  Copy01Icon: './Copy01Icon.js',
  Copy02Icon: './Copy02Icon.js',
  CopyCheckIcon: './CopyCheckIcon.js',
  CopyLinkIcon: './CopyLinkIcon.js',
  CopyMinusIcon: './CopyMinusIcon.js',
  CopyPlusIcon: './CopyPlusIcon.js',
  CopySlashIcon: './CopySlashIcon.js',
  CopyIcon: './CopyIcon.js',
  CopyXIcon: './CopyXIcon.js',
  CopyleftIcon: './CopyleftIcon.js',
  CopyrightIcon: './CopyrightIcon.js',
  CornIcon: './CornIcon.js',
  CornerDownLeftIcon: './CornerDownLeftIcon.js',
  CornerDownRightIcon: './CornerDownRightIcon.js',
  CornerLeftDownIcon: './CornerLeftDownIcon.js',
  CornerLeftUpIcon: './CornerLeftUpIcon.js',
  CornerRightDownIcon: './CornerRightDownIcon.js',
  CornerRightUpIcon: './CornerRightUpIcon.js',
  CornerUpLeftIcon: './CornerUpLeftIcon.js',
  CornerUpRightIcon: './CornerUpRightIcon.js',
  CorporateIcon: './CorporateIcon.js',
  CosIcon: './CosIcon.js',
  Cosine01Icon: './Cosine01Icon.js',
  Cosine02Icon: './Cosine02Icon.js',
  CottageIcon: './CottageIcon.js',
  CottonCandyIcon: './CottonCandyIcon.js',
  Coupon01Icon: './Coupon01Icon.js',
  Coupon02Icon: './Coupon02Icon.js',
  Coupon03Icon: './Coupon03Icon.js',
  CouponPercentIcon: './CouponPercentIcon.js',
  CourseIcon: './CourseIcon.js',
  CourtHouseIcon: './CourtHouseIcon.js',
  CourtLawIcon: './CourtLawIcon.js',
  CovariateIcon: './CovariateIcon.js',
  CovidInfoIcon: './CovidInfoIcon.js',
  CowboyHatIcon: './CowboyHatIcon.js',
  CppIcon: './CppIcon.js',
  CpuChargeIcon: './CpuChargeIcon.js',
  CpuSettingsIcon: './CpuSettingsIcon.js',
  CpuIcon: './CpuIcon.js',
  CrabIcon: './CrabIcon.js',
  CraneIcon: './CraneIcon.js',
  CrazyIcon: './CrazyIcon.js',
  CreativeCommonsIcon: './CreativeCommonsIcon.js',
  CreativeMarketIcon: './CreativeMarketIcon.js',
  CreditCardAcceptIcon: './CreditCardAcceptIcon.js',
  CreditCardAddIcon: './CreditCardAddIcon.js',
  CreditCardChangeIcon: './CreditCardChangeIcon.js',
  CreditCardDefrostIcon: './CreditCardDefrostIcon.js',
  CreditCardFreezeIcon: './CreditCardFreezeIcon.js',
  CreditCardNotAcceptIcon: './CreditCardNotAcceptIcon.js',
  CreditCardNotFoundIcon: './CreditCardNotFoundIcon.js',
  CreditCardPosIcon: './CreditCardPosIcon.js',
  CreditCardIcon: './CreditCardIcon.js',
  CreditCardValidationIcon: './CreditCardValidationIcon.js',
  CricketBatIcon: './CricketBatIcon.js',
  CricketHelmetIcon: './CricketHelmetIcon.js',
  CroissantIcon: './CroissantIcon.js',
  CropIcon: './CropIcon.js',
  CrossIcon: './CrossIcon.js',
  CrosshairIcon: './CrosshairIcon.js',
  CrowdfundingIcon: './CrowdfundingIcon.js',
  Crown02Icon: './Crown02Icon.js',
  Crown03Icon: './Crown03Icon.js',
  CrownMinusIcon: './CrownMinusIcon.js',
  CrownPlusIcon: './CrownPlusIcon.js',
  CrownIcon: './CrownIcon.js',
  CryingIcon: './CryingIcon.js',
  CssThreeIcon: './CssThreeIcon.js',
  CssFile01Icon: './CssFile01Icon.js',
  CssFile02Icon: './CssFile02Icon.js',
  Csv01Icon: './Csv01Icon.js',
  Csv02Icon: './Csv02Icon.js',
  CubeIcon: './CubeIcon.js',
  CupSodaIcon: './CupSodaIcon.js',
  Cupcake01Icon: './Cupcake01Icon.js',
  Cupcake02Icon: './Cupcake02Icon.js',
  Cupcake03Icon: './Cupcake03Icon.js',
  CurlingIcon: './CurlingIcon.js',
  CurrencyIcon: './CurrencyIcon.js',
  Cursor01Icon: './Cursor01Icon.js',
  Cursor02Icon: './Cursor02Icon.js',
  CursorAddSelection01Icon: './CursorAddSelection01Icon.js',
  CursorAddSelection02Icon: './CursorAddSelection02Icon.js',
  CursorCircleSelection01Icon: './CursorCircleSelection01Icon.js',
  CursorCircleSelection02Icon: './CursorCircleSelection02Icon.js',
  CursorDisabled01Icon: './CursorDisabled01Icon.js',
  CursorDisabled02Icon: './CursorDisabled02Icon.js',
  CursorEdit01Icon: './CursorEdit01Icon.js',
  CursorEdit02Icon: './CursorEdit02Icon.js',
  CursorHold01Icon: './CursorHold01Icon.js',
  CursorHold02Icon: './CursorHold02Icon.js',
  CursorInWindowIcon: './CursorInWindowIcon.js',
  CursorInfo01Icon: './CursorInfo01Icon.js',
  CursorInfo02Icon: './CursorInfo02Icon.js',
  CursorLoading01Icon: './CursorLoading01Icon.js',
  CursorLoading02Icon: './CursorLoading02Icon.js',
  CursorMagicSelection01Icon: './CursorMagicSelection01Icon.js',
  CursorMagicSelection02Icon: './CursorMagicSelection02Icon.js',
  CursorMagicSelection03Icon: './CursorMagicSelection03Icon.js',
  CursorMagicSelection04Icon: './CursorMagicSelection04Icon.js',
  CursorMove01Icon: './CursorMove01Icon.js',
  CursorMove02Icon: './CursorMove02Icon.js',
  CursorPointer01Icon: './CursorPointer01Icon.js',
  CursorPointer02Icon: './CursorPointer02Icon.js',
  CursorProgress01Icon: './CursorProgress01Icon.js',
  CursorProgress02Icon: './CursorProgress02Icon.js',
  CursorProgress03Icon: './CursorProgress03Icon.js',
  CursorProgress04Icon: './CursorProgress04Icon.js',
  CursorRectangleSelection01Icon: './CursorRectangleSelection01Icon.js',
  CursorRectangleSelection02Icon: './CursorRectangleSelection02Icon.js',
  CursorRemoveSelection01Icon: './CursorRemoveSelection01Icon.js',
  CursorRemoveSelection02Icon: './CursorRemoveSelection02Icon.js',
  CursorTextIcon: './CursorTextIcon.js',
  CurtainsIcon: './CurtainsIcon.js',
  CurvyLeftDirectionIcon: './CurvyLeftDirectionIcon.js',
  CurvyLeftRightDirectionIcon: './CurvyLeftRightDirectionIcon.js',
  CurvyRightDirectionIcon: './CurvyRightDirectionIcon.js',
  CurvyUpDownDirectionIcon: './CurvyUpDownDirectionIcon.js',
  CustomFieldIcon: './CustomFieldIcon.js',
  CustomerService01Icon: './CustomerService01Icon.js',
  CustomerService02Icon: './CustomerService02Icon.js',
  CustomerServiceIcon: './CustomerServiceIcon.js',
  CustomerSupportIcon: './CustomerSupportIcon.js',
  CustomizeIcon: './CustomizeIcon.js',
  Cylinder01Icon: './Cylinder01Icon.js',
  Cylinder02Icon: './Cylinder02Icon.js',
  Cylinder03Icon: './Cylinder03Icon.js',
  Cylinder04Icon: './Cylinder04Icon.js',
  DamIcon: './DamIcon.js',
  DangerIcon: './DangerIcon.js',
  DarkModeIcon: './DarkModeIcon.js',
  DartIcon: './DartIcon.js',
  DashboardBrowsingIcon: './DashboardBrowsingIcon.js',
  DashboardCircleAddIcon: './DashboardCircleAddIcon.js',
  DashboardCircleEditIcon: './DashboardCircleEditIcon.js',
  DashboardCircleRemoveIcon: './DashboardCircleRemoveIcon.js',
  DashboardCircleSettingsIcon: './DashboardCircleSettingsIcon.js',
  DashboardCircleIcon: './DashboardCircleIcon.js',
  DashboardSpeed01Icon: './DashboardSpeed01Icon.js',
  DashboardSpeed02Icon: './DashboardSpeed02Icon.js',
  DashboardSquare01Icon: './DashboardSquare01Icon.js',
  DashboardSquare02Icon: './DashboardSquare02Icon.js',
  DashboardSquare03Icon: './DashboardSquare03Icon.js',
  DashboardSquareAddIcon: './DashboardSquareAddIcon.js',
  DashboardSquareEditIcon: './DashboardSquareEditIcon.js',
  DashboardSquareRemoveIcon: './DashboardSquareRemoveIcon.js',
  DashboardSquareSettingIcon: './DashboardSquareSettingIcon.js',
  DashedLine01Icon: './DashedLine01Icon.js',
  DashedLine02Icon: './DashedLine02Icon.js',
  DashedLineCircleIcon: './DashedLineCircleIcon.js',
  DataRecoveryIcon: './DataRecoveryIcon.js',
  Database01Icon: './Database01Icon.js',
  Database02Icon: './Database02Icon.js',
  DatabaseAddIcon: './DatabaseAddIcon.js',
  DatabaseBackupIcon: './DatabaseBackupIcon.js',
  DatabaseExpandIcon: './DatabaseExpandIcon.js',
  DatabaseExportIcon: './DatabaseExportIcon.js',
  DatabaseImportIcon: './DatabaseImportIcon.js',
  DatabaseLightningIcon: './DatabaseLightningIcon.js',
  DatabaseLockedIcon: './DatabaseLockedIcon.js',
  DatabaseRestoreIcon: './DatabaseRestoreIcon.js',
  DatabaseSearchIcon: './DatabaseSearchIcon.js',
  DatabaseSettingIcon: './DatabaseSettingIcon.js',
  DatabaseIcon: './DatabaseIcon.js',
  DatabaseSync01Icon: './DatabaseSync01Icon.js',
  DatabaseSyncIcon: './DatabaseSyncIcon.js',
  DatabaseZapIcon: './DatabaseZapIcon.js',
  DateTimeIcon: './DateTimeIcon.js',
  DatesIcon: './DatesIcon.js',
  DatevIcon: './DatevIcon.js',
  DeadIcon: './DeadIcon.js',
  DeepseekIcon: './DeepseekIcon.js',
  Delete01Icon: './Delete01Icon.js',
  Delete02Icon: './Delete02Icon.js',
  Delete03Icon: './Delete03Icon.js',
  Delete04Icon: './Delete04Icon.js',
  DeleteColumnIcon: './DeleteColumnIcon.js',
  DeletePutBackIcon: './DeletePutBackIcon.js',
  DeleteRowIcon: './DeleteRowIcon.js',
  DeleteThrowIcon: './DeleteThrowIcon.js',
  DeliveredSentIcon: './DeliveredSentIcon.js',
  DeliveryBox01Icon: './DeliveryBox01Icon.js',
  DeliveryBox02Icon: './DeliveryBox02Icon.js',
  DeliveryDelay01Icon: './DeliveryDelay01Icon.js',
  DeliveryDelay02Icon: './DeliveryDelay02Icon.js',
  DeliveryReturn01Icon: './DeliveryReturn01Icon.js',
  DeliveryReturn02Icon: './DeliveryReturn02Icon.js',
  DeliverySecure01Icon: './DeliverySecure01Icon.js',
  DeliverySecure02Icon: './DeliverySecure02Icon.js',
  DeliverySent01Icon: './DeliverySent01Icon.js',
  DeliverySent02Icon: './DeliverySent02Icon.js',
  DeliveryTracking01Icon: './DeliveryTracking01Icon.js',
  DeliveryTracking02Icon: './DeliveryTracking02Icon.js',
  DeliveryTruck01Icon: './DeliveryTruck01Icon.js',
  DeliveryTruck02Icon: './DeliveryTruck02Icon.js',
  DeliveryView01Icon: './DeliveryView01Icon.js',
  DeliveryView02Icon: './DeliveryView02Icon.js',
  DentalBracesIcon: './DentalBracesIcon.js',
  DentalBrokenToothIcon: './DentalBrokenToothIcon.js',
  DentalCareIcon: './DentalCareIcon.js',
  DentalToothIcon: './DentalToothIcon.js',
  DepartementIcon: './DepartementIcon.js',
  DesertIcon: './DesertIcon.js',
  Desk01Icon: './Desk01Icon.js',
  Desk02Icon: './Desk02Icon.js',
  DeskIcon: './DeskIcon.js',
  DessertIcon: './DessertIcon.js',
  DetergentIcon: './DetergentIcon.js',
  DeveloperIcon: './DeveloperIcon.js',
  DeviantartIcon: './DeviantartIcon.js',
  DeviceAccessIcon: './DeviceAccessIcon.js',
  DiagonalScrollPoint01Icon: './DiagonalScrollPoint01Icon.js',
  DiagonalScrollPoint02Icon: './DiagonalScrollPoint02Icon.js',
  DialpadCircle01Icon: './DialpadCircle01Icon.js',
  DialpadCircle02Icon: './DialpadCircle02Icon.js',
  DialpadSquare01Icon: './DialpadSquare01Icon.js',
  DialpadSquare02Icon: './DialpadSquare02Icon.js',
  DiameterIcon: './DiameterIcon.js',
  Diamond01Icon: './Diamond01Icon.js',
  Diamond02Icon: './Diamond02Icon.js',
  DiamondMinusIcon: './DiamondMinusIcon.js',
  DiamondPercentIcon: './DiamondPercentIcon.js',
  DiamondPlusIcon: './DiamondPlusIcon.js',
  DiamondIcon: './DiamondIcon.js',
  DiaperIcon: './DiaperIcon.js',
  DiceFaces01Icon: './DiceFaces01Icon.js',
  DiceFaces02Icon: './DiceFaces02Icon.js',
  DiceFaces03Icon: './DiceFaces03Icon.js',
  DiceFaces04Icon: './DiceFaces04Icon.js',
  DiceFaces05Icon: './DiceFaces05Icon.js',
  DiceFaces06Icon: './DiceFaces06Icon.js',
  DiceIcon: './DiceIcon.js',
  DicesIcon: './DicesIcon.js',
  DigestionIcon: './DigestionIcon.js',
  DiggIcon: './DiggIcon.js',
  DigitalClockIcon: './DigitalClockIcon.js',
  DimSum01Icon: './DimSum01Icon.js',
  DimSum02Icon: './DimSum02Icon.js',
  DiningTableIcon: './DiningTableIcon.js',
  DiplomaIcon: './DiplomaIcon.js',
  DirectionLeft01Icon: './DirectionLeft01Icon.js',
  DirectionLeft02Icon: './DirectionLeft02Icon.js',
  DirectionRight01Icon: './DirectionRight01Icon.js',
  DirectionRight02Icon: './DirectionRight02Icon.js',
  Directions01Icon: './Directions01Icon.js',
  Directions02Icon: './Directions02Icon.js',
  DirhamIcon: './DirhamIcon.js',
  Disability01Icon: './Disability01Icon.js',
  Disability02Icon: './Disability02Icon.js',
  DiscTwoIcon: './DiscTwoIcon.js',
  DiscThreeIcon: './DiscThreeIcon.js',
  DiscAlbumIcon: './DiscAlbumIcon.js',
  DiscIcon: './DiscIcon.js',
  DiscordIcon: './DiscordIcon.js',
  Discount01Icon: './Discount01Icon.js',
  DiscountIcon: './DiscountIcon.js',
  DiscountTag01Icon: './DiscountTag01Icon.js',
  DiscountTag02Icon: './DiscountTag02Icon.js',
  DiscoverCircleIcon: './DiscoverCircleIcon.js',
  DiscoverSquareIcon: './DiscoverSquareIcon.js',
  Dish01Icon: './Dish01Icon.js',
  Dish02Icon: './Dish02Icon.js',
  DishWasherIcon: './DishWasherIcon.js',
  DispleasedIcon: './DispleasedIcon.js',
  DistributeHorizontalCenterIcon: './DistributeHorizontalCenterIcon.js',
  DistributeHorizontalLeftIcon: './DistributeHorizontalLeftIcon.js',
  DistributeHorizontalRightIcon: './DistributeHorizontalRightIcon.js',
  DistributeVerticalBottomIcon: './DistributeVerticalBottomIcon.js',
  DistributeVerticalCenterIcon: './DistributeVerticalCenterIcon.js',
  DistributeVerticalTopIcon: './DistributeVerticalTopIcon.js',
  DistributionIcon: './DistributionIcon.js',
  DivideSignCircleIcon: './DivideSignCircleIcon.js',
  DivideSignSquareIcon: './DivideSignSquareIcon.js',
  DivideSignIcon: './DivideSignIcon.js',
  Dna01Icon: './Dna01Icon.js',
  DnaOffIcon: './DnaOffIcon.js',
  DnaIcon: './DnaIcon.js',
  DoNotTouch01Icon: './DoNotTouch01Icon.js',
  DoNotTouch02Icon: './DoNotTouch02Icon.js',
  Doc01Icon: './Doc01Icon.js',
  Doc02Icon: './Doc02Icon.js',
  DockIcon: './DockIcon.js',
  Doctor01Icon: './Doctor01Icon.js',
  Doctor02Icon: './Doctor02Icon.js',
  Doctor03Icon: './Doctor03Icon.js',
  DocumentAttachmentIcon: './DocumentAttachmentIcon.js',
  DocumentCodeIcon: './DocumentCodeIcon.js',
  DocumentValidationIcon: './DocumentValidationIcon.js',
  Dollar01Icon: './Dollar01Icon.js',
  Dollar02Icon: './Dollar02Icon.js',
  DollarCircleIcon: './DollarCircleIcon.js',
  DollarReceive01Icon: './DollarReceive01Icon.js',
  DollarReceive02Icon: './DollarReceive02Icon.js',
  DollarSend01Icon: './DollarSend01Icon.js',
  DollarSend02Icon: './DollarSend02Icon.js',
  DollarSignIcon: './DollarSignIcon.js',
  DollarSquareIcon: './DollarSquareIcon.js',
  DomeIcon: './DomeIcon.js',
  DominoIcon: './DominoIcon.js',
  DonutIcon: './DonutIcon.js',
  Door01Icon: './Door01Icon.js',
  Door02Icon: './Door02Icon.js',
  DoorClosedLockedIcon: './DoorClosedLockedIcon.js',
  DoorClosedIcon: './DoorClosedIcon.js',
  DoorLockIcon: './DoorLockIcon.js',
  DoorOpenIcon: './DoorOpenIcon.js',
  DoorIcon: './DoorIcon.js',
  DotIcon: './DotIcon.js',
  DoughnutIcon: './DoughnutIcon.js',
  Download01Icon: './Download01Icon.js',
  Download02Icon: './Download02Icon.js',
  Download03Icon: './Download03Icon.js',
  Download04Icon: './Download04Icon.js',
  Download05Icon: './Download05Icon.js',
  Download06Icon: './Download06Icon.js',
  DownloadCircle01Icon: './DownloadCircle01Icon.js',
  DownloadCircle02Icon: './DownloadCircle02Icon.js',
  DownloadSquare01Icon: './DownloadSquare01Icon.js',
  DownloadSquare02Icon: './DownloadSquare02Icon.js',
  DraftingCompassIcon: './DraftingCompassIcon.js',
  Drag01Icon: './Drag01Icon.js',
  Drag02Icon: './Drag02Icon.js',
  Drag03Icon: './Drag03Icon.js',
  Drag04Icon: './Drag04Icon.js',
  DragDropHorizontalIcon: './DragDropHorizontalIcon.js',
  DragDropIcon: './DragDropIcon.js',
  DragDropVerticalIcon: './DragDropVerticalIcon.js',
  DragLeft01Icon: './DragLeft01Icon.js',
  DragLeft02Icon: './DragLeft02Icon.js',
  DragLeft03Icon: './DragLeft03Icon.js',
  DragLeft04Icon: './DragLeft04Icon.js',
  DragRight01Icon: './DragRight01Icon.js',
  DragRight02Icon: './DragRight02Icon.js',
  DragRight03Icon: './DragRight03Icon.js',
  DragRight04Icon: './DragRight04Icon.js',
  DramaIcon: './DramaIcon.js',
  DrawingCompassIcon: './DrawingCompassIcon.js',
  DrawingModeIcon: './DrawingModeIcon.js',
  Dress01Icon: './Dress01Icon.js',
  Dress02Icon: './Dress02Icon.js',
  Dress03Icon: './Dress03Icon.js',
  Dress04Icon: './Dress04Icon.js',
  Dress05Icon: './Dress05Icon.js',
  Dress06Icon: './Dress06Icon.js',
  Dress07Icon: './Dress07Icon.js',
  DressingTable01Icon: './DressingTable01Icon.js',
  DressingTable02Icon: './DressingTable02Icon.js',
  DressingTable03Icon: './DressingTable03Icon.js',
  DribbbleIcon: './DribbbleIcon.js',
  DrillIcon: './DrillIcon.js',
  DrinkIcon: './DrinkIcon.js',
  DriveIcon: './DriveIcon.js',
  DroneIcon: './DroneIcon.js',
  DroolingIcon: './DroolingIcon.js',
  DropboxIcon: './DropboxIcon.js',
  DropdownFieldTypeIcon: './DropdownFieldTypeIcon.js',
  DropletOffIcon: './DropletOffIcon.js',
  DropletIcon: './DropletIcon.js',
  DropletsIcon: './DropletsIcon.js',
  DropperIcon: './DropperIcon.js',
  DrumIcon: './DrumIcon.js',
  DrumstickIcon: './DrumstickIcon.js',
  Ds3ToolIcon: './Ds3ToolIcon.js',
  DuaIcon: './DuaIcon.js',
  Dumbbell01Icon: './Dumbbell01Icon.js',
  Dumbbell02Icon: './Dumbbell02Icon.js',
  Dumbbell03Icon: './Dumbbell03Icon.js',
  DuplexIcon: './DuplexIcon.js',
  EarOffIcon: './EarOffIcon.js',
  EarRings01Icon: './EarRings01Icon.js',
  EarRings02Icon: './EarRings02Icon.js',
  EarRings03Icon: './EarRings03Icon.js',
  EarIcon: './EarIcon.js',
  EarthLockIcon: './EarthLockIcon.js',
  EarthIcon: './EarthIcon.js',
  EaseCurveControlPointsIcon: './EaseCurveControlPointsIcon.js',
  EaseInControlPointIcon: './EaseInControlPointIcon.js',
  EaseInOutIcon: './EaseInOutIcon.js',
  EaseInIcon: './EaseInIcon.js',
  EaseOutControlPointIcon: './EaseOutControlPointIcon.js',
  EaseOutIcon: './EaseOutIcon.js',
  EclipseIcon: './EclipseIcon.js',
  EcoEnergyIcon: './EcoEnergyIcon.js',
  EcoLab01Icon: './EcoLab01Icon.js',
  EcoLab02Icon: './EcoLab02Icon.js',
  EcoLabIcon: './EcoLabIcon.js',
  EcoPowerIcon: './EcoPowerIcon.js',
  EdgeStyleIcon: './EdgeStyleIcon.js',
  Edit01Icon: './Edit01Icon.js',
  Edit02Icon: './Edit02Icon.js',
  Edit03Icon: './Edit03Icon.js',
  Edit04Icon: './Edit04Icon.js',
  EditOff03Icon: './EditOff03Icon.js',
  EditOff04Icon: './EditOff04Icon.js',
  EditOffIcon: './EditOffIcon.js',
  EditRoadIcon: './EditRoadIcon.js',
  EditTableIcon: './EditTableIcon.js',
  EditUser02Icon: './EditUser02Icon.js',
  EggFriedIcon: './EggFriedIcon.js',
  EggOffIcon: './EggOffIcon.js',
  EggIcon: './EggIcon.js',
  EggsIcon: './EggsIcon.js',
  EidMubarakIcon: './EidMubarakIcon.js',
  EiffelTowerIcon: './EiffelTowerIcon.js',
  EightCircleIcon: './EightCircleIcon.js',
  EightSquareIcon: './EightSquareIcon.js',
  ElearningExchangeIcon: './ElearningExchangeIcon.js',
  ElectricHome01Icon: './ElectricHome01Icon.js',
  ElectricHome02Icon: './ElectricHome02Icon.js',
  ElectricPlugsIcon: './ElectricPlugsIcon.js',
  ElectricTower01Icon: './ElectricTower01Icon.js',
  ElectricTower02Icon: './ElectricTower02Icon.js',
  ElectricWireIcon: './ElectricWireIcon.js',
  ElectricityStackIcon: './ElectricityStackIcon.js',
  EllipseSelectionIcon: './EllipseSelectionIcon.js',
  EllipseIcon: './EllipseIcon.js',
  EllipsisIcon: './EllipsisIcon.js',
  EllipsisVerticalIcon: './EllipsisVerticalIcon.js',
  EncryptIcon: './EncryptIcon.js',
  EnergyEllipseIcon: './EnergyEllipseIcon.js',
  EnergyRectangleIcon: './EnergyRectangleIcon.js',
  EnergyIcon: './EnergyIcon.js',
  EngineSlashIcon: './EngineSlashIcon.js',
  EngineIcon: './EngineIcon.js',
  EnteringGeoFenceIcon: './EnteringGeoFenceIcon.js',
  EntranceStairsIcon: './EntranceStairsIcon.js',
  EnvatoIcon: './EnvatoIcon.js',
  EqualSignCircleIcon: './EqualSignCircleIcon.js',
  EqualSignSquareIcon: './EqualSignSquareIcon.js',
  EqualSignIcon: './EqualSignIcon.js',
  EquipmentBenchPressIcon: './EquipmentBenchPressIcon.js',
  EquipmentChestPressIcon: './EquipmentChestPressIcon.js',
  EquipmentGym01Icon: './EquipmentGym01Icon.js',
  EquipmentGym02Icon: './EquipmentGym02Icon.js',
  EquipmentGym03Icon: './EquipmentGym03Icon.js',
  EquipmentWeightliftingIcon: './EquipmentWeightliftingIcon.js',
  Eraser01Icon: './Eraser01Icon.js',
  EraserAddIcon: './EraserAddIcon.js',
  EraserAutoIcon: './EraserAutoIcon.js',
  EraserIcon: './EraserIcon.js',
  Estimate01Icon: './Estimate01Icon.js',
  Estimate02Icon: './Estimate02Icon.js',
  EthereumEllipseIcon: './EthereumEllipseIcon.js',
  EthereumRectangleIcon: './EthereumRectangleIcon.js',
  EthereumIcon: './EthereumIcon.js',
  EthernetPortIcon: './EthernetPortIcon.js',
  EuroCircleIcon: './EuroCircleIcon.js',
  EuroReceiveIcon: './EuroReceiveIcon.js',
  EuroSendIcon: './EuroSendIcon.js',
  EuroSquareIcon: './EuroSquareIcon.js',
  EuroIcon: './EuroIcon.js',
  EvChargerIcon: './EvChargerIcon.js',
  EvChargingIcon: './EvChargingIcon.js',
  EvilIcon: './EvilIcon.js',
  Exchange01Icon: './Exchange01Icon.js',
  Exchange02Icon: './Exchange02Icon.js',
  Exchange03Icon: './Exchange03Icon.js',
  ExchangeBitcoinIcon: './ExchangeBitcoinIcon.js',
  ExchangeDollarIcon: './ExchangeDollarIcon.js',
  ExclamationMarkBigSlashIcon: './ExclamationMarkBigSlashIcon.js',
  ExclamationMarkBigIcon: './ExclamationMarkBigIcon.js',
  ExclamationMarkSlashIcon: './ExclamationMarkSlashIcon.js',
  ExclamationMarkIcon: './ExclamationMarkIcon.js',
  ExpandParagraphIcon: './ExpandParagraphIcon.js',
  ExpandIcon: './ExpandIcon.js',
  ExpanderIcon: './ExpanderIcon.js',
  ExternalDriveIcon: './ExternalDriveIcon.js',
  ExternalLinkIcon: './ExternalLinkIcon.js',
  EyeClosedIcon: './EyeClosedIcon.js',
  EyeOffIcon: './EyeOffIcon.js',
  EyeIcon: './EyeIcon.js',
  FaceIdIcon: './FaceIdIcon.js',
  Facebook01Icon: './Facebook01Icon.js',
  Facebook02Icon: './Facebook02Icon.js',
  Factory01Icon: './Factory01Icon.js',
  Factory02Icon: './Factory02Icon.js',
  FactoryIcon: './FactoryIcon.js',
  FahrenheitIcon: './FahrenheitIcon.js',
  FallingStarIcon: './FallingStarIcon.js',
  Fan01Icon: './Fan01Icon.js',
  Fan02Icon: './Fan02Icon.js',
  FastForwardIcon: './FastForwardIcon.js',
  FastWindIcon: './FastWindIcon.js',
  FavouriteCircleIcon: './FavouriteCircleIcon.js',
  FavouriteSquareIcon: './FavouriteSquareIcon.js',
  FavouriteIcon: './FavouriteIcon.js',
  FeatherIcon: './FeatherIcon.js',
  Female02Icon: './Female02Icon.js',
  FemaleSymbolIcon: './FemaleSymbolIcon.js',
  FenceIcon: './FenceIcon.js',
  FencingMaskIcon: './FencingMaskIcon.js',
  FencingIcon: './FencingIcon.js',
  FerrisWheelIcon: './FerrisWheelIcon.js',
  FerryBoatIcon: './FerryBoatIcon.js',
  FigmaIcon: './FigmaIcon.js',
  File01Icon: './File01Icon.js',
  File02Icon: './File02Icon.js',
  FileAddIcon: './FileAddIcon.js',
  FileArchiveIcon: './FileArchiveIcon.js',
  FileAttachmentIcon: './FileAttachmentIcon.js',
  FileAudioIcon: './FileAudioIcon.js',
  FileAxisThreeDIcon: './FileAxisThreeDIcon.js',
  FileBadgeIcon: './FileBadgeIcon.js',
  FileBitcoinIcon: './FileBitcoinIcon.js',
  FileBlockIcon: './FileBlockIcon.js',
  FileBookmarkIcon: './FileBookmarkIcon.js',
  FileBoxIcon: './FileBoxIcon.js',
  FileBracesCornerIcon: './FileBracesCornerIcon.js',
  FileBracesIcon: './FileBracesIcon.js',
  FileChartColumnIncreasingIcon: './FileChartColumnIncreasingIcon.js',
  FileChartColumnIcon: './FileChartColumnIcon.js',
  FileChartLineIcon: './FileChartLineIcon.js',
  FileChartPieIcon: './FileChartPieIcon.js',
  FileCheckCornerIcon: './FileCheckCornerIcon.js',
  FileCheckIcon: './FileCheckIcon.js',
  FileClockIcon: './FileClockIcon.js',
  FileCloudIcon: './FileCloudIcon.js',
  FileCodeCornerIcon: './FileCodeCornerIcon.js',
  FileCodeIcon: './FileCodeIcon.js',
  FileCogIcon: './FileCogIcon.js',
  FileCorruptIcon: './FileCorruptIcon.js',
  FileDatabaseIcon: './FileDatabaseIcon.js',
  FileDiffIcon: './FileDiffIcon.js',
  FileDigitIcon: './FileDigitIcon.js',
  FileDollarIcon: './FileDollarIcon.js',
  FileDownIcon: './FileDownIcon.js',
  FileDownloadIcon: './FileDownloadIcon.js',
  FileEditIcon: './FileEditIcon.js',
  FileEmpty01Icon: './FileEmpty01Icon.js',
  FileEmpty02Icon: './FileEmpty02Icon.js',
  FileEuroIcon: './FileEuroIcon.js',
  FileExclamationPointIcon: './FileExclamationPointIcon.js',
  FileExportIcon: './FileExportIcon.js',
  FileFavouriteIcon: './FileFavouriteIcon.js',
  FileHeadphoneIcon: './FileHeadphoneIcon.js',
  FileHeartIcon: './FileHeartIcon.js',
  FileImageIcon: './FileImageIcon.js',
  FileImportIcon: './FileImportIcon.js',
  FileInputIcon: './FileInputIcon.js',
  FileKeyIcon: './FileKeyIcon.js',
  FileLinkIcon: './FileLinkIcon.js',
  FileLockIcon: './FileLockIcon.js',
  FileLockedIcon: './FileLockedIcon.js',
  FileManagementIcon: './FileManagementIcon.js',
  FileMinusIcon: './FileMinusIcon.js',
  FileMusicIcon: './FileMusicIcon.js',
  FileNotFoundIcon: './FileNotFoundIcon.js',
  FilePasteIcon: './FilePasteIcon.js',
  FilePenIcon: './FilePenIcon.js',
  FilePinIcon: './FilePinIcon.js',
  FilePlayIcon: './FilePlayIcon.js',
  FilePlusIcon: './FilePlusIcon.js',
  FilePoundIcon: './FilePoundIcon.js',
  FileQuestionMarkIcon: './FileQuestionMarkIcon.js',
  FileRemoveIcon: './FileRemoveIcon.js',
  FileScanIcon: './FileScanIcon.js',
  FileScriptIcon: './FileScriptIcon.js',
  FileSearchIcon: './FileSearchIcon.js',
  FileSecurityIcon: './FileSecurityIcon.js',
  FileShredderIcon: './FileShredderIcon.js',
  FileSignalIcon: './FileSignalIcon.js',
  FileSlidersIcon: './FileSlidersIcon.js',
  FileSpreadsheetIcon: './FileSpreadsheetIcon.js',
  FileStackIcon: './FileStackIcon.js',
  FileStarIcon: './FileStarIcon.js',
  FileSyncIcon: './FileSyncIcon.js',
  FileTerminalIcon: './FileTerminalIcon.js',
  FileTypeIcon: './FileTypeIcon.js',
  FileUnknownIcon: './FileUnknownIcon.js',
  FileUnlockedIcon: './FileUnlockedIcon.js',
  FileUpIcon: './FileUpIcon.js',
  FileUploadIcon: './FileUploadIcon.js',
  FileUserIcon: './FileUserIcon.js',
  FileValidationIcon: './FileValidationIcon.js',
  FileVerifiedIcon: './FileVerifiedIcon.js',
  FileVideoCameraIcon: './FileVideoCameraIcon.js',
  FileVideoIcon: './FileVideoIcon.js',
  FileViewIcon: './FileViewIcon.js',
  FileVolumeIcon: './FileVolumeIcon.js',
  FileXCornerIcon: './FileXCornerIcon.js',
  FileXIcon: './FileXIcon.js',
  FileYenIcon: './FileYenIcon.js',
  FileZipIcon: './FileZipIcon.js',
  Files01Icon: './Files01Icon.js',
  Files02Icon: './Files02Icon.js',
  Film01Icon: './Film01Icon.js',
  Film02Icon: './Film02Icon.js',
  FilmRoll01Icon: './FilmRoll01Icon.js',
  FilmRoll02Icon: './FilmRoll02Icon.js',
  FilterAddIcon: './FilterAddIcon.js',
  FilterEditIcon: './FilterEditIcon.js',
  FilterHorizontalIcon: './FilterHorizontalIcon.js',
  FilterMailCircleIcon: './FilterMailCircleIcon.js',
  FilterMailEditIcon: './FilterMailEditIcon.js',
  FilterMailRemoveIcon: './FilterMailRemoveIcon.js',
  FilterMailSquareIcon: './FilterMailSquareIcon.js',
  FilterMailIcon: './FilterMailIcon.js',
  FilterRemoveIcon: './FilterRemoveIcon.js',
  FilterResetIcon: './FilterResetIcon.js',
  FilterIcon: './FilterIcon.js',
  FilterVerticalIcon: './FilterVerticalIcon.js',
  FingerAccessIcon: './FingerAccessIcon.js',
  FingerPrintAddIcon: './FingerPrintAddIcon.js',
  FingerPrintCheckIcon: './FingerPrintCheckIcon.js',
  FingerPrintMinusIcon: './FingerPrintMinusIcon.js',
  FingerPrintRemoveIcon: './FingerPrintRemoveIcon.js',
  FingerPrintScanIcon: './FingerPrintScanIcon.js',
  FingerPrintIcon: './FingerPrintIcon.js',
  FingerprintPatternIcon: './FingerprintPatternIcon.js',
  FinsIcon: './FinsIcon.js',
  Fire02Icon: './Fire02Icon.js',
  Fire03Icon: './Fire03Icon.js',
  FireExtinguisherIcon: './FireExtinguisherIcon.js',
  FirePitIcon: './FirePitIcon.js',
  FireSecurityIcon: './FireSecurityIcon.js',
  FireIcon: './FireIcon.js',
  FirewallIcon: './FirewallIcon.js',
  FireworksIcon: './FireworksIcon.js',
  FirstAidKitIcon: './FirstAidKitIcon.js',
  FishFoodIcon: './FishFoodIcon.js',
  FishOffIcon: './FishOffIcon.js',
  FishIcon: './FishIcon.js',
  FishSymbolIcon: './FishSymbolIcon.js',
  FishingHookIcon: './FishingHookIcon.js',
  FishingRodIcon: './FishingRodIcon.js',
  FitToScreenIcon: './FitToScreenIcon.js',
  FiveCircleIcon: './FiveCircleIcon.js',
  FiveSquareIcon: './FiveSquareIcon.js',
  FiverrIcon: './FiverrIcon.js',
  Flag01Icon: './Flag01Icon.js',
  Flag02Icon: './Flag02Icon.js',
  Flag03Icon: './Flag03Icon.js',
  FlagOffIcon: './FlagOffIcon.js',
  FlagTriangleLeftIcon: './FlagTriangleLeftIcon.js',
  FlagTriangleRightIcon: './FlagTriangleRightIcon.js',
  FlameKindlingIcon: './FlameKindlingIcon.js',
  FlameIcon: './FlameIcon.js',
  FlashOffIcon: './FlashOffIcon.js',
  FlashIcon: './FlashIcon.js',
  FlashlightOffIcon: './FlashlightOffIcon.js',
  FlashlightIcon: './FlashlightIcon.js',
  FlaskConicalOffIcon: './FlaskConicalOffIcon.js',
  FlaskConicalIcon: './FlaskConicalIcon.js',
  FlaskRoundIcon: './FlaskRoundIcon.js',
  FlaticonIcon: './FlaticonIcon.js',
  FlickrIcon: './FlickrIcon.js',
  FlimSlateIcon: './FlimSlateIcon.js',
  FlipBottomIcon: './FlipBottomIcon.js',
  FlipHorizontalIcon: './FlipHorizontalIcon.js',
  FlipLeftIcon: './FlipLeftIcon.js',
  FlipPhoneIcon: './FlipPhoneIcon.js',
  FlipRightIcon: './FlipRightIcon.js',
  FlipTopIcon: './FlipTopIcon.js',
  FlipVerticalIcon: './FlipVerticalIcon.js',
  FloorPlanIcon: './FloorPlanIcon.js',
  FloppyDiskIcon: './FloppyDiskIcon.js',
  FlowCircleIcon: './FlowCircleIcon.js',
  FlowConnectionIcon: './FlowConnectionIcon.js',
  FlowSquareIcon: './FlowSquareIcon.js',
  FlowIcon: './FlowIcon.js',
  Flowchart01Icon: './Flowchart01Icon.js',
  Flowchart02Icon: './Flowchart02Icon.js',
  FlowerPotIcon: './FlowerPotIcon.js',
  FlowerIcon: './FlowerIcon.js',
  FlushedIcon: './FlushedIcon.js',
  FlyingHumanIcon: './FlyingHumanIcon.js',
  FocusPointIcon: './FocusPointIcon.js',
  FocusIcon: './FocusIcon.js',
  FoldHorizontalIcon: './FoldHorizontalIcon.js',
  FoldVerticalIcon: './FoldVerticalIcon.js',
  Folder01Icon: './Folder01Icon.js',
  Folder02Icon: './Folder02Icon.js',
  Folder03Icon: './Folder03Icon.js',
  FolderAddIcon: './FolderAddIcon.js',
  FolderArchiveIcon: './FolderArchiveIcon.js',
  FolderAttachmentIcon: './FolderAttachmentIcon.js',
  FolderAudioIcon: './FolderAudioIcon.js',
  FolderBlockIcon: './FolderBlockIcon.js',
  FolderCheckIcon: './FolderCheckIcon.js',
  FolderClockIcon: './FolderClockIcon.js',
  FolderCloudIcon: './FolderCloudIcon.js',
  FolderCodeIcon: './FolderCodeIcon.js',
  FolderCogIcon: './FolderCogIcon.js',
  FolderDetailsReferenceIcon: './FolderDetailsReferenceIcon.js',
  FolderDetailsIcon: './FolderDetailsIcon.js',
  FolderDotIcon: './FolderDotIcon.js',
  FolderDownloadIcon: './FolderDownloadIcon.js',
  FolderEditIcon: './FolderEditIcon.js',
  FolderExportIcon: './FolderExportIcon.js',
  FolderFavouriteIcon: './FolderFavouriteIcon.js',
  FolderFileStorageIcon: './FolderFileStorageIcon.js',
  FolderGitTwoIcon: './FolderGitTwoIcon.js',
  FolderGitIcon: './FolderGitIcon.js',
  FolderHeartIcon: './FolderHeartIcon.js',
  FolderImportIcon: './FolderImportIcon.js',
  FolderInputIcon: './FolderInputIcon.js',
  FolderKanbanIcon: './FolderKanbanIcon.js',
  FolderKeyIcon: './FolderKeyIcon.js',
  FolderLibraryIcon: './FolderLibraryIcon.js',
  FolderLinksIcon: './FolderLinksIcon.js',
  FolderLockedIcon: './FolderLockedIcon.js',
  FolderManagementIcon: './FolderManagementIcon.js',
  FolderMinusIcon: './FolderMinusIcon.js',
  FolderMusicIcon: './FolderMusicIcon.js',
  FolderOffIcon: './FolderOffIcon.js',
  FolderOpenIcon: './FolderOpenIcon.js',
  FolderOutputIcon: './FolderOutputIcon.js',
  FolderPenIcon: './FolderPenIcon.js',
  FolderPinIcon: './FolderPinIcon.js',
  FolderRemoveIcon: './FolderRemoveIcon.js',
  FolderRootIcon: './FolderRootIcon.js',
  FolderSearchTwoIcon: './FolderSearchTwoIcon.js',
  FolderSearchIcon: './FolderSearchIcon.js',
  FolderSecurityIcon: './FolderSecurityIcon.js',
  FolderShared01Icon: './FolderShared01Icon.js',
  FolderShared02Icon: './FolderShared02Icon.js',
  FolderShared03Icon: './FolderShared03Icon.js',
  FolderSymlinkIcon: './FolderSymlinkIcon.js',
  FolderSyncIcon: './FolderSyncIcon.js',
  FolderTransferIcon: './FolderTransferIcon.js',
  FolderTreeIcon: './FolderTreeIcon.js',
  FolderUnknownIcon: './FolderUnknownIcon.js',
  FolderUnlockedIcon: './FolderUnlockedIcon.js',
  FolderUploadIcon: './FolderUploadIcon.js',
  FolderVideoIcon: './FolderVideoIcon.js',
  FolderViewIcon: './FolderViewIcon.js',
  FolderZipIcon: './FolderZipIcon.js',
  FoldersIcon: './FoldersIcon.js',
  FootballPitchIcon: './FootballPitchIcon.js',
  FootballIcon: './FootballIcon.js',
  FootprintsIcon: './FootprintsIcon.js',
  ForgotPasswordIcon: './ForgotPasswordIcon.js',
  ForkIcon: './ForkIcon.js',
  ForkliftIcon: './ForkliftIcon.js',
  FormIcon: './FormIcon.js',
  ForrstIcon: './ForrstIcon.js',
  FortressIcon: './FortressIcon.js',
  Forward01Icon: './Forward01Icon.js',
  Forward02Icon: './Forward02Icon.js',
  FourCircleIcon: './FourCircleIcon.js',
  FourFinger02Icon: './FourFinger02Icon.js',
  FourFinger03Icon: './FourFinger03Icon.js',
  FourSquareIcon: './FourSquareIcon.js',
  FrameIcon: './FrameIcon.js',
  FramerIcon: './FramerIcon.js',
  FrameworksIcon: './FrameworksIcon.js',
  FrenchFries01Icon: './FrenchFries01Icon.js',
  FrenchFries02Icon: './FrenchFries02Icon.js',
  FridgeIcon: './FridgeIcon.js',
  FrisbeeIcon: './FrisbeeIcon.js',
  FrownIcon: './FrownIcon.js',
  FryIcon: './FryIcon.js',
  Fuel01Icon: './Fuel01Icon.js',
  Fuel02Icon: './Fuel02Icon.js',
  FuelStationIcon: './FuelStationIcon.js',
  FuelIcon: './FuelIcon.js',
  FullScreenIcon: './FullScreenIcon.js',
  FullSignalIcon: './FullSignalIcon.js',
  FunctionCircleIcon: './FunctionCircleIcon.js',
  FunctionOfXIcon: './FunctionOfXIcon.js',
  FunctionSquareIcon: './FunctionSquareIcon.js',
  FunctionIcon: './FunctionIcon.js',
  FunnelPlusIcon: './FunnelPlusIcon.js',
  FunnelIcon: './FunnelIcon.js',
  FunnelXIcon: './FunnelXIcon.js',
  GalaxyIcon: './GalaxyIcon.js',
  GalleryHorizontalEndIcon: './GalleryHorizontalEndIcon.js',
  GalleryHorizontalIcon: './GalleryHorizontalIcon.js',
  GalleryThumbnailsIcon: './GalleryThumbnailsIcon.js',
  GalleryVerticalEndIcon: './GalleryVerticalEndIcon.js',
  GalleryVerticalIcon: './GalleryVerticalIcon.js',
  GameController01Icon: './GameController01Icon.js',
  GameController02Icon: './GameController02Icon.js',
  GameController03Icon: './GameController03Icon.js',
  GameIcon: './GameIcon.js',
  GameboyIcon: './GameboyIcon.js',
  GamepadDirectionalIcon: './GamepadDirectionalIcon.js',
  GamepadIcon: './GamepadIcon.js',
  GarageIcon: './GarageIcon.js',
  GarbageTruckIcon: './GarbageTruckIcon.js',
  GarlandsIcon: './GarlandsIcon.js',
  GasPipeIcon: './GasPipeIcon.js',
  GasStoveIcon: './GasStoveIcon.js',
  GaugeIcon: './GaugeIcon.js',
  GavelIcon: './GavelIcon.js',
  GearsIcon: './GearsIcon.js',
  GemIcon: './GemIcon.js',
  GeologyCrustIcon: './GeologyCrustIcon.js',
  GeometricShapes01Icon: './GeometricShapes01Icon.js',
  GeometricShapes02Icon: './GeometricShapes02Icon.js',
  GeorgianLariIcon: './GeorgianLariIcon.js',
  GhostIcon: './GhostIcon.js',
  GibbousMoonIcon: './GibbousMoonIcon.js',
  Gif01Icon: './Gif01Icon.js',
  Gif02Icon: './Gif02Icon.js',
  GiftCard02Icon: './GiftCard02Icon.js',
  GiftCardIcon: './GiftCardIcon.js',
  GiftIcon: './GiftIcon.js',
  GitBranchMinusIcon: './GitBranchMinusIcon.js',
  GitBranchPlusIcon: './GitBranchPlusIcon.js',
  GitBranchIcon: './GitBranchIcon.js',
  GitCommitHorizontalIcon: './GitCommitHorizontalIcon.js',
  GitCommitIcon: './GitCommitIcon.js',
  GitCommitVerticalIcon: './GitCommitVerticalIcon.js',
  GitCompareArrowsIcon: './GitCompareArrowsIcon.js',
  GitCompareIcon: './GitCompareIcon.js',
  GitForkIcon: './GitForkIcon.js',
  GitGraphIcon: './GitGraphIcon.js',
  GitMergeConflictIcon: './GitMergeConflictIcon.js',
  GitMergeIcon: './GitMergeIcon.js',
  GitPullRequestArrowIcon: './GitPullRequestArrowIcon.js',
  GitPullRequestClosedIcon: './GitPullRequestClosedIcon.js',
  GitPullRequestCreateArrowIcon: './GitPullRequestCreateArrowIcon.js',
  GitPullRequestCreateIcon: './GitPullRequestCreateIcon.js',
  GitPullRequestDraftIcon: './GitPullRequestDraftIcon.js',
  GitPullRequestIcon: './GitPullRequestIcon.js',
  GitbookIcon: './GitbookIcon.js',
  Github01Icon: './Github01Icon.js',
  GithubIcon: './GithubIcon.js',
  GitlabIcon: './GitlabIcon.js',
  GiveBloodIcon: './GiveBloodIcon.js',
  GivePillIcon: './GivePillIcon.js',
  GlassWaterIcon: './GlassWaterIcon.js',
  GlassesIcon: './GlassesIcon.js',
  GlobalEditingIcon: './GlobalEditingIcon.js',
  GlobalEducationIcon: './GlobalEducationIcon.js',
  GlobalRefreshIcon: './GlobalRefreshIcon.js',
  GlobalSearchIcon: './GlobalSearchIcon.js',
  GlobalIcon: './GlobalIcon.js',
  Globe02Icon: './Globe02Icon.js',
  GlobeLockIcon: './GlobeLockIcon.js',
  GlobeOffIcon: './GlobeOffIcon.js',
  GlobeIcon: './GlobeIcon.js',
  GlobeXIcon: './GlobeXIcon.js',
  GloveIcon: './GloveIcon.js',
  GnomeIcon: './GnomeIcon.js',
  GoBackward10SecIcon: './GoBackward10SecIcon.js',
  GoBackward15SecIcon: './GoBackward15SecIcon.js',
  GoBackward30SecIcon: './GoBackward30SecIcon.js',
  GoBackwardFiveSecIcon: './GoBackwardFiveSecIcon.js',
  GoBackward60SecIcon: './GoBackward60SecIcon.js',
  GoForward10SecIcon: './GoForward10SecIcon.js',
  GoForward15SecIcon: './GoForward15SecIcon.js',
  GoForward30SecIcon: './GoForward30SecIcon.js',
  GoForwardFiveSecIcon: './GoForwardFiveSecIcon.js',
  GoForward60SecIcon: './GoForward60SecIcon.js',
  GoalIcon: './GoalIcon.js',
  GoldBuyIcon: './GoldBuyIcon.js',
  GoldIngotsIcon: './GoldIngotsIcon.js',
  GoldSellIcon: './GoldSellIcon.js',
  GoldIcon: './GoldIcon.js',
  GolfBallIcon: './GolfBallIcon.js',
  GolfBatIcon: './GolfBatIcon.js',
  GolfCartIcon: './GolfCartIcon.js',
  GolfHoleIcon: './GolfHoleIcon.js',
  GoogleDocIcon: './GoogleDocIcon.js',
  GoogleDriveIcon: './GoogleDriveIcon.js',
  GoogleGeminiIcon: './GoogleGeminiIcon.js',
  GoogleHomeIcon: './GoogleHomeIcon.js',
  GoogleLensIcon: './GoogleLensIcon.js',
  GoogleMapsIcon: './GoogleMapsIcon.js',
  GooglePhotosIcon: './GooglePhotosIcon.js',
  GoogleSheetIcon: './GoogleSheetIcon.js',
  GoogleIcon: './GoogleIcon.js',
  Gps01Icon: './Gps01Icon.js',
  Gps02Icon: './Gps02Icon.js',
  GpsDisconnectedIcon: './GpsDisconnectedIcon.js',
  GpsOff01Icon: './GpsOff01Icon.js',
  GpsOff02Icon: './GpsOff02Icon.js',
  GpsSignal01Icon: './GpsSignal01Icon.js',
  GpsSignal02Icon: './GpsSignal02Icon.js',
  GpuIcon: './GpuIcon.js',
  GraduateFemaleIcon: './GraduateFemaleIcon.js',
  GraduateMaleIcon: './GraduateMaleIcon.js',
  GraduationCapIcon: './GraduationCapIcon.js',
  GraduationScrollIcon: './GraduationScrollIcon.js',
  GrapeIcon: './GrapeIcon.js',
  GrapesIcon: './GrapesIcon.js',
  GraphicCardIcon: './GraphicCardIcon.js',
  GravityIcon: './GravityIcon.js',
  GreaterThanCircleIcon: './GreaterThanCircleIcon.js',
  GreaterThanSquareIcon: './GreaterThanSquareIcon.js',
  GreaterThanIcon: './GreaterThanIcon.js',
  GreekHelmetIcon: './GreekHelmetIcon.js',
  GreenHouseIcon: './GreenHouseIcon.js',
  Grid02Icon: './Grid02Icon.js',
  GridOffIcon: './GridOffIcon.js',
  GridIcon: './GridIcon.js',
  GridTableIcon: './GridTableIcon.js',
  GridViewIcon: './GridViewIcon.js',
  Grid2X2CheckIcon: './Grid2X2CheckIcon.js',
  Grid2X2PlusIcon: './Grid2X2PlusIcon.js',
  Grid2X2Icon: './Grid2X2Icon.js',
  Grid2X2XIcon: './Grid2X2XIcon.js',
  Grid3X2Icon: './Grid3X2Icon.js',
  Grid3X3Icon: './Grid3X3Icon.js',
  GrimacingIcon: './GrimacingIcon.js',
  GrinningIcon: './GrinningIcon.js',
  GripHorizontalIcon: './GripHorizontalIcon.js',
  GripIcon: './GripIcon.js',
  GripVerticalIcon: './GripVerticalIcon.js',
  Grok02Icon: './Grok02Icon.js',
  GrokIcon: './GrokIcon.js',
  Group01Icon: './Group01Icon.js',
  GroupItemsIcon: './GroupItemsIcon.js',
  GroupLayersIcon: './GroupLayersIcon.js',
  GroupIcon: './GroupIcon.js',
  GrowwIcon: './GrowwIcon.js',
  GuestHouseIcon: './GuestHouseIcon.js',
  GuitarIcon: './GuitarIcon.js',
  GunIcon: './GunIcon.js',
  GymnasticRingsIcon: './GymnasticRingsIcon.js',
  GymnasticIcon: './GymnasticIcon.js',
  HackerrankIcon: './HackerrankIcon.js',
  HairClipsIcon: './HairClipsIcon.js',
  HairDryerIcon: './HairDryerIcon.js',
  HajiIcon: './HajiIcon.js',
  HalalLabIcon: './HalalLabIcon.js',
  HalalIcon: './HalalIcon.js',
  HamIcon: './HamIcon.js',
  Hamburger01Icon: './Hamburger01Icon.js',
  Hamburger02Icon: './Hamburger02Icon.js',
  HammerIcon: './HammerIcon.js',
  HandBag01Icon: './HandBag01Icon.js',
  HandBag02Icon: './HandBag02Icon.js',
  HandBeaterIcon: './HandBeaterIcon.js',
  HandCoinsIcon: './HandCoinsIcon.js',
  HandFistIcon: './HandFistIcon.js',
  HandGrabIcon: './HandGrabIcon.js',
  HandGripIcon: './HandGripIcon.js',
  HandHeartIcon: './HandHeartIcon.js',
  HandHelpingIcon: './HandHelpingIcon.js',
  HandMetalIcon: './HandMetalIcon.js',
  HandPlatterIcon: './HandPlatterIcon.js',
  HandPointingDown01Icon: './HandPointingDown01Icon.js',
  HandPointingDown02Icon: './HandPointingDown02Icon.js',
  HandPointingDown03Icon: './HandPointingDown03Icon.js',
  HandPointingDown04Icon: './HandPointingDown04Icon.js',
  HandPointingLeft01Icon: './HandPointingLeft01Icon.js',
  HandPointingLeft02Icon: './HandPointingLeft02Icon.js',
  HandPointingLeft03Icon: './HandPointingLeft03Icon.js',
  HandPointingLeft04Icon: './HandPointingLeft04Icon.js',
  HandPointingRight01Icon: './HandPointingRight01Icon.js',
  HandPointingRight02Icon: './HandPointingRight02Icon.js',
  HandPointingRight03Icon: './HandPointingRight03Icon.js',
  HandPointingRight04Icon: './HandPointingRight04Icon.js',
  HandPrayerIcon: './HandPrayerIcon.js',
  HandSanitizerIcon: './HandSanitizerIcon.js',
  HandIcon: './HandIcon.js',
  HandbagIcon: './HandbagIcon.js',
  HandcuffsIcon: './HandcuffsIcon.js',
  HandsClappingIcon: './HandsClappingIcon.js',
  HandshakeIcon: './HandshakeIcon.js',
  HangerIcon: './HangerIcon.js',
  HangingClockIcon: './HangingClockIcon.js',
  HangoutIcon: './HangoutIcon.js',
  Happy01Icon: './Happy01Icon.js',
  HappyIcon: './HappyIcon.js',
  HardDriveDownloadIcon: './HardDriveDownloadIcon.js',
  HardDriveIcon: './HardDriveIcon.js',
  HardDriveUploadIcon: './HardDriveUploadIcon.js',
  HardHatIcon: './HardHatIcon.js',
  HashIcon: './HashIcon.js',
  HashtagIcon: './HashtagIcon.js',
  HatGlassesIcon: './HatGlassesIcon.js',
  HatIcon: './HatIcon.js',
  HazeIcon: './HazeIcon.js',
  HdIcon: './HdIcon.js',
  HddIcon: './HddIcon.js',
  HdmiPortIcon: './HdmiPortIcon.js',
  Hdr01Icon: './Hdr01Icon.js',
  Hdr02Icon: './Hdr02Icon.js',
  Heading01Icon: './Heading01Icon.js',
  Heading02Icon: './Heading02Icon.js',
  Heading03Icon: './Heading03Icon.js',
  Heading04Icon: './Heading04Icon.js',
  Heading05Icon: './Heading05Icon.js',
  Heading06Icon: './Heading06Icon.js',
  HeadingIcon: './HeadingIcon.js',
  HeadphoneMuteIcon: './HeadphoneMuteIcon.js',
  HeadphoneOffIcon: './HeadphoneOffIcon.js',
  HeadphonesIcon: './HeadphonesIcon.js',
  HeadsetConnectedIcon: './HeadsetConnectedIcon.js',
  HeadsetOffIcon: './HeadsetOffIcon.js',
  HeadsetIcon: './HeadsetIcon.js',
  HealtcareIcon: './HealtcareIcon.js',
  HealthIcon: './HealthIcon.js',
  HeartAddIcon: './HeartAddIcon.js',
  HeartCheckIcon: './HeartCheckIcon.js',
  HeartCrackIcon: './HeartCrackIcon.js',
  HeartHandshakeIcon: './HeartHandshakeIcon.js',
  HeartMinusIcon: './HeartMinusIcon.js',
  HeartOffIcon: './HeartOffIcon.js',
  HeartPlusIcon: './HeartPlusIcon.js',
  HeartPulseIcon: './HeartPulseIcon.js',
  HeartRemoveIcon: './HeartRemoveIcon.js',
  HeartIcon: './HeartIcon.js',
  HeartbreakIcon: './HeartbreakIcon.js',
  HeaterIcon: './HeaterIcon.js',
  HelicopterIcon: './HelicopterIcon.js',
  HelpCircleIcon: './HelpCircleIcon.js',
  HelpSquareIcon: './HelpSquareIcon.js',
  Hexagon01Icon: './Hexagon01Icon.js',
  HexagonIcon: './HexagonIcon.js',
  HierarchyCircle01Icon: './HierarchyCircle01Icon.js',
  HierarchyCircle02Icon: './HierarchyCircle02Icon.js',
  HierarchyCircle03Icon: './HierarchyCircle03Icon.js',
  HierarchyFilesIcon: './HierarchyFilesIcon.js',
  HierarchySquare01Icon: './HierarchySquare01Icon.js',
  HierarchySquare02Icon: './HierarchySquare02Icon.js',
  HierarchySquare03Icon: './HierarchySquare03Icon.js',
  HierarchySquare04Icon: './HierarchySquare04Icon.js',
  HierarchySquare05Icon: './HierarchySquare05Icon.js',
  HierarchySquare06Icon: './HierarchySquare06Icon.js',
  HierarchySquare07Icon: './HierarchySquare07Icon.js',
  HierarchySquare08Icon: './HierarchySquare08Icon.js',
  HierarchySquare10Icon: './HierarchySquare10Icon.js',
  HierarchyIcon: './HierarchyIcon.js',
  HighHeels01Icon: './HighHeels01Icon.js',
  HighHeels02Icon: './HighHeels02Icon.js',
  HighlighterIcon: './HighlighterIcon.js',
  HijabIcon: './HijabIcon.js',
  HistoryIcon: './HistoryIcon.js',
  HockeyIcon: './HockeyIcon.js',
  Hold01Icon: './Hold01Icon.js',
  Hold02Icon: './Hold02Icon.js',
  Hold03Icon: './Hold03Icon.js',
  Hold04Icon: './Hold04Icon.js',
  Hold05Icon: './Hold05Icon.js',
  HoldLocked01Icon: './HoldLocked01Icon.js',
  HoldLocked02Icon: './HoldLocked02Icon.js',
  HoldPhoneIcon: './HoldPhoneIcon.js',
  HologramIcon: './HologramIcon.js',
  Home01Icon: './Home01Icon.js',
  Home02Icon: './Home02Icon.js',
  Home03Icon: './Home03Icon.js',
  Home04Icon: './Home04Icon.js',
  Home05Icon: './Home05Icon.js',
  Home06Icon: './Home06Icon.js',
  Home07Icon: './Home07Icon.js',
  Home08Icon: './Home08Icon.js',
  Home09Icon: './Home09Icon.js',
  Home10Icon: './Home10Icon.js',
  Home11Icon: './Home11Icon.js',
  Home12Icon: './Home12Icon.js',
  Home13Icon: './Home13Icon.js',
  HomeWifiIcon: './HomeWifiIcon.js',
  Honey01Icon: './Honey01Icon.js',
  Honey02Icon: './Honey02Icon.js',
  HonorIcon: './HonorIcon.js',
  HonourStarIcon: './HonourStarIcon.js',
  HoodieIcon: './HoodieIcon.js',
  HopOffIcon: './HopOffIcon.js',
  HopIcon: './HopIcon.js',
  HorizonalScrollPointIcon: './HorizonalScrollPointIcon.js',
  HorizontalResizeIcon: './HorizontalResizeIcon.js',
  HorseHeadIcon: './HorseHeadIcon.js',
  HorseSaddleIcon: './HorseSaddleIcon.js',
  HorseIcon: './HorseIcon.js',
  Hospital01Icon: './Hospital01Icon.js',
  Hospital02Icon: './Hospital02Icon.js',
  HospitalBed01Icon: './HospitalBed01Icon.js',
  HospitalBed02Icon: './HospitalBed02Icon.js',
  HospitalLocationIcon: './HospitalLocationIcon.js',
  HotAirBalloonIcon: './HotAirBalloonIcon.js',
  HotPriceIcon: './HotPriceIcon.js',
  HotTubeIcon: './HotTubeIcon.js',
  HotdogIcon: './HotdogIcon.js',
  Hotel01Icon: './Hotel01Icon.js',
  Hotel02Icon: './Hotel02Icon.js',
  HotelBellIcon: './HotelBellIcon.js',
  HotspotOfflineIcon: './HotspotOfflineIcon.js',
  HotspotIcon: './HotspotIcon.js',
  HourglassOffIcon: './HourglassOffIcon.js',
  HourglassIcon: './HourglassIcon.js',
  House01Icon: './House01Icon.js',
  House02Icon: './House02Icon.js',
  House03Icon: './House03Icon.js',
  House04Icon: './House04Icon.js',
  House05Icon: './House05Icon.js',
  HouseHeartIcon: './HouseHeartIcon.js',
  HousePlugIcon: './HousePlugIcon.js',
  HousePlusIcon: './HousePlusIcon.js',
  HouseSolarPanelIcon: './HouseSolarPanelIcon.js',
  HouseWifiIcon: './HouseWifiIcon.js',
  HtmlFiveIcon: './HtmlFiveIcon.js',
  HtmlFile01Icon: './HtmlFile01Icon.js',
  HtmlFile02Icon: './HtmlFile02Icon.js',
  HugeiconsIcon: './HugeiconsIcon.js',
  HumidityIcon: './HumidityIcon.js',
  HutIcon: './HutIcon.js',
  HydroPowerIcon: './HydroPowerIcon.js',
  HyperboleIcon: './HyperboleIcon.js',
  IceCream01Icon: './IceCream01Icon.js',
  IceCream02Icon: './IceCream02Icon.js',
  IceCream03Icon: './IceCream03Icon.js',
  IceCream04Icon: './IceCream04Icon.js',
  IceCubesIcon: './IceCubesIcon.js',
  IceHockeyIcon: './IceHockeyIcon.js',
  IceSkatingIcon: './IceSkatingIcon.js',
  IcoIcon: './IcoIcon.js',
  IconjarIcon: './IconjarIcon.js',
  IdCardLanyardIcon: './IdCardLanyardIcon.js',
  IdNotVerifiedIcon: './IdNotVerifiedIcon.js',
  IdIcon: './IdIcon.js',
  IdVerifiedIcon: './IdVerifiedIcon.js',
  Idea01Icon: './Idea01Icon.js',
  IdeaIcon: './IdeaIcon.js',
  IdentificationIcon: './IdentificationIcon.js',
  IdentityCardIcon: './IdentityCardIcon.js',
  Image01Icon: './Image01Icon.js',
  Image02Icon: './Image02Icon.js',
  Image03Icon: './Image03Icon.js',
  ImageActualSizeIcon: './ImageActualSizeIcon.js',
  ImageAdd01Icon: './ImageAdd01Icon.js',
  ImageAdd02Icon: './ImageAdd02Icon.js',
  ImageCompositionOvalIcon: './ImageCompositionOvalIcon.js',
  ImageCompositionIcon: './ImageCompositionIcon.js',
  ImageCounterClockwiseIcon: './ImageCounterClockwiseIcon.js',
  ImageCropIcon: './ImageCropIcon.js',
  ImageDelete01Icon: './ImageDelete01Icon.js',
  ImageDelete02Icon: './ImageDelete02Icon.js',
  ImageDone01Icon: './ImageDone01Icon.js',
  ImageDone02Icon: './ImageDone02Icon.js',
  ImageDownload02Icon: './ImageDownload02Icon.js',
  ImageDownloadIcon: './ImageDownloadIcon.js',
  ImageFlipHorizontalIcon: './ImageFlipHorizontalIcon.js',
  ImageFlipVerticalIcon: './ImageFlipVerticalIcon.js',
  ImageNotFound01Icon: './ImageNotFound01Icon.js',
  ImageNotFound02Icon: './ImageNotFound02Icon.js',
  ImagePlayIcon: './ImagePlayIcon.js',
  ImageRemove01Icon: './ImageRemove01Icon.js',
  ImageRemove02Icon: './ImageRemove02Icon.js',
  ImageRotationClockwiseIcon: './ImageRotationClockwiseIcon.js',
  ImageUpload01Icon: './ImageUpload01Icon.js',
  ImageUploadIcon: './ImageUploadIcon.js',
  ImoIcon: './ImoIcon.js',
  ImportantBookIcon: './ImportantBookIcon.js',
  InLoveIcon: './InLoveIcon.js',
  InboxCheckIcon: './InboxCheckIcon.js',
  InboxDownloadIcon: './InboxDownloadIcon.js',
  InboxIcon: './InboxIcon.js',
  InboxUnreadIcon: './InboxUnreadIcon.js',
  InboxUploadIcon: './InboxUploadIcon.js',
  IncognitoIcon: './IncognitoIcon.js',
  IndiaGateIcon: './IndiaGateIcon.js',
  Inequality01Icon: './Inequality01Icon.js',
  Inequality02Icon: './Inequality02Icon.js',
  InequalityCircle01Icon: './InequalityCircle01Icon.js',
  InequalityCircle02Icon: './InequalityCircle02Icon.js',
  InequalitySquare01Icon: './InequalitySquare01Icon.js',
  InequalitySquare02Icon: './InequalitySquare02Icon.js',
  InfantIcon: './InfantIcon.js',
  Infinity01Icon: './Infinity01Icon.js',
  Infinity02Icon: './Infinity02Icon.js',
  InfinityCircleIcon: './InfinityCircleIcon.js',
  InfinitySquareIcon: './InfinitySquareIcon.js',
  InformationCircleIcon: './InformationCircleIcon.js',
  InformationDiamondIcon: './InformationDiamondIcon.js',
  InformationSquareIcon: './InformationSquareIcon.js',
  InjectionIcon: './InjectionIcon.js',
  InputCursorTextIcon: './InputCursorTextIcon.js',
  InputLongTextIcon: './InputLongTextIcon.js',
  InputNumericIcon: './InputNumericIcon.js',
  InputShortTextIcon: './InputShortTextIcon.js',
  InputTextIcon: './InputTextIcon.js',
  InsertBottomImageIcon: './InsertBottomImageIcon.js',
  InsertCenterImageIcon: './InsertCenterImageIcon.js',
  InsertColumnLeftIcon: './InsertColumnLeftIcon.js',
  InsertColumnRightIcon: './InsertColumnRightIcon.js',
  InsertColumnIcon: './InsertColumnIcon.js',
  InsertPiIcon: './InsertPiIcon.js',
  InsertRowDownIcon: './InsertRowDownIcon.js',
  InsertRowIcon: './InsertRowIcon.js',
  InsertRowUpIcon: './InsertRowUpIcon.js',
  InsertTopImageIcon: './InsertTopImageIcon.js',
  InspectCodeIcon: './InspectCodeIcon.js',
  InstagramIcon: './InstagramIcon.js',
  InstallingUpdates01Icon: './InstallingUpdates01Icon.js',
  InstallingUpdates02Icon: './InstallingUpdates02Icon.js',
  InternetAntenna01Icon: './InternetAntenna01Icon.js',
  InternetAntenna02Icon: './InternetAntenna02Icon.js',
  InternetAntenna03Icon: './InternetAntenna03Icon.js',
  InternetAntenna04Icon: './InternetAntenna04Icon.js',
  InternetIcon: './InternetIcon.js',
  InvestigationIcon: './InvestigationIcon.js',
  Invoice01Icon: './Invoice01Icon.js',
  Invoice02Icon: './Invoice02Icon.js',
  Invoice03Icon: './Invoice03Icon.js',
  Invoice04Icon: './Invoice04Icon.js',
  InvoiceIcon: './InvoiceIcon.js',
  IpodIcon: './IpodIcon.js',
  IrisScanIcon: './IrisScanIcon.js',
  IslandIcon: './IslandIcon.js',
  IterationCcwIcon: './IterationCcwIcon.js',
  IterationCwIcon: './IterationCwIcon.js',
  JarIcon: './JarIcon.js',
  JavaScriptIcon: './JavaScriptIcon.js',
  JavaIcon: './JavaIcon.js',
  JobLinkIcon: './JobLinkIcon.js',
  JobSearchIcon: './JobSearchIcon.js',
  JobShareIcon: './JobShareIcon.js',
  JoggerPantsIcon: './JoggerPantsIcon.js',
  JoinBevelIcon: './JoinBevelIcon.js',
  JoinRoundIcon: './JoinRoundIcon.js',
  JoinStraightIcon: './JoinStraightIcon.js',
  JokerIcon: './JokerIcon.js',
  Joystick01Icon: './Joystick01Icon.js',
  Joystick02Icon: './Joystick02Icon.js',
  Joystick03Icon: './Joystick03Icon.js',
  Joystick04Icon: './Joystick04Icon.js',
  Joystick05Icon: './Joystick05Icon.js',
  Jpg01Icon: './Jpg01Icon.js',
  Jpg02Icon: './Jpg02Icon.js',
  Jsx01Icon: './Jsx01Icon.js',
  Jsx02Icon: './Jsx02Icon.js',
  Jsx03Icon: './Jsx03Icon.js',
  JudgeIcon: './JudgeIcon.js',
  JumpersIcon: './JumpersIcon.js',
  JupiterIcon: './JupiterIcon.js',
  JusticeScale01Icon: './JusticeScale01Icon.js',
  JusticeScale02Icon: './JusticeScale02Icon.js',
  Kaaba01Icon: './Kaaba01Icon.js',
  Kaaba02Icon: './Kaaba02Icon.js',
  KanbanIcon: './KanbanIcon.js',
  KayakIcon: './KayakIcon.js',
  KeffiyehIcon: './KeffiyehIcon.js',
  Kettle01Icon: './Kettle01Icon.js',
  KettleIcon: './KettleIcon.js',
  KettlebellIcon: './KettlebellIcon.js',
  KetupatIcon: './KetupatIcon.js',
  Key01Icon: './Key01Icon.js',
  Key02Icon: './Key02Icon.js',
  KeyGeneratorFobIcon: './KeyGeneratorFobIcon.js',
  KeyboardIcon: './KeyboardIcon.js',
  KeyframeAddIcon: './KeyframeAddIcon.js',
  KeyframeAlignCenterIcon: './KeyframeAlignCenterIcon.js',
  KeyframeAlignHorizontalIcon: './KeyframeAlignHorizontalIcon.js',
  KeyframeAlignVerticalIcon: './KeyframeAlignVerticalIcon.js',
  KeyframeBottomIcon: './KeyframeBottomIcon.js',
  KeyframeLeftIcon: './KeyframeLeftIcon.js',
  KeyframeRemoveIcon: './KeyframeRemoveIcon.js',
  KeyframeRightIcon: './KeyframeRightIcon.js',
  KeyframeIcon: './KeyframeIcon.js',
  KeyframeTopIcon: './KeyframeTopIcon.js',
  KeyframesDoubleAddIcon: './KeyframesDoubleAddIcon.js',
  KeyframesDoubleRemoveIcon: './KeyframesDoubleRemoveIcon.js',
  KeyframesDoubleIcon: './KeyframesDoubleIcon.js',
  KeyframesMultipleAddIcon: './KeyframesMultipleAddIcon.js',
  KeyframesMultipleRemoveIcon: './KeyframesMultipleRemoveIcon.js',
  KeyframesMultipleIcon: './KeyframesMultipleIcon.js',
  Kickstarter01Icon: './Kickstarter01Icon.js',
  Kickstarter02Icon: './Kickstarter02Icon.js',
  KidIcon: './KidIcon.js',
  KidneysIcon: './KidneysIcon.js',
  KimiAiIcon: './KimiAiIcon.js',
  KissingIcon: './KissingIcon.js',
  KitchenUtensilsIcon: './KitchenUtensilsIcon.js',
  KiteIcon: './KiteIcon.js',
  KlarnaIcon: './KlarnaIcon.js',
  Knife01Icon: './Knife01Icon.js',
  Knife02Icon: './Knife02Icon.js',
  KnifeBreadIcon: './KnifeBreadIcon.js',
  KnightShieldIcon: './KnightShieldIcon.js',
  KnivesIcon: './KnivesIcon.js',
  Knowledge01Icon: './Knowledge01Icon.js',
  Knowledge02Icon: './Knowledge02Icon.js',
  KoFiIcon: './KoFiIcon.js',
  Kurta01Icon: './Kurta01Icon.js',
  KurtaIcon: './KurtaIcon.js',
  LabelImportantIcon: './LabelImportantIcon.js',
  LabelIcon: './LabelIcon.js',
  LaborIcon: './LaborIcon.js',
  LabsIcon: './LabsIcon.js',
  LakeIcon: './LakeIcon.js',
  Lamp01Icon: './Lamp01Icon.js',
  Lamp02Icon: './Lamp02Icon.js',
  Lamp03Icon: './Lamp03Icon.js',
  Lamp04Icon: './Lamp04Icon.js',
  Lamp05Icon: './Lamp05Icon.js',
  LampIcon: './LampIcon.js',
  LampWallDownIcon: './LampWallDownIcon.js',
  LampWallUpIcon: './LampWallUpIcon.js',
  LandPlotIcon: './LandPlotIcon.js',
  LandmarkIcon: './LandmarkIcon.js',
  LanguageCircleIcon: './LanguageCircleIcon.js',
  LanguageSkillIcon: './LanguageSkillIcon.js',
  LanguageSquareIcon: './LanguageSquareIcon.js',
  LanternIcon: './LanternIcon.js',
  LaptopAddIcon: './LaptopAddIcon.js',
  LaptopChargingIcon: './LaptopChargingIcon.js',
  LaptopCheckIcon: './LaptopCheckIcon.js',
  LaptopCloudIcon: './LaptopCloudIcon.js',
  LaptopIssueIcon: './LaptopIssueIcon.js',
  LaptopPerformanceIcon: './LaptopPerformanceIcon.js',
  LaptopPhoneSyncIcon: './LaptopPhoneSyncIcon.js',
  LaptopProgrammingIcon: './LaptopProgrammingIcon.js',
  LaptopRemoveIcon: './LaptopRemoveIcon.js',
  LaptopSettingsIcon: './LaptopSettingsIcon.js',
  LaptopIcon: './LaptopIcon.js',
  LaptopVideoIcon: './LaptopVideoIcon.js',
  LassoSelectIcon: './LassoSelectIcon.js',
  LassoTool01Icon: './LassoTool01Icon.js',
  LassoTool02Icon: './LassoTool02Icon.js',
  LastFmIcon: './LastFmIcon.js',
  LatitudeIcon: './LatitudeIcon.js',
  LaughingIcon: './LaughingIcon.js',
  LaurelWreath01Icon: './LaurelWreath01Icon.js',
  LaurelWreath02Icon: './LaurelWreath02Icon.js',
  LaurelWreathFirst01Icon: './LaurelWreathFirst01Icon.js',
  LaurelWreathFirst02Icon: './LaurelWreathFirst02Icon.js',
  LaurelWreathLeft01Icon: './LaurelWreathLeft01Icon.js',
  LaurelWreathLeft02Icon: './LaurelWreathLeft02Icon.js',
  LaurelWreathLeft03Icon: './LaurelWreathLeft03Icon.js',
  LaurelWreathRight01Icon: './LaurelWreathRight01Icon.js',
  LaurelWreathRight02Icon: './LaurelWreathRight02Icon.js',
  LaurelWreathRight03Icon: './LaurelWreathRight03Icon.js',
  LayerAddIcon: './LayerAddIcon.js',
  LayerBringForwardIcon: './LayerBringForwardIcon.js',
  LayerBringToFrontIcon: './LayerBringToFrontIcon.js',
  LayerMask01Icon: './LayerMask01Icon.js',
  LayerMask02Icon: './LayerMask02Icon.js',
  LayerSendBackwardIcon: './LayerSendBackwardIcon.js',
  LayerSendToBackIcon: './LayerSendToBackIcon.js',
  LayerIcon: './LayerIcon.js',
  Layers01Icon: './Layers01Icon.js',
  Layers02Icon: './Layers02Icon.js',
  LayersLogoIcon: './LayersLogoIcon.js',
  Layout01Icon: './Layout01Icon.js',
  Layout02Icon: './Layout02Icon.js',
  Layout03Icon: './Layout03Icon.js',
  Layout04Icon: './Layout04Icon.js',
  Layout05Icon: './Layout05Icon.js',
  Layout06Icon: './Layout06Icon.js',
  Layout07Icon: './Layout07Icon.js',
  LayoutTwoColumnIcon: './LayoutTwoColumnIcon.js',
  LayoutTwoRowIcon: './LayoutTwoRowIcon.js',
  LayoutThreeColumnIcon: './LayoutThreeColumnIcon.js',
  LayoutThreeRowIcon: './LayoutThreeRowIcon.js',
  LayoutAlignBottomIcon: './LayoutAlignBottomIcon.js',
  LayoutAlignLeftIcon: './LayoutAlignLeftIcon.js',
  LayoutAlignRightIcon: './LayoutAlignRightIcon.js',
  LayoutAlignTopIcon: './LayoutAlignTopIcon.js',
  LayoutBottomIcon: './LayoutBottomIcon.js',
  LayoutGridIcon: './LayoutGridIcon.js',
  LayoutLeftIcon: './LayoutLeftIcon.js',
  LayoutRightIcon: './LayoutRightIcon.js',
  LayoutTable01Icon: './LayoutTable01Icon.js',
  LayoutTable02Icon: './LayoutTable02Icon.js',
  LayoutTopIcon: './LayoutTopIcon.js',
  Leaf01Icon: './Leaf01Icon.js',
  Leaf02Icon: './Leaf02Icon.js',
  Leaf03Icon: './Leaf03Icon.js',
  Leaf04Icon: './Leaf04Icon.js',
  LeafyGreenIcon: './LeafyGreenIcon.js',
  LeavingGeoFenceIcon: './LeavingGeoFenceIcon.js',
  LeetcodeIcon: './LeetcodeIcon.js',
  LeftAngleIcon: './LeftAngleIcon.js',
  LeftToRightBlockQuoteIcon: './LeftToRightBlockQuoteIcon.js',
  LeftToRightListBulletIcon: './LeftToRightListBulletIcon.js',
  LeftToRightListDashIcon: './LeftToRightListDashIcon.js',
  LeftToRightListNumberIcon: './LeftToRightListNumberIcon.js',
  LeftToRightListStar01Icon: './LeftToRightListStar01Icon.js',
  LeftToRightListStarIcon: './LeftToRightListStarIcon.js',
  LeftToRightListTriangleIcon: './LeftToRightListTriangleIcon.js',
  LeftTriangleIcon: './LeftTriangleIcon.js',
  Legal01Icon: './Legal01Icon.js',
  Legal02Icon: './Legal02Icon.js',
  LegalDocument01Icon: './LegalDocument01Icon.js',
  LegalDocument02Icon: './LegalDocument02Icon.js',
  LegalHammerIcon: './LegalHammerIcon.js',
  LessThanCircleIcon: './LessThanCircleIcon.js',
  LessThanSquareIcon: './LessThanSquareIcon.js',
  LessThanIcon: './LessThanIcon.js',
  LetterSpacingIcon: './LetterSpacingIcon.js',
  LibrariesIcon: './LibrariesIcon.js',
  LibraryIcon: './LibraryIcon.js',
  LicenseDraftIcon: './LicenseDraftIcon.js',
  LicenseMaintenanceIcon: './LicenseMaintenanceIcon.js',
  LicenseNoIcon: './LicenseNoIcon.js',
  LicensePinIcon: './LicensePinIcon.js',
  LicenseIcon: './LicenseIcon.js',
  LicenseThirdPartyIcon: './LicenseThirdPartyIcon.js',
  LifebuoyIcon: './LifebuoyIcon.js',
  LiftTruckIcon: './LiftTruckIcon.js',
  LigatureIcon: './LigatureIcon.js',
  LightbulbOffIcon: './LightbulbOffIcon.js',
  LighthouseIcon: './LighthouseIcon.js',
  LimitOrderIcon: './LimitOrderIcon.js',
  LimitationIcon: './LimitationIcon.js',
  LineIcon: './LineIcon.js',
  LinerIcon: './LinerIcon.js',
  Link01Icon: './Link01Icon.js',
  Link02Icon: './Link02Icon.js',
  Link03Icon: './Link03Icon.js',
  Link04Icon: './Link04Icon.js',
  Link05Icon: './Link05Icon.js',
  Link06Icon: './Link06Icon.js',
  LinkBackwardIcon: './LinkBackwardIcon.js',
  LinkCircle02Icon: './LinkCircle02Icon.js',
  LinkCircleIcon: './LinkCircleIcon.js',
  LinkForwardIcon: './LinkForwardIcon.js',
  LinkSquare01Icon: './LinkSquare01Icon.js',
  LinkSquare02Icon: './LinkSquare02Icon.js',
  Linkedin01Icon: './Linkedin01Icon.js',
  Linkedin02Icon: './Linkedin02Icon.js',
  ListChevronsDownUpIcon: './ListChevronsDownUpIcon.js',
  ListFilterPlusIcon: './ListFilterPlusIcon.js',
  ListIndentDecreaseIcon: './ListIndentDecreaseIcon.js',
  ListIndentIncreaseIcon: './ListIndentIncreaseIcon.js',
  ListMusicIcon: './ListMusicIcon.js',
  ListPlusIcon: './ListPlusIcon.js',
  ListRestartIcon: './ListRestartIcon.js',
  ListSettingIcon: './ListSettingIcon.js',
  ListStartIcon: './ListStartIcon.js',
  ListTreeIcon: './ListTreeIcon.js',
  ListVideoIcon: './ListVideoIcon.js',
  ListViewIcon: './ListViewIcon.js',
  ListXIcon: './ListXIcon.js',
  LitecoinIcon: './LitecoinIcon.js',
  LiveStreaming01Icon: './LiveStreaming01Icon.js',
  LiveStreaming02Icon: './LiveStreaming02Icon.js',
  LiveStreaming03Icon: './LiveStreaming03Icon.js',
  LiverIcon: './LiverIcon.js',
  LoaderPinwheelIcon: './LoaderPinwheelIcon.js',
  Loading01Icon: './Loading01Icon.js',
  Loading02Icon: './Loading02Icon.js',
  Loading03Icon: './Loading03Icon.js',
  Loading04Icon: './Loading04Icon.js',
  Location01Icon: './Location01Icon.js',
  Location02Icon: './Location02Icon.js',
  Location03Icon: './Location03Icon.js',
  Location04Icon: './Location04Icon.js',
  Location05Icon: './Location05Icon.js',
  Location06Icon: './Location06Icon.js',
  Location07Icon: './Location07Icon.js',
  Location08Icon: './Location08Icon.js',
  Location09Icon: './Location09Icon.js',
  Location10Icon: './Location10Icon.js',
  LocationAdd01Icon: './LocationAdd01Icon.js',
  LocationAdd02Icon: './LocationAdd02Icon.js',
  LocationCheck01Icon: './LocationCheck01Icon.js',
  LocationCheck02Icon: './LocationCheck02Icon.js',
  LocationFavourite01Icon: './LocationFavourite01Icon.js',
  LocationFavourite02Icon: './LocationFavourite02Icon.js',
  LocationOffline01Icon: './LocationOffline01Icon.js',
  LocationOffline02Icon: './LocationOffline02Icon.js',
  LocationOffline03Icon: './LocationOffline03Icon.js',
  LocationOffline04Icon: './LocationOffline04Icon.js',
  LocationRemove01Icon: './LocationRemove01Icon.js',
  LocationRemove02Icon: './LocationRemove02Icon.js',
  LocationShare01Icon: './LocationShare01Icon.js',
  LocationShare02Icon: './LocationShare02Icon.js',
  LocationStar01Icon: './LocationStar01Icon.js',
  LocationStar02Icon: './LocationStar02Icon.js',
  LocationUpdate01Icon: './LocationUpdate01Icon.js',
  LocationUpdate02Icon: './LocationUpdate02Icon.js',
  LocationUser01Icon: './LocationUser01Icon.js',
  LocationUser02Icon: './LocationUser02Icon.js',
  LocationUser03Icon: './LocationUser03Icon.js',
  LocationUser04Icon: './LocationUser04Icon.js',
  LockComputerIcon: './LockComputerIcon.js',
  LockKeyIcon: './LockKeyIcon.js',
  LockPasswordIcon: './LockPasswordIcon.js',
  LockIcon: './LockIcon.js',
  LockSync01Icon: './LockSync01Icon.js',
  LockSync02Icon: './LockSync02Icon.js',
  LockedIcon: './LockedIcon.js',
  Locker01Icon: './Locker01Icon.js',
  LockerIcon: './LockerIcon.js',
  Login01Icon: './Login01Icon.js',
  Login02Icon: './Login02Icon.js',
  Login03Icon: './Login03Icon.js',
  LoginCircle01Icon: './LoginCircle01Icon.js',
  LoginCircle02Icon: './LoginCircle02Icon.js',
  LoginMethodIcon: './LoginMethodIcon.js',
  LoginSquare01Icon: './LoginSquare01Icon.js',
  LoginSquare02Icon: './LoginSquare02Icon.js',
  Logout01Icon: './Logout01Icon.js',
  Logout02Icon: './Logout02Icon.js',
  Logout03Icon: './Logout03Icon.js',
  Logout04Icon: './Logout04Icon.js',
  Logout05Icon: './Logout05Icon.js',
  LogoutCircle01Icon: './LogoutCircle01Icon.js',
  LogoutCircle02Icon: './LogoutCircle02Icon.js',
  LogoutSquare01Icon: './LogoutSquare01Icon.js',
  LogoutSquare02Icon: './LogoutSquare02Icon.js',
  LollipopIcon: './LollipopIcon.js',
  LongSleeveShirtIcon: './LongSleeveShirtIcon.js',
  LongitudeIcon: './LongitudeIcon.js',
  LookBottomIcon: './LookBottomIcon.js',
  LookLeftIcon: './LookLeftIcon.js',
  LookRightIcon: './LookRightIcon.js',
  LookTopIcon: './LookTopIcon.js',
  LoomIcon: './LoomIcon.js',
  LottiefilesIcon: './LottiefilesIcon.js',
  LoveKoreanFingerIcon: './LoveKoreanFingerIcon.js',
  LowSignalIcon: './LowSignalIcon.js',
  LoyaltyCardIcon: './LoyaltyCardIcon.js',
  Luggage01Icon: './Luggage01Icon.js',
  Luggage02Icon: './Luggage02Icon.js',
  LungsIcon: './LungsIcon.js',
  MachineRobotIcon: './MachineRobotIcon.js',
  MagicWand01Icon: './MagicWand01Icon.js',
  MagicWand02Icon: './MagicWand02Icon.js',
  MagicWand03Icon: './MagicWand03Icon.js',
  MagicWand04Icon: './MagicWand04Icon.js',
  MagicWand05Icon: './MagicWand05Icon.js',
  Magnet01Icon: './Magnet01Icon.js',
  Magnet02Icon: './Magnet02Icon.js',
  MagnetIcon: './MagnetIcon.js',
  Mail01Icon: './Mail01Icon.js',
  Mail02Icon: './Mail02Icon.js',
  MailAccount01Icon: './MailAccount01Icon.js',
  MailAccount02Icon: './MailAccount02Icon.js',
  MailAdd01Icon: './MailAdd01Icon.js',
  MailAdd02Icon: './MailAdd02Icon.js',
  MailAtSign01Icon: './MailAtSign01Icon.js',
  MailAtSign02Icon: './MailAtSign02Icon.js',
  MailBlock01Icon: './MailBlock01Icon.js',
  MailBlock02Icon: './MailBlock02Icon.js',
  MailDownload01Icon: './MailDownload01Icon.js',
  MailDownload02Icon: './MailDownload02Icon.js',
  MailEdit01Icon: './MailEdit01Icon.js',
  MailEdit02Icon: './MailEdit02Icon.js',
  MailLock01Icon: './MailLock01Icon.js',
  MailLock02Icon: './MailLock02Icon.js',
  MailLove01Icon: './MailLove01Icon.js',
  MailLove02Icon: './MailLove02Icon.js',
  MailMinus01Icon: './MailMinus01Icon.js',
  MailMinus02Icon: './MailMinus02Icon.js',
  MailOpen01Icon: './MailOpen01Icon.js',
  MailOpen02Icon: './MailOpen02Icon.js',
  MailOpenLoveIcon: './MailOpenLoveIcon.js',
  MailOpenIcon: './MailOpenIcon.js',
  MailQuestionMarkIcon: './MailQuestionMarkIcon.js',
  MailReceive01Icon: './MailReceive01Icon.js',
  MailReceive02Icon: './MailReceive02Icon.js',
  MailRemove01Icon: './MailRemove01Icon.js',
  MailRemove02Icon: './MailRemove02Icon.js',
  MailReply01Icon: './MailReply01Icon.js',
  MailReply02Icon: './MailReply02Icon.js',
  MailReplyAll01Icon: './MailReplyAll01Icon.js',
  MailReplyAll02Icon: './MailReplyAll02Icon.js',
  MailSearch01Icon: './MailSearch01Icon.js',
  MailSearch02Icon: './MailSearch02Icon.js',
  MailSecure01Icon: './MailSecure01Icon.js',
  MailSecure02Icon: './MailSecure02Icon.js',
  MailSend01Icon: './MailSend01Icon.js',
  MailSend02Icon: './MailSend02Icon.js',
  MailSetting01Icon: './MailSetting01Icon.js',
  MailSetting02Icon: './MailSetting02Icon.js',
  MailUnlock01Icon: './MailUnlock01Icon.js',
  MailUnlock02Icon: './MailUnlock02Icon.js',
  MailUpload01Icon: './MailUpload01Icon.js',
  MailUpload02Icon: './MailUpload02Icon.js',
  MailValidation01Icon: './MailValidation01Icon.js',
  MailValidation02Icon: './MailValidation02Icon.js',
  MailVoice01Icon: './MailVoice01Icon.js',
  MailVoice02Icon: './MailVoice02Icon.js',
  MailWarningIcon: './MailWarningIcon.js',
  Mailbox01Icon: './Mailbox01Icon.js',
  MailboxIcon: './MailboxIcon.js',
  MailsIcon: './MailsIcon.js',
  Male02Icon: './Male02Icon.js',
  MaleSymbolIcon: './MaleSymbolIcon.js',
  ManIcon: './ManIcon.js',
  ManWomanIcon: './ManWomanIcon.js',
  ManagerIcon: './ManagerIcon.js',
  MapPinCheckIcon: './MapPinCheckIcon.js',
  MapPinHouseIcon: './MapPinHouseIcon.js',
  MapPinMinusIcon: './MapPinMinusIcon.js',
  MapPinPenIcon: './MapPinPenIcon.js',
  MapPinPlusIcon: './MapPinPlusIcon.js',
  MapPinIcon: './MapPinIcon.js',
  MapPinXInsideIcon: './MapPinXInsideIcon.js',
  MapPinXIcon: './MapPinXIcon.js',
  MapPinpoint01Icon: './MapPinpoint01Icon.js',
  MapPinpoint02Icon: './MapPinpoint02Icon.js',
  MapingIcon: './MapingIcon.js',
  MapsCircle01Icon: './MapsCircle01Icon.js',
  MapsCircle02Icon: './MapsCircle02Icon.js',
  MapsEditingIcon: './MapsEditingIcon.js',
  MapsGlobal01Icon: './MapsGlobal01Icon.js',
  MapsGlobal02Icon: './MapsGlobal02Icon.js',
  MapsLocation01Icon: './MapsLocation01Icon.js',
  MapsLocation02Icon: './MapsLocation02Icon.js',
  MapsOffIcon: './MapsOffIcon.js',
  MapsRefreshIcon: './MapsRefreshIcon.js',
  MapsSearchIcon: './MapsSearchIcon.js',
  MapsSquare01Icon: './MapsSquare01Icon.js',
  MapsSquare02Icon: './MapsSquare02Icon.js',
  MapsIcon: './MapsIcon.js',
  MarketAnalysisIcon: './MarketAnalysisIcon.js',
  MarketOrderIcon: './MarketOrderIcon.js',
  MarketingIcon: './MarketingIcon.js',
  MaskLoveIcon: './MaskLoveIcon.js',
  MaskIcon: './MaskIcon.js',
  MaskTheater01Icon: './MaskTheater01Icon.js',
  MaskTheater02Icon: './MaskTheater02Icon.js',
  MasterCardIcon: './MasterCardIcon.js',
  MastodonIcon: './MastodonIcon.js',
  MatchesIcon: './MatchesIcon.js',
  MaterialAndTextureIcon: './MaterialAndTextureIcon.js',
  MathIcon: './MathIcon.js',
  MatrixIcon: './MatrixIcon.js',
  Maximize01Icon: './Maximize01Icon.js',
  Maximize02Icon: './Maximize02Icon.js',
  Maximize03Icon: './Maximize03Icon.js',
  Maximize04Icon: './Maximize04Icon.js',
  MaximizeScreenIcon: './MaximizeScreenIcon.js',
  MayanPyramidIcon: './MayanPyramidIcon.js',
  MazeIcon: './MazeIcon.js',
  McpServerIcon: './McpServerIcon.js',
  MealScanIcon: './MealScanIcon.js',
  Medal01Icon: './Medal01Icon.js',
  Medal02Icon: './Medal02Icon.js',
  Medal03Icon: './Medal03Icon.js',
  Medal04Icon: './Medal04Icon.js',
  Medal05Icon: './Medal05Icon.js',
  Medal06Icon: './Medal06Icon.js',
  Medal07Icon: './Medal07Icon.js',
  MedalFirstPlaceIcon: './MedalFirstPlaceIcon.js',
  MedalSecondPlaceIcon: './MedalSecondPlaceIcon.js',
  MedalThirdPlaceIcon: './MedalThirdPlaceIcon.js',
  MedicalFileIcon: './MedicalFileIcon.js',
  MedicalMaskIcon: './MedicalMaskIcon.js',
  Medicine01Icon: './Medicine01Icon.js',
  Medicine02Icon: './Medicine02Icon.js',
  MedicineBottle01Icon: './MedicineBottle01Icon.js',
  MedicineBottle02Icon: './MedicineBottle02Icon.js',
  MedicineSyrupIcon: './MedicineSyrupIcon.js',
  MediumSignalIcon: './MediumSignalIcon.js',
  MediumSquareIcon: './MediumSquareIcon.js',
  MediumIcon: './MediumIcon.js',
  MeetingRoomIcon: './MeetingRoomIcon.js',
  Megaphone01Icon: './Megaphone01Icon.js',
  Megaphone02Icon: './Megaphone02Icon.js',
  Megaphone03Icon: './Megaphone03Icon.js',
  MehIcon: './MehIcon.js',
  MemoryStickIcon: './MemoryStickIcon.js',
  MentorIcon: './MentorIcon.js',
  MentoringIcon: './MentoringIcon.js',
  Menu01Icon: './Menu01Icon.js',
  Menu02Icon: './Menu02Icon.js',
  Menu03Icon: './Menu03Icon.js',
  Menu04Icon: './Menu04Icon.js',
  Menu05Icon: './Menu05Icon.js',
  Menu06Icon: './Menu06Icon.js',
  Menu07Icon: './Menu07Icon.js',
  Menu08Icon: './Menu08Icon.js',
  Menu09Icon: './Menu09Icon.js',
  Menu10Icon: './Menu10Icon.js',
  Menu11Icon: './Menu11Icon.js',
  MenuCircleIcon: './MenuCircleIcon.js',
  MenuCollapseIcon: './MenuCollapseIcon.js',
  MenuRestaurantIcon: './MenuRestaurantIcon.js',
  MenuSquareIcon: './MenuSquareIcon.js',
  MenuTwoLineIcon: './MenuTwoLineIcon.js',
  Message01Icon: './Message01Icon.js',
  Message02Icon: './Message02Icon.js',
  MessageAdd01Icon: './MessageAdd01Icon.js',
  MessageAdd02Icon: './MessageAdd02Icon.js',
  MessageBlockedIcon: './MessageBlockedIcon.js',
  MessageCancel01Icon: './MessageCancel01Icon.js',
  MessageCancel02Icon: './MessageCancel02Icon.js',
  MessageCircleCodeIcon: './MessageCircleCodeIcon.js',
  MessageCircleReplyIcon: './MessageCircleReplyIcon.js',
  MessageDelay01Icon: './MessageDelay01Icon.js',
  MessageDelay02Icon: './MessageDelay02Icon.js',
  MessageDone01Icon: './MessageDone01Icon.js',
  MessageDone02Icon: './MessageDone02Icon.js',
  MessageDownload01Icon: './MessageDownload01Icon.js',
  MessageDownload02Icon: './MessageDownload02Icon.js',
  MessageEdit01Icon: './MessageEdit01Icon.js',
  MessageEdit02Icon: './MessageEdit02Icon.js',
  MessageFavourite01Icon: './MessageFavourite01Icon.js',
  MessageFavourite02Icon: './MessageFavourite02Icon.js',
  MessageIncoming01Icon: './MessageIncoming01Icon.js',
  MessageIncoming02Icon: './MessageIncoming02Icon.js',
  MessageLock01Icon: './MessageLock01Icon.js',
  MessageLock02Icon: './MessageLock02Icon.js',
  MessageMultiple01Icon: './MessageMultiple01Icon.js',
  MessageMultiple02Icon: './MessageMultiple02Icon.js',
  MessageNotification01Icon: './MessageNotification01Icon.js',
  MessageNotification02Icon: './MessageNotification02Icon.js',
  MessageOutgoing01Icon: './MessageOutgoing01Icon.js',
  MessageOutgoing02Icon: './MessageOutgoing02Icon.js',
  MessagePreview01Icon: './MessagePreview01Icon.js',
  MessagePreview02Icon: './MessagePreview02Icon.js',
  MessageProgrammingIcon: './MessageProgrammingIcon.js',
  MessageQuestionIcon: './MessageQuestionIcon.js',
  MessageSearch01Icon: './MessageSearch01Icon.js',
  MessageSearch02Icon: './MessageSearch02Icon.js',
  MessageSecure01Icon: './MessageSecure01Icon.js',
  MessageSecure02Icon: './MessageSecure02Icon.js',
  MessageSquareCodeIcon: './MessageSquareCodeIcon.js',
  MessageSquareDashedIcon: './MessageSquareDashedIcon.js',
  MessageSquareDiffIcon: './MessageSquareDiffIcon.js',
  MessageSquareShareIcon: './MessageSquareShareIcon.js',
  MessageTranslateIcon: './MessageTranslateIcon.js',
  MessageUnlock01Icon: './MessageUnlock01Icon.js',
  MessageUnlock02Icon: './MessageUnlock02Icon.js',
  MessageUpload01Icon: './MessageUpload01Icon.js',
  MessageUpload02Icon: './MessageUpload02Icon.js',
  MessageUser01Icon: './MessageUser01Icon.js',
  MessageUser02Icon: './MessageUser02Icon.js',
  MessengerIcon: './MessengerIcon.js',
  MetaIcon: './MetaIcon.js',
  MetroIcon: './MetroIcon.js',
  Mic01Icon: './Mic01Icon.js',
  Mic02Icon: './Mic02Icon.js',
  MicOff01Icon: './MicOff01Icon.js',
  MicOff02Icon: './MicOff02Icon.js',
  MicrochipIcon: './MicrochipIcon.js',
  MicroscopeIcon: './MicroscopeIcon.js',
  MicrosoftAdminIcon: './MicrosoftAdminIcon.js',
  MicrosoftIcon: './MicrosoftIcon.js',
  MicrowaveIcon: './MicrowaveIcon.js',
  MilkBottleIcon: './MilkBottleIcon.js',
  MilkCartonIcon: './MilkCartonIcon.js',
  MilkCoconutIcon: './MilkCoconutIcon.js',
  MilkOatIcon: './MilkOatIcon.js',
  Minimize01Icon: './Minimize01Icon.js',
  Minimize02Icon: './Minimize02Icon.js',
  Minimize03Icon: './Minimize03Icon.js',
  Minimize04Icon: './Minimize04Icon.js',
  MinimizeScreenIcon: './MinimizeScreenIcon.js',
  Mining01Icon: './Mining01Icon.js',
  Mining02Icon: './Mining02Icon.js',
  Mining03Icon: './Mining03Icon.js',
  MinusPlus01Icon: './MinusPlus01Icon.js',
  MinusPlus02Icon: './MinusPlus02Icon.js',
  MinusPlusCircle01Icon: './MinusPlusCircle01Icon.js',
  MinusPlusCircle02Icon: './MinusPlusCircle02Icon.js',
  MinusPlusSquare01Icon: './MinusPlusSquare01Icon.js',
  MinusPlusSquare02Icon: './MinusPlusSquare02Icon.js',
  MinusSignCircleIcon: './MinusSignCircleIcon.js',
  MinusSignSquareIcon: './MinusSignSquareIcon.js',
  MinusSignIcon: './MinusSignIcon.js',
  MirrorIcon: './MirrorIcon.js',
  MirroringScreenIcon: './MirroringScreenIcon.js',
  MistralIcon: './MistralIcon.js',
  MixerIcon: './MixerIcon.js',
  MobileNavigator01Icon: './MobileNavigator01Icon.js',
  MobileNavigator02Icon: './MobileNavigator02Icon.js',
  MobileProgramming01Icon: './MobileProgramming01Icon.js',
  MobileProgramming02Icon: './MobileProgramming02Icon.js',
  MobileProtectionIcon: './MobileProtectionIcon.js',
  MobileSecurityIcon: './MobileSecurityIcon.js',
  MochiIcon: './MochiIcon.js',
  ModernTvFourKIcon: './ModernTvFourKIcon.js',
  ModernTvIssueIcon: './ModernTvIssueIcon.js',
  ModernTvIcon: './ModernTvIcon.js',
  MoleculesIcon: './MoleculesIcon.js',
  MollieIcon: './MollieIcon.js',
  MonasIcon: './MonasIcon.js',
  Money01Icon: './Money01Icon.js',
  Money02Icon: './Money02Icon.js',
  Money03Icon: './Money03Icon.js',
  Money04Icon: './Money04Icon.js',
  MoneyAdd01Icon: './MoneyAdd01Icon.js',
  MoneyAdd02Icon: './MoneyAdd02Icon.js',
  MoneyBag01Icon: './MoneyBag01Icon.js',
  MoneyBag02Icon: './MoneyBag02Icon.js',
  MoneyExchange01Icon: './MoneyExchange01Icon.js',
  MoneyExchange02Icon: './MoneyExchange02Icon.js',
  MoneyExchange03Icon: './MoneyExchange03Icon.js',
  MoneyLockIcon: './MoneyLockIcon.js',
  MoneyNotFound01Icon: './MoneyNotFound01Icon.js',
  MoneyNotFound02Icon: './MoneyNotFound02Icon.js',
  MoneyNotFound03Icon: './MoneyNotFound03Icon.js',
  MoneyNotFound04Icon: './MoneyNotFound04Icon.js',
  MoneyReceive01Icon: './MoneyReceive01Icon.js',
  MoneyReceive02Icon: './MoneyReceive02Icon.js',
  MoneyReceiveCircleIcon: './MoneyReceiveCircleIcon.js',
  MoneyReceiveFlow01Icon: './MoneyReceiveFlow01Icon.js',
  MoneyReceiveFlow02Icon: './MoneyReceiveFlow02Icon.js',
  MoneyReceiveSquareIcon: './MoneyReceiveSquareIcon.js',
  MoneyRemove01Icon: './MoneyRemove01Icon.js',
  MoneyRemove02Icon: './MoneyRemove02Icon.js',
  MoneySafeIcon: './MoneySafeIcon.js',
  MoneySavingJarIcon: './MoneySavingJarIcon.js',
  MoneySecurityIcon: './MoneySecurityIcon.js',
  MoneySend01Icon: './MoneySend01Icon.js',
  MoneySend02Icon: './MoneySend02Icon.js',
  MoneySendCircleIcon: './MoneySendCircleIcon.js',
  MoneySendFlow01Icon: './MoneySendFlow01Icon.js',
  MoneySendFlow02Icon: './MoneySendFlow02Icon.js',
  MoneySendSquareIcon: './MoneySendSquareIcon.js',
  MonitorDotIcon: './MonitorDotIcon.js',
  MonitorPauseIcon: './MonitorPauseIcon.js',
  MonitorSpeakerIcon: './MonitorSpeakerIcon.js',
  MonitorStopIcon: './MonitorStopIcon.js',
  Monocle01Icon: './Monocle01Icon.js',
  MonocleIcon: './MonocleIcon.js',
  MonsterIcon: './MonsterIcon.js',
  Moon01Icon: './Moon01Icon.js',
  Moon02Icon: './Moon02Icon.js',
  MoonAngledRainZapIcon: './MoonAngledRainZapIcon.js',
  MoonCloudAngledRainIcon: './MoonCloudAngledRainIcon.js',
  MoonCloudAngledZapIcon: './MoonCloudAngledZapIcon.js',
  MoonCloudBigRainIcon: './MoonCloudBigRainIcon.js',
  MoonCloudFastWindIcon: './MoonCloudFastWindIcon.js',
  MoonCloudHailstoneIcon: './MoonCloudHailstoneIcon.js',
  MoonCloudLittleRainIcon: './MoonCloudLittleRainIcon.js',
  MoonCloudLittleSnowIcon: './MoonCloudLittleSnowIcon.js',
  MoonCloudMidRainIcon: './MoonCloudMidRainIcon.js',
  MoonCloudMidSnowIcon: './MoonCloudMidSnowIcon.js',
  MoonCloudSlowWindIcon: './MoonCloudSlowWindIcon.js',
  MoonCloudSnowIcon: './MoonCloudSnowIcon.js',
  MoonCloudIcon: './MoonCloudIcon.js',
  MoonEclipseIcon: './MoonEclipseIcon.js',
  MoonFastWindIcon: './MoonFastWindIcon.js',
  MoonLandingIcon: './MoonLandingIcon.js',
  MoonSlowWindIcon: './MoonSlowWindIcon.js',
  MoonIcon: './MoonIcon.js',
  MoonsetIcon: './MoonsetIcon.js',
  More01Icon: './More01Icon.js',
  More02Icon: './More02Icon.js',
  More03Icon: './More03Icon.js',
  MoreHorizontalCircle01Icon: './MoreHorizontalCircle01Icon.js',
  MoreHorizontalCircle02Icon: './MoreHorizontalCircle02Icon.js',
  MoreHorizontalSquare01Icon: './MoreHorizontalSquare01Icon.js',
  MoreHorizontalSquare02Icon: './MoreHorizontalSquare02Icon.js',
  MoreHorizontalIcon: './MoreHorizontalIcon.js',
  MoreOrLessCircleIcon: './MoreOrLessCircleIcon.js',
  MoreOrLessSquareIcon: './MoreOrLessSquareIcon.js',
  MoreOrLessIcon: './MoreOrLessIcon.js',
  MoreIcon: './MoreIcon.js',
  MoreVerticalCircle01Icon: './MoreVerticalCircle01Icon.js',
  MoreVerticalCircle02Icon: './MoreVerticalCircle02Icon.js',
  MoreVerticalSquare01Icon: './MoreVerticalSquare01Icon.js',
  MoreVerticalSquare02Icon: './MoreVerticalSquare02Icon.js',
  MoreVerticalIcon: './MoreVerticalIcon.js',
  MortarIcon: './MortarIcon.js',
  Mortarboard01Icon: './Mortarboard01Icon.js',
  Mortarboard02Icon: './Mortarboard02Icon.js',
  Mosque01Icon: './Mosque01Icon.js',
  Mosque02Icon: './Mosque02Icon.js',
  Mosque03Icon: './Mosque03Icon.js',
  Mosque04Icon: './Mosque04Icon.js',
  Mosque05Icon: './Mosque05Icon.js',
  MosqueLocationIcon: './MosqueLocationIcon.js',
  Motion01Icon: './Motion01Icon.js',
  Motion02Icon: './Motion02Icon.js',
  Motorbike01Icon: './Motorbike01Icon.js',
  Motorbike02Icon: './Motorbike02Icon.js',
  MountainIcon: './MountainIcon.js',
  Mouse01Icon: './Mouse01Icon.js',
  Mouse02Icon: './Mouse02Icon.js',
  Mouse03Icon: './Mouse03Icon.js',
  Mouse04Icon: './Mouse04Icon.js',
  Mouse05Icon: './Mouse05Icon.js',
  Mouse06Icon: './Mouse06Icon.js',
  Mouse07Icon: './Mouse07Icon.js',
  Mouse08Icon: './Mouse08Icon.js',
  Mouse09Icon: './Mouse09Icon.js',
  Mouse10Icon: './Mouse10Icon.js',
  Mouse11Icon: './Mouse11Icon.js',
  Mouse12Icon: './Mouse12Icon.js',
  Mouse13Icon: './Mouse13Icon.js',
  Mouse14Icon: './Mouse14Icon.js',
  Mouse15Icon: './Mouse15Icon.js',
  Mouse16Icon: './Mouse16Icon.js',
  Mouse17Icon: './Mouse17Icon.js',
  Mouse18Icon: './Mouse18Icon.js',
  Mouse19Icon: './Mouse19Icon.js',
  Mouse20Icon: './Mouse20Icon.js',
  Mouse21Icon: './Mouse21Icon.js',
  Mouse22Icon: './Mouse22Icon.js',
  Mouse23Icon: './Mouse23Icon.js',
  MouseLeftClick01Icon: './MouseLeftClick01Icon.js',
  MouseLeftClick02Icon: './MouseLeftClick02Icon.js',
  MouseLeftClick03Icon: './MouseLeftClick03Icon.js',
  MouseLeftClick04Icon: './MouseLeftClick04Icon.js',
  MouseLeftClick05Icon: './MouseLeftClick05Icon.js',
  MouseLeftClick06Icon: './MouseLeftClick06Icon.js',
  MousePointerOffIcon: './MousePointerOffIcon.js',
  MouseRightClick01Icon: './MouseRightClick01Icon.js',
  MouseRightClick02Icon: './MouseRightClick02Icon.js',
  MouseRightClick03Icon: './MouseRightClick03Icon.js',
  MouseRightClick04Icon: './MouseRightClick04Icon.js',
  MouseRightClick05Icon: './MouseRightClick05Icon.js',
  MouseRightClick06Icon: './MouseRightClick06Icon.js',
  MouseScroll01Icon: './MouseScroll01Icon.js',
  MouseScroll02Icon: './MouseScroll02Icon.js',
  Move01Icon: './Move01Icon.js',
  Move02Icon: './Move02Icon.js',
  MoveBottomIcon: './MoveBottomIcon.js',
  MoveLeftIcon: './MoveLeftIcon.js',
  MoveRightIcon: './MoveRightIcon.js',
  MoveIcon: './MoveIcon.js',
  MoveToIcon: './MoveToIcon.js',
  MoveTopIcon: './MoveTopIcon.js',
  MpThree02Icon: './MpThree02Icon.js',
  MpFour01Icon: './MpFour01Icon.js',
  MpFour02Icon: './MpFour02Icon.js',
  Mp301Icon: './Mp301Icon.js',
  MuhammadIcon: './MuhammadIcon.js',
  MultiplicationSignCircleIcon: './MultiplicationSignCircleIcon.js',
  MultiplicationSignSquareIcon: './MultiplicationSignSquareIcon.js',
  MultiplicationSignIcon: './MultiplicationSignIcon.js',
  Mushroom01Icon: './Mushroom01Icon.js',
  MushroomIcon: './MushroomIcon.js',
  MusicThreeIcon: './MusicThreeIcon.js',
  MusicNote01Icon: './MusicNote01Icon.js',
  MusicNote02Icon: './MusicNote02Icon.js',
  MusicNote03Icon: './MusicNote03Icon.js',
  MusicNote04Icon: './MusicNote04Icon.js',
  MusicNoteSquare01Icon: './MusicNoteSquare01Icon.js',
  MusicNoteSquare02Icon: './MusicNoteSquare02Icon.js',
  MuslimIcon: './MuslimIcon.js',
  MuteIcon: './MuteIcon.js',
  MymindIcon: './MymindIcon.js',
  NThRootCircleIcon: './NThRootCircleIcon.js',
  NThRootSquareIcon: './NThRootSquareIcon.js',
  NThRootIcon: './NThRootIcon.js',
  NanoTechnologyIcon: './NanoTechnologyIcon.js',
  Napkins01Icon: './Napkins01Icon.js',
  Napkins02Icon: './Napkins02Icon.js',
  NaturalFoodIcon: './NaturalFoodIcon.js',
  Navigation01Icon: './Navigation01Icon.js',
  Navigation02Icon: './Navigation02Icon.js',
  Navigation03Icon: './Navigation03Icon.js',
  Navigation04Icon: './Navigation04Icon.js',
  Navigation05Icon: './Navigation05Icon.js',
  Navigation06Icon: './Navigation06Icon.js',
  NavigationTwoOffIcon: './NavigationTwoOffIcon.js',
  NavigationTwoIcon: './NavigationTwoIcon.js',
  NavigationOffIcon: './NavigationOffIcon.js',
  NavigationIcon: './NavigationIcon.js',
  Navigator01Icon: './Navigator01Icon.js',
  Navigator02Icon: './Navigator02Icon.js',
  NecklaceIcon: './NecklaceIcon.js',
  NerdIcon: './NerdIcon.js',
  NeuralNetworkIcon: './NeuralNetworkIcon.js',
  NeutralIcon: './NeutralIcon.js',
  NewJobIcon: './NewJobIcon.js',
  NewOfficeIcon: './NewOfficeIcon.js',
  NewReleasesIcon: './NewReleasesIcon.js',
  NewTwitterEllipseIcon: './NewTwitterEllipseIcon.js',
  NewTwitterRectangleIcon: './NewTwitterRectangleIcon.js',
  NewTwitterIcon: './NewTwitterIcon.js',
  News01Icon: './News01Icon.js',
  NewsIcon: './NewsIcon.js',
  NextIcon: './NextIcon.js',
  NextWeekIcon: './NextWeekIcon.js',
  NfcIcon: './NfcIcon.js',
  NikeIcon: './NikeIcon.js',
  NineCircleIcon: './NineCircleIcon.js',
  NineSquareIcon: './NineSquareIcon.js',
  NintendoIcon: './NintendoIcon.js',
  NintendoSwitchIcon: './NintendoSwitchIcon.js',
  NiqabIcon: './NiqabIcon.js',
  NoInternetIcon: './NoInternetIcon.js',
  NoMeetingRoomIcon: './NoMeetingRoomIcon.js',
  NoSignalIcon: './NoSignalIcon.js',
  NodeAddIcon: './NodeAddIcon.js',
  NodeEditIcon: './NodeEditIcon.js',
  NodeMoveDownIcon: './NodeMoveDownIcon.js',
  NodeMoveUpIcon: './NodeMoveUpIcon.js',
  NodeRemoveIcon: './NodeRemoveIcon.js',
  NoodlesIcon: './NoodlesIcon.js',
  NoseIcon: './NoseIcon.js',
  NotEqualSignCircleIcon: './NotEqualSignCircleIcon.js',
  NotEqualSignSquareIcon: './NotEqualSignSquareIcon.js',
  NotEqualSignIcon: './NotEqualSignIcon.js',
  Note01Icon: './Note01Icon.js',
  Note02Icon: './Note02Icon.js',
  Note03Icon: './Note03Icon.js',
  Note04Icon: './Note04Icon.js',
  Note05Icon: './Note05Icon.js',
  NoteAddIcon: './NoteAddIcon.js',
  NoteDoneIcon: './NoteDoneIcon.js',
  NoteEditIcon: './NoteEditIcon.js',
  NoteRemoveIcon: './NoteRemoveIcon.js',
  NoteIcon: './NoteIcon.js',
  Notebook01Icon: './Notebook01Icon.js',
  Notebook02Icon: './Notebook02Icon.js',
  NotebookIcon: './NotebookIcon.js',
  NotepadTextDashedIcon: './NotepadTextDashedIcon.js',
  Notification01Icon: './Notification01Icon.js',
  Notification02Icon: './Notification02Icon.js',
  Notification03Icon: './Notification03Icon.js',
  NotificationBlock01Icon: './NotificationBlock01Icon.js',
  NotificationBlock02Icon: './NotificationBlock02Icon.js',
  NotificationBlock03Icon: './NotificationBlock03Icon.js',
  NotificationBubbleIcon: './NotificationBubbleIcon.js',
  NotificationCircleIcon: './NotificationCircleIcon.js',
  NotificationOff01Icon: './NotificationOff01Icon.js',
  NotificationOff02Icon: './NotificationOff02Icon.js',
  NotificationOff03Icon: './NotificationOff03Icon.js',
  NotificationSnooze01Icon: './NotificationSnooze01Icon.js',
  NotificationSnooze02Icon: './NotificationSnooze02Icon.js',
  NotificationSnooze03Icon: './NotificationSnooze03Icon.js',
  NotificationSquareIcon: './NotificationSquareIcon.js',
  Notion01Icon: './Notion01Icon.js',
  Notion02Icon: './Notion02Icon.js',
  NpmIcon: './NpmIcon.js',
  NuclearPowerIcon: './NuclearPowerIcon.js',
  NutOffIcon: './NutOffIcon.js',
  NutIcon: './NutIcon.js',
  ObtuseIcon: './ObtuseIcon.js',
  OctagonIcon: './OctagonIcon.js',
  OctagonXIcon: './OctagonXIcon.js',
  OctopusIcon: './OctopusIcon.js',
  Office365Icon: './Office365Icon.js',
  OfficeChairIcon: './OfficeChairIcon.js',
  OfficeIcon: './OfficeIcon.js',
  OilBarrelIcon: './OilBarrelIcon.js',
  OkFingerIcon: './OkFingerIcon.js',
  OlympicTorchIcon: './OlympicTorchIcon.js',
  OneCircleIcon: './OneCircleIcon.js',
  OneSquareIcon: './OneSquareIcon.js',
  OnlineLearning01Icon: './OnlineLearning01Icon.js',
  OnlineLearning02Icon: './OnlineLearning02Icon.js',
  OnlineLearning03Icon: './OnlineLearning03Icon.js',
  OnlineLearning04Icon: './OnlineLearning04Icon.js',
  OpenCaptionIcon: './OpenCaptionIcon.js',
  OpenSourceIcon: './OpenSourceIcon.js',
  OptionIcon: './OptionIcon.js',
  OptionsCallIcon: './OptionsCallIcon.js',
  OrangeIcon: './OrangeIcon.js',
  Orbit01Icon: './Orbit01Icon.js',
  Orbit02Icon: './Orbit02Icon.js',
  OrganicFoodIcon: './OrganicFoodIcon.js',
  OrientationImageLandscapeToPotraitIcon: './OrientationImageLandscapeToPotraitIcon.js',
  OrientationImagePotraitToLandscapeIcon: './OrientationImagePotraitToLandscapeIcon.js',
  OrientationLandscapeToPotraitIcon: './OrientationLandscapeToPotraitIcon.js',
  OrientationPotraitToLandscapeIcon: './OrientationPotraitToLandscapeIcon.js',
  OrigamiIcon: './OrigamiIcon.js',
  OrthogonalEdgeIcon: './OrthogonalEdgeIcon.js',
  OvalIcon: './OvalIcon.js',
  OvenIcon: './OvenIcon.js',
  Package01Icon: './Package01Icon.js',
  Package02Icon: './Package02Icon.js',
  Package03Icon: './Package03Icon.js',
  PackageAdd01Icon: './PackageAdd01Icon.js',
  PackageAddIcon: './PackageAddIcon.js',
  PackageDelivered01Icon: './PackageDelivered01Icon.js',
  PackageDeliveredIcon: './PackageDeliveredIcon.js',
  PackageDimensions01Icon: './PackageDimensions01Icon.js',
  PackageDimensions02Icon: './PackageDimensions02Icon.js',
  PackageMoving01Icon: './PackageMoving01Icon.js',
  PackageMovingIcon: './PackageMovingIcon.js',
  PackageOpenIcon: './PackageOpenIcon.js',
  PackageOutOfStockIcon: './PackageOutOfStockIcon.js',
  PackageProcess01Icon: './PackageProcess01Icon.js',
  PackageProcessIcon: './PackageProcessIcon.js',
  PackageReceive01Icon: './PackageReceive01Icon.js',
  PackageReceiveIcon: './PackageReceiveIcon.js',
  PackageRemove01Icon: './PackageRemove01Icon.js',
  PackageRemoveIcon: './PackageRemoveIcon.js',
  PackageSearch01Icon: './PackageSearch01Icon.js',
  PackageSearchIcon: './PackageSearchIcon.js',
  PackageSent01Icon: './PackageSent01Icon.js',
  PackageSentIcon: './PackageSentIcon.js',
  PackageIcon: './PackageIcon.js',
  PackagingIcon: './PackagingIcon.js',
  Pacman01Icon: './Pacman01Icon.js',
  Pacman02Icon: './Pacman02Icon.js',
  PaellaIcon: './PaellaIcon.js',
  PaintBoardIcon: './PaintBoardIcon.js',
  PaintBrush01Icon: './PaintBrush01Icon.js',
  PaintBrush02Icon: './PaintBrush02Icon.js',
  PaintBrush03Icon: './PaintBrush03Icon.js',
  PaintBrush04Icon: './PaintBrush04Icon.js',
  PaintBucketIcon: './PaintBucketIcon.js',
  Pan01Icon: './Pan01Icon.js',
  Pan02Icon: './Pan02Icon.js',
  Pan03Icon: './Pan03Icon.js',
  PanelBottomCloseIcon: './PanelBottomCloseIcon.js',
  PanelLeftCloseIcon: './PanelLeftCloseIcon.js',
  PanelLeftDashedIcon: './PanelLeftDashedIcon.js',
  PanelLeftOpenIcon: './PanelLeftOpenIcon.js',
  PanelLeftIcon: './PanelLeftIcon.js',
  PanelRightCloseIcon: './PanelRightCloseIcon.js',
  PanelRightDashedIcon: './PanelRightDashedIcon.js',
  PanelRightOpenIcon: './PanelRightOpenIcon.js',
  PanelRightIcon: './PanelRightIcon.js',
  PanelTopBottomDashedIcon: './PanelTopBottomDashedIcon.js',
  Parabola01Icon: './Parabola01Icon.js',
  Parabola02Icon: './Parabola02Icon.js',
  Parabola03Icon: './Parabola03Icon.js',
  ParaglidingIcon: './ParaglidingIcon.js',
  ParagraphBulletsPoint01Icon: './ParagraphBulletsPoint01Icon.js',
  ParagraphBulletsPoint02Icon: './ParagraphBulletsPoint02Icon.js',
  ParagraphSpacingIcon: './ParagraphSpacingIcon.js',
  ParagraphIcon: './ParagraphIcon.js',
  ParallelogramIcon: './ParallelogramIcon.js',
  ParkingAreaCircleIcon: './ParkingAreaCircleIcon.js',
  ParkingAreaSquareIcon: './ParkingAreaSquareIcon.js',
  ParkingMeterIcon: './ParkingMeterIcon.js',
  PartyIcon: './PartyIcon.js',
  Passport01Icon: './Passport01Icon.js',
  PassportExpiredIcon: './PassportExpiredIcon.js',
  PassportIcon: './PassportIcon.js',
  PassportValidIcon: './PassportValidIcon.js',
  PasswordValidationIcon: './PasswordValidationIcon.js',
  PathIcon: './PathIcon.js',
  PathfinderCropIcon: './PathfinderCropIcon.js',
  PathfinderDivideIcon: './PathfinderDivideIcon.js',
  PathfinderExcludeIcon: './PathfinderExcludeIcon.js',
  PathfinderIntersectIcon: './PathfinderIntersectIcon.js',
  PathfinderMergeIcon: './PathfinderMergeIcon.js',
  PathfinderMinusBackIcon: './PathfinderMinusBackIcon.js',
  PathfinderMinusFrontIcon: './PathfinderMinusFrontIcon.js',
  PathfinderOutlineIcon: './PathfinderOutlineIcon.js',
  PathfinderTrimIcon: './PathfinderTrimIcon.js',
  PathfinderUniteIcon: './PathfinderUniteIcon.js',
  PatientIcon: './PatientIcon.js',
  PatioIcon: './PatioIcon.js',
  PauseCircleIcon: './PauseCircleIcon.js',
  PauseIcon: './PauseIcon.js',
  PavilonIcon: './PavilonIcon.js',
  PayByCheckIcon: './PayByCheckIcon.js',
  Payment01Icon: './Payment01Icon.js',
  Payment02Icon: './Payment02Icon.js',
  PaymentSuccess01Icon: './PaymentSuccess01Icon.js',
  PaymentSuccess02Icon: './PaymentSuccess02Icon.js',
  PayoneerIcon: './PayoneerIcon.js',
  PaypalIcon: './PaypalIcon.js',
  PcCaseIcon: './PcCaseIcon.js',
  Pdf01Icon: './Pdf01Icon.js',
  Pdf02Icon: './Pdf02Icon.js',
  PeerToPeer01Icon: './PeerToPeer01Icon.js',
  PeerToPeer02Icon: './PeerToPeer02Icon.js',
  PeerToPeer03Icon: './PeerToPeer03Icon.js',
  Pen01Icon: './Pen01Icon.js',
  Pen02Icon: './Pen02Icon.js',
  PenConnectBluetoothIcon: './PenConnectBluetoothIcon.js',
  PenConnectUsbIcon: './PenConnectUsbIcon.js',
  PenConnectWifiIcon: './PenConnectWifiIcon.js',
  PenTool01Icon: './PenTool01Icon.js',
  PenTool02Icon: './PenTool02Icon.js',
  PenTool03Icon: './PenTool03Icon.js',
  PenToolAddIcon: './PenToolAddIcon.js',
  PenToolMinusIcon: './PenToolMinusIcon.js',
  PencilEdit01Icon: './PencilEdit01Icon.js',
  PencilEdit02Icon: './PencilEdit02Icon.js',
  PencilRulerIcon: './PencilRulerIcon.js',
  PencilIcon: './PencilIcon.js',
  PendulumIcon: './PendulumIcon.js',
  PensiveIcon: './PensiveIcon.js',
  Pentagon01Icon: './Pentagon01Icon.js',
  PentagonIcon: './PentagonIcon.js',
  PercentCircleIcon: './PercentCircleIcon.js',
  PercentSquareIcon: './PercentSquareIcon.js',
  PercentIcon: './PercentIcon.js',
  PerfumeIcon: './PerfumeIcon.js',
  PeriscopeIcon: './PeriscopeIcon.js',
  PermanentJobIcon: './PermanentJobIcon.js',
  PerplexityAiIcon: './PerplexityAiIcon.js',
  PerspectiveIcon: './PerspectiveIcon.js',
  PetrolPumpIcon: './PetrolPumpIcon.js',
  PexelsIcon: './PexelsIcon.js',
  PhilippinePesoIcon: './PhilippinePesoIcon.js',
  PhoneArrowDownIcon: './PhoneArrowDownIcon.js',
  PhoneArrowUpIcon: './PhoneArrowUpIcon.js',
  PhoneCheckIcon: './PhoneCheckIcon.js',
  PhoneDeveloperModeIcon: './PhoneDeveloperModeIcon.js',
  PhoneEraseIcon: './PhoneEraseIcon.js',
  PhoneLockIcon: './PhoneLockIcon.js',
  PhoneOff01Icon: './PhoneOff01Icon.js',
  PhoneOff02Icon: './PhoneOff02Icon.js',
  PhpIcon: './PhpIcon.js',
  PhysicsIcon: './PhysicsIcon.js',
  PiCircleIcon: './PiCircleIcon.js',
  PiSquareIcon: './PiSquareIcon.js',
  PiIcon: './PiIcon.js',
  PicasaIcon: './PicasaIcon.js',
  Pickup01Icon: './Pickup01Icon.js',
  Pickup02Icon: './Pickup02Icon.js',
  PictureInPictureExitIcon: './PictureInPictureExitIcon.js',
  PictureInPictureOnIcon: './PictureInPictureOnIcon.js',
  PieChart01Icon: './PieChart01Icon.js',
  PieChart02Icon: './PieChart02Icon.js',
  PieChart03Icon: './PieChart03Icon.js',
  PieChart04Icon: './PieChart04Icon.js',
  PieChart05Icon: './PieChart05Icon.js',
  PieChart06Icon: './PieChart06Icon.js',
  PieChart07Icon: './PieChart07Icon.js',
  PieChart08Icon: './PieChart08Icon.js',
  PieChart09Icon: './PieChart09Icon.js',
  PieChartSquareIcon: './PieChartSquareIcon.js',
  PieChartIcon: './PieChartIcon.js',
  PieIcon: './PieIcon.js',
  PiggyBankIcon: './PiggyBankIcon.js',
  PillBottleIcon: './PillBottleIcon.js',
  PillOffIcon: './PillOffIcon.js',
  PillIcon: './PillIcon.js',
  PillsTabletIcon: './PillsTabletIcon.js',
  Pin02Icon: './Pin02Icon.js',
  PinCodeIcon: './PinCodeIcon.js',
  PinLocation01Icon: './PinLocation01Icon.js',
  PinLocation02Icon: './PinLocation02Icon.js',
  PinLocation03Icon: './PinLocation03Icon.js',
  PinOffIcon: './PinOffIcon.js',
  PinIcon: './PinIcon.js',
  PineTreeIcon: './PineTreeIcon.js',
  PinterestIcon: './PinterestIcon.js',
  PipelineIcon: './PipelineIcon.js',
  PisaTowerIcon: './PisaTowerIcon.js',
  PivotIcon: './PivotIcon.js',
  Pizza01Icon: './Pizza01Icon.js',
  Pizza02Icon: './Pizza02Icon.js',
  Pizza03Icon: './Pizza03Icon.js',
  Pizza04Icon: './Pizza04Icon.js',
  PizzaCutterIcon: './PizzaCutterIcon.js',
  PlaneIcon: './PlaneIcon.js',
  Plant01Icon: './Plant01Icon.js',
  Plant02Icon: './Plant02Icon.js',
  Plant03Icon: './Plant03Icon.js',
  Plant04Icon: './Plant04Icon.js',
  PlateIcon: './PlateIcon.js',
  PlaxoIcon: './PlaxoIcon.js',
  PlayCircle02Icon: './PlayCircle02Icon.js',
  PlayCircleIcon: './PlayCircleIcon.js',
  PlayListAddIcon: './PlayListAddIcon.js',
  PlayListFavourite01Icon: './PlayListFavourite01Icon.js',
  PlayListFavourite02Icon: './PlayListFavourite02Icon.js',
  PlayListMinusIcon: './PlayListMinusIcon.js',
  PlayListRemoveIcon: './PlayListRemoveIcon.js',
  PlayListIcon: './PlayListIcon.js',
  PlaySquareIcon: './PlaySquareIcon.js',
  PlayStoreIcon: './PlayStoreIcon.js',
  PlayIcon: './PlayIcon.js',
  Playlist01Icon: './Playlist01Icon.js',
  Playlist02Icon: './Playlist02Icon.js',
  Playlist03Icon: './Playlist03Icon.js',
  PlazaIcon: './PlazaIcon.js',
  Plug01Icon: './Plug01Icon.js',
  Plug02Icon: './Plug02Icon.js',
  PlugSocketIcon: './PlugSocketIcon.js',
  PlusMinus01Icon: './PlusMinus01Icon.js',
  PlusMinus02Icon: './PlusMinus02Icon.js',
  PlusMinus03Icon: './PlusMinus03Icon.js',
  PlusMinusCircle01Icon: './PlusMinusCircle01Icon.js',
  PlusMinusCircle02Icon: './PlusMinusCircle02Icon.js',
  PlusMinusSquare01Icon: './PlusMinusSquare01Icon.js',
  PlusMinusSquare02Icon: './PlusMinusSquare02Icon.js',
  PlusMinusIcon: './PlusMinusIcon.js',
  PlusSignCircleIcon: './PlusSignCircleIcon.js',
  PlusSignSquareIcon: './PlusSignSquareIcon.js',
  PlusSignIcon: './PlusSignIcon.js',
  Png01Icon: './Png01Icon.js',
  Png02Icon: './Png02Icon.js',
  PocketKnifeIcon: './PocketKnifeIcon.js',
  PocketIcon: './PocketIcon.js',
  PodcastIcon: './PodcastIcon.js',
  PodiumIcon: './PodiumIcon.js',
  PointingLeft01Icon: './PointingLeft01Icon.js',
  PointingLeft02Icon: './PointingLeft02Icon.js',
  PointingLeft03Icon: './PointingLeft03Icon.js',
  PointingLeft04Icon: './PointingLeft04Icon.js',
  PointingLeft05Icon: './PointingLeft05Icon.js',
  PointingLeft06Icon: './PointingLeft06Icon.js',
  PointingLeft07Icon: './PointingLeft07Icon.js',
  PointingLeft08Icon: './PointingLeft08Icon.js',
  PointingRight01Icon: './PointingRight01Icon.js',
  PointingRight02Icon: './PointingRight02Icon.js',
  PointingRight03Icon: './PointingRight03Icon.js',
  PointingRight04Icon: './PointingRight04Icon.js',
  PointingRight05Icon: './PointingRight05Icon.js',
  PointingRight06Icon: './PointingRight06Icon.js',
  PointingRight07Icon: './PointingRight07Icon.js',
  PointingRight08Icon: './PointingRight08Icon.js',
  PokeballIcon: './PokeballIcon.js',
  PokemonIcon: './PokemonIcon.js',
  PoliceBadgeIcon: './PoliceBadgeIcon.js',
  PoliceCapIcon: './PoliceCapIcon.js',
  PoliceCarIcon: './PoliceCarIcon.js',
  PoliceStationIcon: './PoliceStationIcon.js',
  PolicyIcon: './PolicyIcon.js',
  PolyTankIcon: './PolyTankIcon.js',
  PolygonIcon: './PolygonIcon.js',
  PoolIcon: './PoolIcon.js',
  PoolTableIcon: './PoolTableIcon.js',
  PoopIcon: './PoopIcon.js',
  PopcornIcon: './PopcornIcon.js',
  PopsicleIcon: './PopsicleIcon.js',
  Pot01Icon: './Pot01Icon.js',
  Pot02Icon: './Pot02Icon.js',
  PotionIcon: './PotionIcon.js',
  PoundCircleIcon: './PoundCircleIcon.js',
  PoundReceiveIcon: './PoundReceiveIcon.js',
  PoundSendIcon: './PoundSendIcon.js',
  PoundSquareIcon: './PoundSquareIcon.js',
  PoundIcon: './PoundIcon.js',
  PowerOffIcon: './PowerOffIcon.js',
  PowerServiceIcon: './PowerServiceIcon.js',
  PowerSocket01Icon: './PowerSocket01Icon.js',
  PowerSocket02Icon: './PowerSocket02Icon.js',
  PowerIcon: './PowerIcon.js',
  Ppt01Icon: './Ppt01Icon.js',
  Ppt02Icon: './Ppt02Icon.js',
  PrawnIcon: './PrawnIcon.js',
  PrayerRug01Icon: './PrayerRug01Icon.js',
  PrayerRug02Icon: './PrayerRug02Icon.js',
  PreferenceHorizontalIcon: './PreferenceHorizontalIcon.js',
  PreferenceVerticalIcon: './PreferenceVerticalIcon.js',
  PrescriptionIcon: './PrescriptionIcon.js',
  PrescriptionsIcon: './PrescriptionsIcon.js',
  Presentation01Icon: './Presentation01Icon.js',
  Presentation02Icon: './Presentation02Icon.js',
  Presentation03Icon: './Presentation03Icon.js',
  Presentation04Icon: './Presentation04Icon.js',
  Presentation05Icon: './Presentation05Icon.js',
  Presentation06Icon: './Presentation06Icon.js',
  Presentation07Icon: './Presentation07Icon.js',
  PresentationBarChart01Icon: './PresentationBarChart01Icon.js',
  PresentationBarChart02Icon: './PresentationBarChart02Icon.js',
  PresentationLineChart01Icon: './PresentationLineChart01Icon.js',
  PresentationLineChart02Icon: './PresentationLineChart02Icon.js',
  PresentationOnlineIcon: './PresentationOnlineIcon.js',
  PresentationPodiumIcon: './PresentationPodiumIcon.js',
  PreviousIcon: './PreviousIcon.js',
  PrinterThreeDIcon: './PrinterThreeDIcon.js',
  PrinterCheckIcon: './PrinterCheckIcon.js',
  PrinterOffIcon: './PrinterOffIcon.js',
  PrinterIcon: './PrinterIcon.js',
  PrinterXIcon: './PrinterXIcon.js',
  Prism01Icon: './Prism01Icon.js',
  PrismIcon: './PrismIcon.js',
  PrisonGuardIcon: './PrisonGuardIcon.js',
  PrisonIcon: './PrisonIcon.js',
  PrisonerIcon: './PrisonerIcon.js',
  ProductLoadingIcon: './ProductLoadingIcon.js',
  Profile02Icon: './Profile02Icon.js',
  ProfileIcon: './ProfileIcon.js',
  ProfitIcon: './ProfitIcon.js',
  ProgrammingFlagIcon: './ProgrammingFlagIcon.js',
  Progress01Icon: './Progress01Icon.js',
  Progress02Icon: './Progress02Icon.js',
  Progress03Icon: './Progress03Icon.js',
  Progress04Icon: './Progress04Icon.js',
  ProgressIcon: './ProgressIcon.js',
  Projector01Icon: './Projector01Icon.js',
  ProjectorIcon: './ProjectorIcon.js',
  PromotionIcon: './PromotionIcon.js',
  PropertyAddIcon: './PropertyAddIcon.js',
  PropertyDeleteIcon: './PropertyDeleteIcon.js',
  PropertyEditIcon: './PropertyEditIcon.js',
  PropertyNewIcon: './PropertyNewIcon.js',
  PropertySearchIcon: './PropertySearchIcon.js',
  PropertyViewIcon: './PropertyViewIcon.js',
  ProportionsIcon: './ProportionsIcon.js',
  ProtectionMaskIcon: './ProtectionMaskIcon.js',
  PulleyIcon: './PulleyIcon.js',
  Pulse01Icon: './Pulse01Icon.js',
  Pulse02Icon: './Pulse02Icon.js',
  PulseRectangle01Icon: './PulseRectangle01Icon.js',
  PulseRectangle02Icon: './PulseRectangle02Icon.js',
  PumpkinIcon: './PumpkinIcon.js',
  PunchIcon: './PunchIcon.js',
  PunchingBall01Icon: './PunchingBall01Icon.js',
  PunchingBall02Icon: './PunchingBall02Icon.js',
  Purse01Icon: './Purse01Icon.js',
  PurseIcon: './PurseIcon.js',
  PushUpBarIcon: './PushUpBarIcon.js',
  PuzzleIcon: './PuzzleIcon.js',
  PyramidMaslowoIcon: './PyramidMaslowoIcon.js',
  PyramidIcon: './PyramidIcon.js',
  PyramidStructure01Icon: './PyramidStructure01Icon.js',
  PyramidStructure02Icon: './PyramidStructure02Icon.js',
  PythonIcon: './PythonIcon.js',
  QqPlotIcon: './QqPlotIcon.js',
  QrCode01Icon: './QrCode01Icon.js',
  QrCodeScanIcon: './QrCodeScanIcon.js',
  QrCodeIcon: './QrCodeIcon.js',
  QuestionIcon: './QuestionIcon.js',
  Queue01Icon: './Queue01Icon.js',
  Queue02Icon: './Queue02Icon.js',
  QuillWrite01Icon: './QuillWrite01Icon.js',
  QuillWrite02Icon: './QuillWrite02Icon.js',
  Quiz01Icon: './Quiz01Icon.js',
  Quiz02Icon: './Quiz02Icon.js',
  Quiz03Icon: './Quiz03Icon.js',
  Quiz04Icon: './Quiz04Icon.js',
  Quiz05Icon: './Quiz05Icon.js',
  QuoraIcon: './QuoraIcon.js',
  QuoteDownCircleIcon: './QuoteDownCircleIcon.js',
  QuoteDownSquareIcon: './QuoteDownSquareIcon.js',
  QuoteDownIcon: './QuoteDownIcon.js',
  QuoteUpCircleIcon: './QuoteUpCircleIcon.js',
  QuoteUpSquareIcon: './QuoteUpSquareIcon.js',
  QuoteUpIcon: './QuoteUpIcon.js',
  QuotesIcon: './QuotesIcon.js',
  Quran01Icon: './Quran01Icon.js',
  Quran02Icon: './Quran02Icon.js',
  Quran03Icon: './Quran03Icon.js',
  QwenIcon: './QwenIcon.js',
  RacingFlagIcon: './RacingFlagIcon.js',
  Radar01Icon: './Radar01Icon.js',
  Radar02Icon: './Radar02Icon.js',
  Radar03Icon: './Radar03Icon.js',
  RadialIcon: './RadialIcon.js',
  RadiationIcon: './RadiationIcon.js',
  Radio01Icon: './Radio01Icon.js',
  Radio02Icon: './Radio02Icon.js',
  RadioButtonIcon: './RadioButtonIcon.js',
  RadioIcon: './RadioIcon.js',
  RadioactiveAlertIcon: './RadioactiveAlertIcon.js',
  RadiusIcon: './RadiusIcon.js',
  RailSymbolIcon: './RailSymbolIcon.js',
  RainDoubleDropIcon: './RainDoubleDropIcon.js',
  RainDropIcon: './RainDropIcon.js',
  RainIcon: './RainIcon.js',
  RainbowIcon: './RainbowIcon.js',
  RamMemoryIcon: './RamMemoryIcon.js',
  Ramadhan01Icon: './Ramadhan01Icon.js',
  Ramadhan02Icon: './Ramadhan02Icon.js',
  RamadhanMonthIcon: './RamadhanMonthIcon.js',
  RankingIcon: './RankingIcon.js',
  Rar01Icon: './Rar01Icon.js',
  Rar02Icon: './Rar02Icon.js',
  RatioIcon: './RatioIcon.js',
  Raw01Icon: './Raw01Icon.js',
  Raw02Icon: './Raw02Icon.js',
  ReIcon: './ReIcon.js',
  ReactIcon: './ReactIcon.js',
  RealEstate01Icon: './RealEstate01Icon.js',
  RealEstate02Icon: './RealEstate02Icon.js',
  ReceiptCentIcon: './ReceiptCentIcon.js',
  ReceiptDollarIcon: './ReceiptDollarIcon.js',
  ReceiptEuroIcon: './ReceiptEuroIcon.js',
  ReceiptIndianRupeeIcon: './ReceiptIndianRupeeIcon.js',
  ReceiptJapaneseYenIcon: './ReceiptJapaneseYenIcon.js',
  ReceiptPoundSterlingIcon: './ReceiptPoundSterlingIcon.js',
  ReceiptRussianRubleIcon: './ReceiptRussianRubleIcon.js',
  ReceiptSwissFrancIcon: './ReceiptSwissFrancIcon.js',
  ReceiptTextIcon: './ReceiptTextIcon.js',
  ReceiptTurkishLiraIcon: './ReceiptTurkishLiraIcon.js',
  RecordIcon: './RecordIcon.js',
  RecoveryMailIcon: './RecoveryMailIcon.js',
  RectangleCircleIcon: './RectangleCircleIcon.js',
  Rectangular01Icon: './Rectangular01Icon.js',
  RectangularIcon: './RectangularIcon.js',
  Recycle01Icon: './Recycle01Icon.js',
  Recycle02Icon: './Recycle02Icon.js',
  Recycle03Icon: './Recycle03Icon.js',
  RedditIcon: './RedditIcon.js',
  Redo02Icon: './Redo02Icon.js',
  Redo03Icon: './Redo03Icon.js',
  RedoDotIcon: './RedoDotIcon.js',
  RedoIcon: './RedoIcon.js',
  ReduceParagraphIcon: './ReduceParagraphIcon.js',
  ReflexIcon: './ReflexIcon.js',
  Refresh01Icon: './Refresh01Icon.js',
  Refresh03Icon: './Refresh03Icon.js',
  Refresh04Icon: './Refresh04Icon.js',
  RefreshCwOffIcon: './RefreshCwOffIcon.js',
  RefreshDotIcon: './RefreshDotIcon.js',
  RefreshIcon: './RefreshIcon.js',
  RefrigeratorIcon: './RefrigeratorIcon.js',
  RegexIcon: './RegexIcon.js',
  RegisterIcon: './RegisterIcon.js',
  RegisteredIcon: './RegisteredIcon.js',
  Relieved01Icon: './Relieved01Icon.js',
  Relieved02Icon: './Relieved02Icon.js',
  ReloadIcon: './ReloadIcon.js',
  ReminderIcon: './ReminderIcon.js',
  RemoteControlIcon: './RemoteControlIcon.js',
  Remove01Icon: './Remove01Icon.js',
  Remove02Icon: './Remove02Icon.js',
  RemoveCircleHalfDotIcon: './RemoveCircleHalfDotIcon.js',
  RemoveCircleIcon: './RemoveCircleIcon.js',
  RemoveFemaleIcon: './RemoveFemaleIcon.js',
  RemoveMaleIcon: './RemoveMaleIcon.js',
  RemovePiIcon: './RemovePiIcon.js',
  RemoveSquareIcon: './RemoveSquareIcon.js',
  RenewableEnergy01Icon: './RenewableEnergy01Icon.js',
  RenewableEnergyIcon: './RenewableEnergyIcon.js',
  RepairIcon: './RepairIcon.js',
  RepeatOffIcon: './RepeatOffIcon.js',
  RepeatOne01Icon: './RepeatOne01Icon.js',
  RepeatOne02Icon: './RepeatOne02Icon.js',
  RepeatIcon: './RepeatIcon.js',
  ReplaceAllIcon: './ReplaceAllIcon.js',
  ReplaceIcon: './ReplaceIcon.js',
  ReplayIcon: './ReplayIcon.js',
  ReplitIcon: './ReplitIcon.js',
  RepositoryIcon: './RepositoryIcon.js',
  RepostIcon: './RepostIcon.js',
  ResetPasswordIcon: './ResetPasswordIcon.js',
  Resize01Icon: './Resize01Icon.js',
  Resize02Icon: './Resize02Icon.js',
  ResizeFieldRectangleIcon: './ResizeFieldRectangleIcon.js',
  ResizeFieldIcon: './ResizeFieldIcon.js',
  ResourcesAddIcon: './ResourcesAddIcon.js',
  ResourcesRemoveIcon: './ResourcesRemoveIcon.js',
  Restaurant01Icon: './Restaurant01Icon.js',
  Restaurant02Icon: './Restaurant02Icon.js',
  Restaurant03Icon: './Restaurant03Icon.js',
  RestaurantIcon: './RestaurantIcon.js',
  RestaurantTableIcon: './RestaurantTableIcon.js',
  RestoreBinIcon: './RestoreBinIcon.js',
  ReturnRequestIcon: './ReturnRequestIcon.js',
  ReverseWithdrawal01Icon: './ReverseWithdrawal01Icon.js',
  ReverseWithdrawal02Icon: './ReverseWithdrawal02Icon.js',
  Rhombus01Icon: './Rhombus01Icon.js',
  RhombusIcon: './RhombusIcon.js',
  RibbonIcon: './RibbonIcon.js',
  RiceBowl01Icon: './RiceBowl01Icon.js',
  RiceBowl02Icon: './RiceBowl02Icon.js',
  RightAngleIcon: './RightAngleIcon.js',
  RightToLeftBlockQuoteIcon: './RightToLeftBlockQuoteIcon.js',
  RightToLeftListBulletIcon: './RightToLeftListBulletIcon.js',
  RightToLeftListDashIcon: './RightToLeftListDashIcon.js',
  RightToLeftListNumberIcon: './RightToLeftListNumberIcon.js',
  RightToLeftListTriangleIcon: './RightToLeftListTriangleIcon.js',
  RightTriangleIcon: './RightTriangleIcon.js',
  RippleIcon: './RippleIcon.js',
  RiyalRectangleIcon: './RiyalRectangleIcon.js',
  RiyalIcon: './RiyalIcon.js',
  Road01Icon: './Road01Icon.js',
  Road02Icon: './Road02Icon.js',
  RoadLocation01Icon: './RoadLocation01Icon.js',
  RoadLocation02Icon: './RoadLocation02Icon.js',
  RoadIcon: './RoadIcon.js',
  RoadWaysideIcon: './RoadWaysideIcon.js',
  Robot01Icon: './Robot01Icon.js',
  Robot02Icon: './Robot02Icon.js',
  RoboticIcon: './RoboticIcon.js',
  Rocket01Icon: './Rocket01Icon.js',
  Rocket02Icon: './Rocket02Icon.js',
  RocketIcon: './RocketIcon.js',
  RockingChairIcon: './RockingChairIcon.js',
  RockingHorseIcon: './RockingHorseIcon.js',
  RollerCoasterIcon: './RollerCoasterIcon.js',
  RollerSkateIcon: './RollerSkateIcon.js',
  RollingPinIcon: './RollingPinIcon.js',
  Root01Icon: './Root01Icon.js',
  Root02Icon: './Root02Icon.js',
  RootFirstBracketIcon: './RootFirstBracketIcon.js',
  RootSecondBracketIcon: './RootSecondBracketIcon.js',
  RootThirdBracketIcon: './RootThirdBracketIcon.js',
  RootCircleIcon: './RootCircleIcon.js',
  RoseIcon: './RoseIcon.js',
  Rotate01Icon: './Rotate01Icon.js',
  Rotate02Icon: './Rotate02Icon.js',
  Rotate360Icon: './Rotate360Icon.js',
  RotateBottomLeftIcon: './RotateBottomLeftIcon.js',
  RotateBottomRightIcon: './RotateBottomRightIcon.js',
  RotateCcwSquareIcon: './RotateCcwSquareIcon.js',
  RotateClockwiseIcon: './RotateClockwiseIcon.js',
  RotateCropIcon: './RotateCropIcon.js',
  RotateCwSquareIcon: './RotateCwSquareIcon.js',
  RotateLeft01Icon: './RotateLeft01Icon.js',
  RotateLeft02Icon: './RotateLeft02Icon.js',
  RotateLeft03Icon: './RotateLeft03Icon.js',
  RotateLeft04Icon: './RotateLeft04Icon.js',
  RotateLeft05Icon: './RotateLeft05Icon.js',
  RotateLeft06Icon: './RotateLeft06Icon.js',
  RotateRight01Icon: './RotateRight01Icon.js',
  RotateRight02Icon: './RotateRight02Icon.js',
  RotateRight03Icon: './RotateRight03Icon.js',
  RotateRight04Icon: './RotateRight04Icon.js',
  RotateRight05Icon: './RotateRight05Icon.js',
  RotateRight06Icon: './RotateRight06Icon.js',
  RotateSquareIcon: './RotateSquareIcon.js',
  RotateTopLeftIcon: './RotateTopLeftIcon.js',
  RotateTopRightIcon: './RotateTopRightIcon.js',
  Route01Icon: './Route01Icon.js',
  Route02Icon: './Route02Icon.js',
  Route03Icon: './Route03Icon.js',
  RouteBlockIcon: './RouteBlockIcon.js',
  Router01Icon: './Router01Icon.js',
  Router02Icon: './Router02Icon.js',
  RouterIcon: './RouterIcon.js',
  RowDeleteIcon: './RowDeleteIcon.js',
  RowInsertIcon: './RowInsertIcon.js',
  RssConnected01Icon: './RssConnected01Icon.js',
  RssConnected02Icon: './RssConnected02Icon.js',
  RssErrorIcon: './RssErrorIcon.js',
  RssLockedIcon: './RssLockedIcon.js',
  RssIcon: './RssIcon.js',
  RssUnlockedIcon: './RssUnlockedIcon.js',
  RubElHizbIcon: './RubElHizbIcon.js',
  RubberDuckIcon: './RubberDuckIcon.js',
  RubiksCubeIcon: './RubiksCubeIcon.js',
  RukuIcon: './RukuIcon.js',
  RulerIcon: './RulerIcon.js',
  RunningShoesIcon: './RunningShoesIcon.js',
  RupeeCircleIcon: './RupeeCircleIcon.js',
  RupeeShieldIcon: './RupeeShieldIcon.js',
  RupeeSquareIcon: './RupeeSquareIcon.js',
  RupeeIcon: './RupeeIcon.js',
  RussianRubleIcon: './RussianRubleIcon.js',
  Sad01Icon: './Sad01Icon.js',
  Sad02Icon: './Sad02Icon.js',
  SadDizzyIcon: './SadDizzyIcon.js',
  SafariIcon: './SafariIcon.js',
  SafeBoxIcon: './SafeBoxIcon.js',
  SafeDelivery01Icon: './SafeDelivery01Icon.js',
  SafeDelivery02Icon: './SafeDelivery02Icon.js',
  SafeIcon: './SafeIcon.js',
  SafetyPin01Icon: './SafetyPin01Icon.js',
  SafetyPin02Icon: './SafetyPin02Icon.js',
  SailboatCoastalIcon: './SailboatCoastalIcon.js',
  SailboatOffshoreIcon: './SailboatOffshoreIcon.js',
  SakuraIcon: './SakuraIcon.js',
  SaladIcon: './SaladIcon.js',
  SalahIcon: './SalahIcon.js',
  SalahTimeIcon: './SalahTimeIcon.js',
  SaleTag01Icon: './SaleTag01Icon.js',
  SaleTag02Icon: './SaleTag02Icon.js',
  SandalsIcon: './SandalsIcon.js',
  Satellite01Icon: './Satellite01Icon.js',
  Satellite02Icon: './Satellite02Icon.js',
  Satellite03Icon: './Satellite03Icon.js',
  SatelliteIcon: './SatelliteIcon.js',
  Saturn01Icon: './Saturn01Icon.js',
  Saturn02Icon: './Saturn02Icon.js',
  SaturnIcon: './SaturnIcon.js',
  SaudiRiyalIcon: './SaudiRiyalIcon.js',
  SausageIcon: './SausageIcon.js',
  SaveAllIcon: './SaveAllIcon.js',
  SaveEnergy01Icon: './SaveEnergy01Icon.js',
  SaveEnergy02Icon: './SaveEnergy02Icon.js',
  SaveMoneyDollarIcon: './SaveMoneyDollarIcon.js',
  SaveMoneyEuroIcon: './SaveMoneyEuroIcon.js',
  SaveMoneyPoundIcon: './SaveMoneyPoundIcon.js',
  SaveMoneyYenIcon: './SaveMoneyYenIcon.js',
  SaveOffIcon: './SaveOffIcon.js',
  SaveIcon: './SaveIcon.js',
  SavingsIcon: './SavingsIcon.js',
  ScanEyeIcon: './ScanEyeIcon.js',
  ScanHeartIcon: './ScanHeartIcon.js',
  ScanImageIcon: './ScanImageIcon.js',
  ScanIcon: './ScanIcon.js',
  SchemeIcon: './SchemeIcon.js',
  School01Icon: './School01Icon.js',
  SchoolBell01Icon: './SchoolBell01Icon.js',
  SchoolBell02Icon: './SchoolBell02Icon.js',
  SchoolBusIcon: './SchoolBusIcon.js',
  SchoolReportCardIcon: './SchoolReportCardIcon.js',
  SchoolIcon: './SchoolIcon.js',
  SchoolTieIcon: './SchoolTieIcon.js',
  Scissor01Icon: './Scissor01Icon.js',
  ScissorRectangleIcon: './ScissorRectangleIcon.js',
  ScissorIcon: './ScissorIcon.js',
  Scooter01Icon: './Scooter01Icon.js',
  Scooter02Icon: './Scooter02Icon.js',
  Scooter03Icon: './Scooter03Icon.js',
  Scooter04Icon: './Scooter04Icon.js',
  ScooterElectricIcon: './ScooterElectricIcon.js',
  ScratchCardIcon: './ScratchCardIcon.js',
  ScreenAddToHome02Icon: './ScreenAddToHome02Icon.js',
  ScreenAddToHomeIcon: './ScreenAddToHomeIcon.js',
  ScreenLockRotationIcon: './ScreenLockRotationIcon.js',
  ScreenRotationIcon: './ScreenRotationIcon.js',
  ScribdIcon: './ScribdIcon.js',
  Scroll01Icon: './Scroll01Icon.js',
  ScrollHorizontalIcon: './ScrollHorizontalIcon.js',
  ScrollIcon: './ScrollIcon.js',
  ScrollVerticalIcon: './ScrollVerticalIcon.js',
  SdCardIcon: './SdCardIcon.js',
  SealIcon: './SealIcon.js',
  Search01Icon: './Search01Icon.js',
  Search02Icon: './Search02Icon.js',
  SearchAddIcon: './SearchAddIcon.js',
  SearchAreaIcon: './SearchAreaIcon.js',
  SearchCircleIcon: './SearchCircleIcon.js',
  SearchCodeIcon: './SearchCodeIcon.js',
  SearchDollarIcon: './SearchDollarIcon.js',
  SearchFocusIcon: './SearchFocusIcon.js',
  SearchList01Icon: './SearchList01Icon.js',
  SearchList02Icon: './SearchList02Icon.js',
  SearchMinusIcon: './SearchMinusIcon.js',
  SearchRemoveIcon: './SearchRemoveIcon.js',
  SearchReplaceIcon: './SearchReplaceIcon.js',
  SearchSquareIcon: './SearchSquareIcon.js',
  SearchVisualIcon: './SearchVisualIcon.js',
  SearchingIcon: './SearchingIcon.js',
  SeatSelectorIcon: './SeatSelectorIcon.js',
  SectionIcon: './SectionIcon.js',
  SecuredNetworkIcon: './SecuredNetworkIcon.js',
  SecurityBlockIcon: './SecurityBlockIcon.js',
  SecurityCheckIcon: './SecurityCheckIcon.js',
  SecurityKeyUsbIcon: './SecurityKeyUsbIcon.js',
  SecurityLockIcon: './SecurityLockIcon.js',
  SecurityPasswordIcon: './SecurityPasswordIcon.js',
  SecurityIcon: './SecurityIcon.js',
  SecurityValidationIcon: './SecurityValidationIcon.js',
  SecurityWarningIcon: './SecurityWarningIcon.js',
  SecurityWifiIcon: './SecurityWifiIcon.js',
  SeesawIcon: './SeesawIcon.js',
  SegmentIcon: './SegmentIcon.js',
  Select01Icon: './Select01Icon.js',
  Select02Icon: './Select02Icon.js',
  SelfTransferIcon: './SelfTransferIcon.js',
  SemiTruckIcon: './SemiTruckIcon.js',
  SendToBackIcon: './SendToBackIcon.js',
  SendToMobile02Icon: './SendToMobile02Icon.js',
  SendToMobileIcon: './SendToMobileIcon.js',
  SendingOrderIcon: './SendingOrderIcon.js',
  SenselessIcon: './SenselessIcon.js',
  Sent02Icon: './Sent02Icon.js',
  SentIcon: './SentIcon.js',
  SeoIcon: './SeoIcon.js',
  SeparatorHorizontalIcon: './SeparatorHorizontalIcon.js',
  ServerStack01Icon: './ServerStack01Icon.js',
  ServerStack02Icon: './ServerStack02Icon.js',
  ServerStack03Icon: './ServerStack03Icon.js',
  ServiceIcon: './ServiceIcon.js',
  ServingFoodIcon: './ServingFoodIcon.js',
  Setting06Icon: './Setting06Icon.js',
  Setting07Icon: './Setting07Icon.js',
  SettingDone01Icon: './SettingDone01Icon.js',
  SettingDone02Icon: './SettingDone02Icon.js',
  SettingDone03Icon: './SettingDone03Icon.js',
  SettingDone04Icon: './SettingDone04Icon.js',
  SettingError03Icon: './SettingError03Icon.js',
  SettingError04Icon: './SettingError04Icon.js',
  Settings01Icon: './Settings01Icon.js',
  Settings02Icon: './Settings02Icon.js',
  Settings03Icon: './Settings03Icon.js',
  Settings04Icon: './Settings04Icon.js',
  Settings05Icon: './Settings05Icon.js',
  SettingsError01Icon: './SettingsError01Icon.js',
  SettingsError02Icon: './SettingsError02Icon.js',
  Setup01Icon: './Setup01Icon.js',
  Setup02Icon: './Setup02Icon.js',
  SevenCircleIcon: './SevenCircleIcon.js',
  SevenSquareIcon: './SevenSquareIcon.js',
  ShadcnSquareIcon: './ShadcnSquareIcon.js',
  ShadcnIcon: './ShadcnIcon.js',
  Shaka01Icon: './Shaka01Icon.js',
  Shaka02Icon: './Shaka02Icon.js',
  Shaka03Icon: './Shaka03Icon.js',
  Shaka04Icon: './Shaka04Icon.js',
  ShampooIcon: './ShampooIcon.js',
  ShapeCollectionIcon: './ShapeCollectionIcon.js',
  Shapes01Icon: './Shapes01Icon.js',
  ShapesIcon: './ShapesIcon.js',
  Share01Icon: './Share01Icon.js',
  Share02Icon: './Share02Icon.js',
  Share03Icon: './Share03Icon.js',
  Share04Icon: './Share04Icon.js',
  Share05Icon: './Share05Icon.js',
  Share06Icon: './Share06Icon.js',
  Share07Icon: './Share07Icon.js',
  Share08Icon: './Share08Icon.js',
  ShareKnowledgeIcon: './ShareKnowledgeIcon.js',
  ShareLocation01Icon: './ShareLocation01Icon.js',
  ShareLocation02Icon: './ShareLocation02Icon.js',
  SharedDriveIcon: './SharedDriveIcon.js',
  SharedWifiIcon: './SharedWifiIcon.js',
  SharingIcon: './SharingIcon.js',
  ShellIcon: './ShellIcon.js',
  ShellfishIcon: './ShellfishIcon.js',
  Sheriff01Icon: './Sheriff01Icon.js',
  Sheriff02Icon: './Sheriff02Icon.js',
  Shield01Icon: './Shield01Icon.js',
  Shield02Icon: './Shield02Icon.js',
  ShieldBanIcon: './ShieldBanIcon.js',
  ShieldBlockchainIcon: './ShieldBlockchainIcon.js',
  ShieldEnergyIcon: './ShieldEnergyIcon.js',
  ShieldHalfIcon: './ShieldHalfIcon.js',
  ShieldKeyIcon: './ShieldKeyIcon.js',
  ShieldMinusIcon: './ShieldMinusIcon.js',
  ShieldPlusIcon: './ShieldPlusIcon.js',
  ShieldQuestionMarkIcon: './ShieldQuestionMarkIcon.js',
  ShieldUserIcon: './ShieldUserIcon.js',
  ShipmentTrackingIcon: './ShipmentTrackingIcon.js',
  ShippingCenterIcon: './ShippingCenterIcon.js',
  ShippingLoadingIcon: './ShippingLoadingIcon.js',
  ShippingTruck01Icon: './ShippingTruck01Icon.js',
  ShippingTruck02Icon: './ShippingTruck02Icon.js',
  Shirt01Icon: './Shirt01Icon.js',
  ShockedIcon: './ShockedIcon.js',
  ShopSignIcon: './ShopSignIcon.js',
  ShopifyIcon: './ShopifyIcon.js',
  ShoppingBag01Icon: './ShoppingBag01Icon.js',
  ShoppingBag02Icon: './ShoppingBag02Icon.js',
  ShoppingBag03Icon: './ShoppingBag03Icon.js',
  ShoppingBagAddIcon: './ShoppingBagAddIcon.js',
  ShoppingBagCheckIcon: './ShoppingBagCheckIcon.js',
  ShoppingBagFavoriteIcon: './ShoppingBagFavoriteIcon.js',
  ShoppingBagRemoveIcon: './ShoppingBagRemoveIcon.js',
  ShoppingBasket01Icon: './ShoppingBasket01Icon.js',
  ShoppingBasket02Icon: './ShoppingBasket02Icon.js',
  ShoppingBasket03Icon: './ShoppingBasket03Icon.js',
  ShoppingBasketAdd01Icon: './ShoppingBasketAdd01Icon.js',
  ShoppingBasketAdd02Icon: './ShoppingBasketAdd02Icon.js',
  ShoppingBasketAdd03Icon: './ShoppingBasketAdd03Icon.js',
  ShoppingBasketCheckIn01Icon: './ShoppingBasketCheckIn01Icon.js',
  ShoppingBasketCheckIn02Icon: './ShoppingBasketCheckIn02Icon.js',
  ShoppingBasketCheckIn03Icon: './ShoppingBasketCheckIn03Icon.js',
  ShoppingBasketCheckOut01Icon: './ShoppingBasketCheckOut01Icon.js',
  ShoppingBasketCheckOut02Icon: './ShoppingBasketCheckOut02Icon.js',
  ShoppingBasketCheckOut03Icon: './ShoppingBasketCheckOut03Icon.js',
  ShoppingBasketDone01Icon: './ShoppingBasketDone01Icon.js',
  ShoppingBasketDone02Icon: './ShoppingBasketDone02Icon.js',
  ShoppingBasketDone03Icon: './ShoppingBasketDone03Icon.js',
  ShoppingBasketFavorite01Icon: './ShoppingBasketFavorite01Icon.js',
  ShoppingBasketFavorite02Icon: './ShoppingBasketFavorite02Icon.js',
  ShoppingBasketFavorite03Icon: './ShoppingBasketFavorite03Icon.js',
  ShoppingBasketRemove01Icon: './ShoppingBasketRemove01Icon.js',
  ShoppingBasketRemove02Icon: './ShoppingBasketRemove02Icon.js',
  ShoppingBasketRemove03Icon: './ShoppingBasketRemove03Icon.js',
  ShoppingBasketSecure01Icon: './ShoppingBasketSecure01Icon.js',
  ShoppingBasketSecure02Icon: './ShoppingBasketSecure02Icon.js',
  ShoppingBasketSecure03Icon: './ShoppingBasketSecure03Icon.js',
  ShoppingCart01Icon: './ShoppingCart01Icon.js',
  ShoppingCart02Icon: './ShoppingCart02Icon.js',
  ShoppingCartAdd01Icon: './ShoppingCartAdd01Icon.js',
  ShoppingCartAdd02Icon: './ShoppingCartAdd02Icon.js',
  ShoppingCartCheck01Icon: './ShoppingCartCheck01Icon.js',
  ShoppingCartCheck02Icon: './ShoppingCartCheck02Icon.js',
  ShoppingCartCheckIn01Icon: './ShoppingCartCheckIn01Icon.js',
  ShoppingCartCheckIn02Icon: './ShoppingCartCheckIn02Icon.js',
  ShoppingCartCheckOut01Icon: './ShoppingCartCheckOut01Icon.js',
  ShoppingCartCheckOut02Icon: './ShoppingCartCheckOut02Icon.js',
  ShoppingCartFavorite01Icon: './ShoppingCartFavorite01Icon.js',
  ShoppingCartFavorite02Icon: './ShoppingCartFavorite02Icon.js',
  ShoppingCartRemove01Icon: './ShoppingCartRemove01Icon.js',
  ShoppingCartRemove02Icon: './ShoppingCartRemove02Icon.js',
  ShortsPantsIcon: './ShortsPantsIcon.js',
  ShoulderIcon: './ShoulderIcon.js',
  ShovelIcon: './ShovelIcon.js',
  ShowerHeadIcon: './ShowerHeadIcon.js',
  ShrinkDotIcon: './ShrinkDotIcon.js',
  ShuffleSquareIcon: './ShuffleSquareIcon.js',
  ShuffleIcon: './ShuffleIcon.js',
  ShutDownIcon: './ShutDownIcon.js',
  ShutterstockIcon: './ShutterstockIcon.js',
  SidebarBottomIcon: './SidebarBottomIcon.js',
  SidebarLeft01Icon: './SidebarLeft01Icon.js',
  SidebarLeftIcon: './SidebarLeftIcon.js',
  SidebarRight01Icon: './SidebarRight01Icon.js',
  SidebarRightIcon: './SidebarRightIcon.js',
  SidebarTopIcon: './SidebarTopIcon.js',
  SignLanguageCIcon: './SignLanguageCIcon.js',
  SignalFull01Icon: './SignalFull01Icon.js',
  SignalFull02Icon: './SignalFull02Icon.js',
  SignalLow01Icon: './SignalLow01Icon.js',
  SignalLow02Icon: './SignalLow02Icon.js',
  SignalLowMediumIcon: './SignalLowMediumIcon.js',
  SignalMedium01Icon: './SignalMedium01Icon.js',
  SignalMedium02Icon: './SignalMedium02Icon.js',
  SignalNo01Icon: './SignalNo01Icon.js',
  SignalNo02Icon: './SignalNo02Icon.js',
  SignalIcon: './SignalIcon.js',
  SignatureIcon: './SignatureIcon.js',
  SilenceIcon: './SilenceIcon.js',
  Simcard01Icon: './Simcard01Icon.js',
  Simcard02Icon: './Simcard02Icon.js',
  SimcardDualIcon: './SimcardDualIcon.js',
  SinIcon: './SinIcon.js',
  Sine01Icon: './Sine01Icon.js',
  Sine02Icon: './Sine02Icon.js',
  SingLeftIcon: './SingLeftIcon.js',
  SingRightIcon: './SingRightIcon.js',
  Sink01Icon: './Sink01Icon.js',
  Sink02Icon: './Sink02Icon.js',
  SiriNewIcon: './SiriNewIcon.js',
  SiriIcon: './SiriIcon.js',
  SixCircleIcon: './SixCircleIcon.js',
  SixSquareIcon: './SixSquareIcon.js',
  SketchIcon: './SketchIcon.js',
  SkewIcon: './SkewIcon.js',
  SkiIcon: './SkiIcon.js',
  SkippingRopeIcon: './SkippingRopeIcon.js',
  SkoolIcon: './SkoolIcon.js',
  SkullIcon: './SkullIcon.js',
  SkypeIcon: './SkypeIcon.js',
  SlackIcon: './SlackIcon.js',
  SleepingIcon: './SleepingIcon.js',
  SleevelessIcon: './SleevelessIcon.js',
  SlideIcon: './SlideIcon.js',
  SlidersHorizontalIcon: './SlidersHorizontalIcon.js',
  SlidersVerticalIcon: './SlidersVerticalIcon.js',
  SlideshareIcon: './SlideshareIcon.js',
  SlowWindsIcon: './SlowWindsIcon.js',
  SmartAcIcon: './SmartAcIcon.js',
  SmartPhone01Icon: './SmartPhone01Icon.js',
  SmartPhone02Icon: './SmartPhone02Icon.js',
  SmartPhone03Icon: './SmartPhone03Icon.js',
  SmartPhone04Icon: './SmartPhone04Icon.js',
  SmartPhoneLandscape02Icon: './SmartPhoneLandscape02Icon.js',
  SmartPhoneLandscapeIcon: './SmartPhoneLandscapeIcon.js',
  SmartIcon: './SmartIcon.js',
  SmartWatch01Icon: './SmartWatch01Icon.js',
  SmartWatch02Icon: './SmartWatch02Icon.js',
  SmartWatch03Icon: './SmartWatch03Icon.js',
  SmartWatch04Icon: './SmartWatch04Icon.js',
  SmartphoneChargingIcon: './SmartphoneChargingIcon.js',
  SmartphoneLostWifiIcon: './SmartphoneLostWifiIcon.js',
  SmartphoneNfcIcon: './SmartphoneNfcIcon.js',
  SmartphoneWifiIcon: './SmartphoneWifiIcon.js',
  SmileDizzyIcon: './SmileDizzyIcon.js',
  SmilePlusIcon: './SmilePlusIcon.js',
  SmileIcon: './SmileIcon.js',
  SmsCodeIcon: './SmsCodeIcon.js',
  SnailIcon: './SnailIcon.js',
  SnapchatIcon: './SnapchatIcon.js',
  SnowIcon: './SnowIcon.js',
  SocksIcon: './SocksIcon.js',
  SodaCanIcon: './SodaCanIcon.js',
  Sofa01Icon: './Sofa01Icon.js',
  Sofa02Icon: './Sofa02Icon.js',
  Sofa03Icon: './Sofa03Icon.js',
  SofaSingleIcon: './SofaSingleIcon.js',
  SoftDrink01Icon: './SoftDrink01Icon.js',
  SoftDrink02Icon: './SoftDrink02Icon.js',
  SoftwareLicenseIcon: './SoftwareLicenseIcon.js',
  SoftwareIcon: './SoftwareIcon.js',
  SoftwareUninstallIcon: './SoftwareUninstallIcon.js',
  SoilMoistureFieldIcon: './SoilMoistureFieldIcon.js',
  SoilMoistureGlobalIcon: './SoilMoistureGlobalIcon.js',
  SoilTemperatureFieldIcon: './SoilTemperatureFieldIcon.js',
  SoilTemperatureGlobalIcon: './SoilTemperatureGlobalIcon.js',
  SolarEnergyIcon: './SolarEnergyIcon.js',
  SolarPanel01Icon: './SolarPanel01Icon.js',
  SolarPanel02Icon: './SolarPanel02Icon.js',
  SolarPanel03Icon: './SolarPanel03Icon.js',
  SolarPanel04Icon: './SolarPanel04Icon.js',
  SolarPanel05Icon: './SolarPanel05Icon.js',
  SolarPowerIcon: './SolarPowerIcon.js',
  SolarSystem01Icon: './SolarSystem01Icon.js',
  SolarSystemIcon: './SolarSystemIcon.js',
  SolidLine01Icon: './SolidLine01Icon.js',
  SolidLine02Icon: './SolidLine02Icon.js',
  SortByDown01Icon: './SortByDown01Icon.js',
  SortByDown02Icon: './SortByDown02Icon.js',
  SortByUp01Icon: './SortByUp01Icon.js',
  SortByUp02Icon: './SortByUp02Icon.js',
  SortDescendingIcon: './SortDescendingIcon.js',
  Sorting01Icon: './Sorting01Icon.js',
  Sorting02Icon: './Sorting02Icon.js',
  Sorting03Icon: './Sorting03Icon.js',
  Sorting04Icon: './Sorting04Icon.js',
  Sorting05Icon: './Sorting05Icon.js',
  SortingOneNineIcon: './SortingOneNineIcon.js',
  SortingNineOneIcon: './SortingNineOneIcon.js',
  SortingAZ01Icon: './SortingAZ01Icon.js',
  SortingAZ02Icon: './SortingAZ02Icon.js',
  SortingDownIcon: './SortingDownIcon.js',
  SortingUpIcon: './SortingUpIcon.js',
  SortingZA01Icon: './SortingZA01Icon.js',
  SoundcloudIcon: './SoundcloudIcon.js',
  SourceCodeCircleIcon: './SourceCodeCircleIcon.js',
  SourceCodeSquareIcon: './SourceCodeSquareIcon.js',
  SourceCodeIcon: './SourceCodeIcon.js',
  SpaceshipIcon: './SpaceshipIcon.js',
  SpadesIcon: './SpadesIcon.js',
  SpaghettiIcon: './SpaghettiIcon.js',
  SpamIcon: './SpamIcon.js',
  SparklesIcon: './SparklesIcon.js',
  SpartanHelmetIcon: './SpartanHelmetIcon.js',
  SpatulaIcon: './SpatulaIcon.js',
  Speaker01Icon: './Speaker01Icon.js',
  SpeakerIcon: './SpeakerIcon.js',
  SpeechIcon: './SpeechIcon.js',
  SpeechToTextIcon: './SpeechToTextIcon.js',
  SpeedTrain01Icon: './SpeedTrain01Icon.js',
  SpeedTrain02Icon: './SpeedTrain02Icon.js',
  SpermIcon: './SpermIcon.js',
  SphereIcon: './SphereIcon.js',
  SpiralsIcon: './SpiralsIcon.js',
  SplinePointerIcon: './SplinePointerIcon.js',
  SplitIcon: './SplitIcon.js',
  SpoolIcon: './SpoolIcon.js',
  SpoonAndForkIcon: './SpoonAndForkIcon.js',
  SpoonAndKnifeIcon: './SpoonAndKnifeIcon.js',
  SpoonIcon: './SpoonIcon.js',
  SpotifyIcon: './SpotifyIcon.js',
  SpotlightIcon: './SpotlightIcon.js',
  SprayCanIcon: './SprayCanIcon.js',
  SqlIcon: './SqlIcon.js',
  Square01Icon: './Square01Icon.js',
  SquareArrowDataTransferDiagonalIcon: './SquareArrowDataTransferDiagonalIcon.js',
  SquareArrowDataTransferHorizontalIcon: './SquareArrowDataTransferHorizontalIcon.js',
  SquareArrowDataTransferVerticalIcon: './SquareArrowDataTransferVerticalIcon.js',
  SquareArrowDiagonal01Icon: './SquareArrowDiagonal01Icon.js',
  SquareArrowDiagonal02Icon: './SquareArrowDiagonal02Icon.js',
  SquareArrowDown01Icon: './SquareArrowDown01Icon.js',
  SquareArrowDown02Icon: './SquareArrowDown02Icon.js',
  SquareArrowDown03Icon: './SquareArrowDown03Icon.js',
  SquareArrowDownDoubleIcon: './SquareArrowDownDoubleIcon.js',
  SquareArrowDownLeftIcon: './SquareArrowDownLeftIcon.js',
  SquareArrowDownRightIcon: './SquareArrowDownRightIcon.js',
  SquareArrowExpand01Icon: './SquareArrowExpand01Icon.js',
  SquareArrowExpand02Icon: './SquareArrowExpand02Icon.js',
  SquareArrowHorizontalIcon: './SquareArrowHorizontalIcon.js',
  SquareArrowLeft01Icon: './SquareArrowLeft01Icon.js',
  SquareArrowLeft02Icon: './SquareArrowLeft02Icon.js',
  SquareArrowLeft03Icon: './SquareArrowLeft03Icon.js',
  SquareArrowLeftDoubleIcon: './SquareArrowLeftDoubleIcon.js',
  SquareArrowLeftRightIcon: './SquareArrowLeftRightIcon.js',
  SquareArrowMoveDownLeftIcon: './SquareArrowMoveDownLeftIcon.js',
  SquareArrowMoveDownRightIcon: './SquareArrowMoveDownRightIcon.js',
  SquareArrowMoveLeftDownIcon: './SquareArrowMoveLeftDownIcon.js',
  SquareArrowMoveLeftUpIcon: './SquareArrowMoveLeftUpIcon.js',
  SquareArrowMoveRightDownIcon: './SquareArrowMoveRightDownIcon.js',
  SquareArrowMoveRightUpIcon: './SquareArrowMoveRightUpIcon.js',
  SquareArrowReload01Icon: './SquareArrowReload01Icon.js',
  SquareArrowReload02Icon: './SquareArrowReload02Icon.js',
  SquareArrowRight01Icon: './SquareArrowRight01Icon.js',
  SquareArrowRight02Icon: './SquareArrowRight02Icon.js',
  SquareArrowRight03Icon: './SquareArrowRight03Icon.js',
  SquareArrowRightDoubleIcon: './SquareArrowRightDoubleIcon.js',
  SquareArrowShrink01Icon: './SquareArrowShrink01Icon.js',
  SquareArrowShrink02Icon: './SquareArrowShrink02Icon.js',
  SquareArrowUp01Icon: './SquareArrowUp01Icon.js',
  SquareArrowUp02Icon: './SquareArrowUp02Icon.js',
  SquareArrowUp03Icon: './SquareArrowUp03Icon.js',
  SquareArrowUpDoubleIcon: './SquareArrowUpDoubleIcon.js',
  SquareArrowUpDownIcon: './SquareArrowUpDownIcon.js',
  SquareArrowUpLeftIcon: './SquareArrowUpLeftIcon.js',
  SquareArrowUpRight02Icon: './SquareArrowUpRight02Icon.js',
  SquareArrowUpRightIcon: './SquareArrowUpRightIcon.js',
  SquareArrowVerticalIcon: './SquareArrowVerticalIcon.js',
  SquareAsteriskIcon: './SquareAsteriskIcon.js',
  SquareBottomDashedScissorsIcon: './SquareBottomDashedScissorsIcon.js',
  SquareChartGanttIcon: './SquareChartGanttIcon.js',
  SquareCircleIcon: './SquareCircleIcon.js',
  SquareDashedKanbanIcon: './SquareDashedKanbanIcon.js',
  SquareDashedTopSolidIcon: './SquareDashedTopSolidIcon.js',
  SquareFIcon: './SquareFIcon.js',
  SquareLock01Icon: './SquareLock01Icon.js',
  SquareLock02Icon: './SquareLock02Icon.js',
  SquareLockAdd01Icon: './SquareLockAdd01Icon.js',
  SquareLockAdd02Icon: './SquareLockAdd02Icon.js',
  SquareLockCheck01Icon: './SquareLockCheck01Icon.js',
  SquareLockCheck02Icon: './SquareLockCheck02Icon.js',
  SquareLockMinus01Icon: './SquareLockMinus01Icon.js',
  SquareLockMinus02Icon: './SquareLockMinus02Icon.js',
  SquareLockPasswordIcon: './SquareLockPasswordIcon.js',
  SquareLockRemove01Icon: './SquareLockRemove01Icon.js',
  SquareLockRemove02Icon: './SquareLockRemove02Icon.js',
  SquareMIcon: './SquareMIcon.js',
  SquareMousePointerIcon: './SquareMousePointerIcon.js',
  SquareParkingOffIcon: './SquareParkingOffIcon.js',
  SquarePowerIcon: './SquarePowerIcon.js',
  SquareRootSquareIcon: './SquareRootSquareIcon.js',
  SquareRoundCornerIcon: './SquareRoundCornerIcon.js',
  SquareSquareIcon: './SquareSquareIcon.js',
  SquareStackIcon: './SquareStackIcon.js',
  SquareIcon: './SquareIcon.js',
  SquareUnlock01Icon: './SquareUnlock01Icon.js',
  SquareUnlock02Icon: './SquareUnlock02Icon.js',
  SquaresExcludeIcon: './SquaresExcludeIcon.js',
  StackStarIcon: './StackStarIcon.js',
  Stairs01Icon: './Stairs01Icon.js',
  Stairs02Icon: './Stairs02Icon.js',
  Stairs03Icon: './Stairs03Icon.js',
  Stairs04Icon: './Stairs04Icon.js',
  StakeIcon: './StakeIcon.js',
  Stamp01Icon: './Stamp01Icon.js',
  Stamp02Icon: './Stamp02Icon.js',
  StampIcon: './StampIcon.js',
  StarAward01Icon: './StarAward01Icon.js',
  StarAward02Icon: './StarAward02Icon.js',
  StarCircleIcon: './StarCircleIcon.js',
  StarFaceIcon: './StarFaceIcon.js',
  StarHalfIcon: './StarHalfIcon.js',
  StarOffIcon: './StarOffIcon.js',
  StarSquareIcon: './StarSquareIcon.js',
  StarIcon: './StarIcon.js',
  StarsIcon: './StarsIcon.js',
  StartUp01Icon: './StartUp01Icon.js',
  StartUp02Icon: './StartUp02Icon.js',
  StationeryIcon: './StationeryIcon.js',
  StatusIcon: './StatusIcon.js',
  SteakIcon: './SteakIcon.js',
  SteeringIcon: './SteeringIcon.js',
  StepIntoIcon: './StepIntoIcon.js',
  StepOutIcon: './StepOutIcon.js',
  StepOverIcon: './StepOverIcon.js',
  Stethoscope02Icon: './Stethoscope02Icon.js',
  StethoscopeIcon: './StethoscopeIcon.js',
  StickerIcon: './StickerIcon.js',
  StickyNote01Icon: './StickyNote01Icon.js',
  StickyNote02Icon: './StickyNote02Icon.js',
  StickyNote03Icon: './StickyNote03Icon.js',
  StopCircleIcon: './StopCircleIcon.js',
  StopLossOrderIcon: './StopLossOrderIcon.js',
  StopIcon: './StopIcon.js',
  StopWatchIcon: './StopWatchIcon.js',
  Store01Icon: './Store01Icon.js',
  Store02Icon: './Store02Icon.js',
  Store03Icon: './Store03Icon.js',
  Store04Icon: './Store04Icon.js',
  StoreAdd01Icon: './StoreAdd01Icon.js',
  StoreAdd02Icon: './StoreAdd02Icon.js',
  StoreLocation01Icon: './StoreLocation01Icon.js',
  StoreLocation02Icon: './StoreLocation02Icon.js',
  StoreManagement01Icon: './StoreManagement01Icon.js',
  StoreManagement02Icon: './StoreManagement02Icon.js',
  StoreRemove01Icon: './StoreRemove01Icon.js',
  StoreRemove02Icon: './StoreRemove02Icon.js',
  StoreVerified01Icon: './StoreVerified01Icon.js',
  StoreVerified02Icon: './StoreVerified02Icon.js',
  StraightEdgeIcon: './StraightEdgeIcon.js',
  StrategyIcon: './StrategyIcon.js',
  StreeringWheelIcon: './StreeringWheelIcon.js',
  StreetFoodIcon: './StreetFoodIcon.js',
  StripeIcon: './StripeIcon.js',
  StrokeBottomIcon: './StrokeBottomIcon.js',
  StrokeCenterIcon: './StrokeCenterIcon.js',
  StrokeInsideIcon: './StrokeInsideIcon.js',
  StrokeLeftIcon: './StrokeLeftIcon.js',
  StrokeOutsideIcon: './StrokeOutsideIcon.js',
  StrokeRightIcon: './StrokeRightIcon.js',
  StrokeTopIcon: './StrokeTopIcon.js',
  Structure01Icon: './Structure01Icon.js',
  Structure02Icon: './Structure02Icon.js',
  Structure03Icon: './Structure03Icon.js',
  Structure04Icon: './Structure04Icon.js',
  Structure05Icon: './Structure05Icon.js',
  StructureAddIcon: './StructureAddIcon.js',
  StructureCheckIcon: './StructureCheckIcon.js',
  StructureFailIcon: './StructureFailIcon.js',
  StructureFolderCircleIcon: './StructureFolderCircleIcon.js',
  StructureFolderIcon: './StructureFolderIcon.js',
  StudentCardIcon: './StudentCardIcon.js',
  StudentIcon: './StudentIcon.js',
  StudentsIcon: './StudentsIcon.js',
  StudyDeskIcon: './StudyDeskIcon.js',
  StudyLampIcon: './StudyLampIcon.js',
  StumbleuponIcon: './StumbleuponIcon.js',
  SubmarineIcon: './SubmarineIcon.js',
  SubmergeIcon: './SubmergeIcon.js',
  SubnodeAddIcon: './SubnodeAddIcon.js',
  SubnodeDeleteIcon: './SubnodeDeleteIcon.js',
  SubpoenaIcon: './SubpoenaIcon.js',
  SubtitleIcon: './SubtitleIcon.js',
  Suit01Icon: './Suit01Icon.js',
  Suit02Icon: './Suit02Icon.js',
  SujoodIcon: './SujoodIcon.js',
  Summation01Icon: './Summation01Icon.js',
  Summation02Icon: './Summation02Icon.js',
  SummationCircleIcon: './SummationCircleIcon.js',
  SummationSquareIcon: './SummationSquareIcon.js',
  Sun01Icon: './Sun01Icon.js',
  Sun02Icon: './Sun02Icon.js',
  Sun03Icon: './Sun03Icon.js',
  SunCloud01Icon: './SunCloud01Icon.js',
  SunCloud02Icon: './SunCloud02Icon.js',
  SunCloudAngledRain01Icon: './SunCloudAngledRain01Icon.js',
  SunCloudAngledRain02Icon: './SunCloudAngledRain02Icon.js',
  SunCloudAngledRainZap01Icon: './SunCloudAngledRainZap01Icon.js',
  SunCloudAngledRainZap02Icon: './SunCloudAngledRainZap02Icon.js',
  SunCloudAngledZap01Icon: './SunCloudAngledZap01Icon.js',
  SunCloudAngledZap02Icon: './SunCloudAngledZap02Icon.js',
  SunCloudBigRain01Icon: './SunCloudBigRain01Icon.js',
  SunCloudBigRain02Icon: './SunCloudBigRain02Icon.js',
  SunCloudFastWind01Icon: './SunCloudFastWind01Icon.js',
  SunCloudFastWind02Icon: './SunCloudFastWind02Icon.js',
  SunCloudHailstone01Icon: './SunCloudHailstone01Icon.js',
  SunCloudHailstone02Icon: './SunCloudHailstone02Icon.js',
  SunCloudLittleRain01Icon: './SunCloudLittleRain01Icon.js',
  SunCloudLittleRain02Icon: './SunCloudLittleRain02Icon.js',
  SunCloudLittleSnow01Icon: './SunCloudLittleSnow01Icon.js',
  SunCloudLittleSnow02Icon: './SunCloudLittleSnow02Icon.js',
  SunCloudMidRain01Icon: './SunCloudMidRain01Icon.js',
  SunCloudMidRain02Icon: './SunCloudMidRain02Icon.js',
  SunCloudMidSnow01Icon: './SunCloudMidSnow01Icon.js',
  SunCloudMidSnow02Icon: './SunCloudMidSnow02Icon.js',
  SunCloudSlowWind01Icon: './SunCloudSlowWind01Icon.js',
  SunCloudSlowWind02Icon: './SunCloudSlowWind02Icon.js',
  SunCloudSnow01Icon: './SunCloudSnow01Icon.js',
  SunCloudSnow02Icon: './SunCloudSnow02Icon.js',
  SunglassesIcon: './SunglassesIcon.js',
  SunriseIcon: './SunriseIcon.js',
  SunsetIcon: './SunsetIcon.js',
  SuperMarioIcon: './SuperMarioIcon.js',
  SuperMarioToadIcon: './SuperMarioToadIcon.js',
  SurfboardIcon: './SurfboardIcon.js',
  SurpriseIcon: './SurpriseIcon.js',
  Sushi01Icon: './Sushi01Icon.js',
  Sushi02Icon: './Sushi02Icon.js',
  Sushi03Icon: './Sushi03Icon.js',
  SuspiciousIcon: './SuspiciousIcon.js',
  SustainableEnergyIcon: './SustainableEnergyIcon.js',
  Svg01Icon: './Svg01Icon.js',
  Svg02Icon: './Svg02Icon.js',
  SwarmIcon: './SwarmIcon.js',
  SwatchIcon: './SwatchIcon.js',
  SwimmingCapIcon: './SwimmingCapIcon.js',
  SwimmingIcon: './SwimmingIcon.js',
  SwipeDown01Icon: './SwipeDown01Icon.js',
  SwipeDown02Icon: './SwipeDown02Icon.js',
  SwipeDown03Icon: './SwipeDown03Icon.js',
  SwipeDown04Icon: './SwipeDown04Icon.js',
  SwipeDown05Icon: './SwipeDown05Icon.js',
  SwipeDown06Icon: './SwipeDown06Icon.js',
  SwipeDown07Icon: './SwipeDown07Icon.js',
  SwipeDown08Icon: './SwipeDown08Icon.js',
  SwipeLeft01Icon: './SwipeLeft01Icon.js',
  SwipeLeft02Icon: './SwipeLeft02Icon.js',
  SwipeLeft03Icon: './SwipeLeft03Icon.js',
  SwipeLeft04Icon: './SwipeLeft04Icon.js',
  SwipeLeft05Icon: './SwipeLeft05Icon.js',
  SwipeLeft06Icon: './SwipeLeft06Icon.js',
  SwipeLeft07Icon: './SwipeLeft07Icon.js',
  SwipeLeft08Icon: './SwipeLeft08Icon.js',
  SwipeLeft09Icon: './SwipeLeft09Icon.js',
  SwipeRight01Icon: './SwipeRight01Icon.js',
  SwipeRight02Icon: './SwipeRight02Icon.js',
  SwipeRight03Icon: './SwipeRight03Icon.js',
  SwipeRight04Icon: './SwipeRight04Icon.js',
  SwipeRight05Icon: './SwipeRight05Icon.js',
  SwipeRight06Icon: './SwipeRight06Icon.js',
  SwipeRight07Icon: './SwipeRight07Icon.js',
  SwipeRight08Icon: './SwipeRight08Icon.js',
  SwipeRight09Icon: './SwipeRight09Icon.js',
  SwipeUp01Icon: './SwipeUp01Icon.js',
  SwipeUp02Icon: './SwipeUp02Icon.js',
  SwipeUp03Icon: './SwipeUp03Icon.js',
  SwipeUp04Icon: './SwipeUp04Icon.js',
  SwipeUp05Icon: './SwipeUp05Icon.js',
  SwipeUp06Icon: './SwipeUp06Icon.js',
  SwipeUp07Icon: './SwipeUp07Icon.js',
  SwipeUp08Icon: './SwipeUp08Icon.js',
  SwissFrancIcon: './SwissFrancIcon.js',
  SwitchCameraIcon: './SwitchCameraIcon.js',
  Sword01Icon: './Sword01Icon.js',
  Sword02Icon: './Sword02Icon.js',
  Sword03Icon: './Sword03Icon.js',
  SystemUpdate01Icon: './SystemUpdate01Icon.js',
  SystemUpdate02Icon: './SystemUpdate02Icon.js',
  TShirtIcon: './TShirtIcon.js',
  Table01Icon: './Table01Icon.js',
  Table02Icon: './Table02Icon.js',
  Table03Icon: './Table03Icon.js',
  TableColumnsSplitIcon: './TableColumnsSplitIcon.js',
  TableLamp01Icon: './TableLamp01Icon.js',
  TableLamp02Icon: './TableLamp02Icon.js',
  TableRoundIcon: './TableRoundIcon.js',
  TableRowsSplitIcon: './TableRowsSplitIcon.js',
  TableIcon: './TableIcon.js',
  TableTennisBatIcon: './TableTennisBatIcon.js',
  Tablet01Icon: './Tablet01Icon.js',
  Tablet02Icon: './Tablet02Icon.js',
  TabletConnectedBluetoothIcon: './TabletConnectedBluetoothIcon.js',
  TabletConnectedUsbIcon: './TabletConnectedUsbIcon.js',
  TabletConnectedWifiIcon: './TabletConnectedWifiIcon.js',
  TabletPenIcon: './TabletPenIcon.js',
  TabletsIcon: './TabletsIcon.js',
  Taco01Icon: './Taco01Icon.js',
  Taco02Icon: './Taco02Icon.js',
  Tag01Icon: './Tag01Icon.js',
  Tag02Icon: './Tag02Icon.js',
  TagsIcon: './TagsIcon.js',
  TailwindcssIcon: './TailwindcssIcon.js',
  TajMahalIcon: './TajMahalIcon.js',
  TallyOneIcon: './TallyOneIcon.js',
  TallyTwoIcon: './TallyTwoIcon.js',
  TallyThreeIcon: './TallyThreeIcon.js',
  TallyFourIcon: './TallyFourIcon.js',
  TallyFiveIcon: './TallyFiveIcon.js',
  TanIcon: './TanIcon.js',
  TangentIcon: './TangentIcon.js',
  TankTopIcon: './TankTopIcon.js',
  TankerTruckIcon: './TankerTruckIcon.js',
  Tap01Icon: './Tap01Icon.js',
  Tap02Icon: './Tap02Icon.js',
  Tap03Icon: './Tap03Icon.js',
  Tap04Icon: './Tap04Icon.js',
  Tap05Icon: './Tap05Icon.js',
  Tap06Icon: './Tap06Icon.js',
  Tap07Icon: './Tap07Icon.js',
  Tap08Icon: './Tap08Icon.js',
  TapeMeasureIcon: './TapeMeasureIcon.js',
  Target01Icon: './Target01Icon.js',
  Target02Icon: './Target02Icon.js',
  Target03Icon: './Target03Icon.js',
  TargetDollarIcon: './TargetDollarIcon.js',
  TasbihIcon: './TasbihIcon.js',
  Task01Icon: './Task01Icon.js',
  Task02Icon: './Task02Icon.js',
  TaskAdd01Icon: './TaskAdd01Icon.js',
  TaskAdd02Icon: './TaskAdd02Icon.js',
  TaskDaily01Icon: './TaskDaily01Icon.js',
  TaskDaily02Icon: './TaskDaily02Icon.js',
  TaskDone01Icon: './TaskDone01Icon.js',
  TaskDone02Icon: './TaskDone02Icon.js',
  TaskEdit01Icon: './TaskEdit01Icon.js',
  TaskEdit02Icon: './TaskEdit02Icon.js',
  TaskRemove01Icon: './TaskRemove01Icon.js',
  TaskRemove02Icon: './TaskRemove02Icon.js',
  TaxesIcon: './TaxesIcon.js',
  Taxi02Icon: './Taxi02Icon.js',
  TaxiIcon: './TaxiIcon.js',
  TeaPodIcon: './TeaPodIcon.js',
  TeaIcon: './TeaIcon.js',
  TeacherIcon: './TeacherIcon.js',
  TeachingIcon: './TeachingIcon.js',
  TeamWorkIcon: './TeamWorkIcon.js',
  TeamviewerIcon: './TeamviewerIcon.js',
  TelegramIcon: './TelegramIcon.js',
  TelephoneIcon: './TelephoneIcon.js',
  Telescope01Icon: './Telescope01Icon.js',
  Telescope02Icon: './Telescope02Icon.js',
  TelevisionTableIcon: './TelevisionTableIcon.js',
  TemperatureIcon: './TemperatureIcon.js',
  TennisBallIcon: './TennisBallIcon.js',
  TennisRacketIcon: './TennisRacketIcon.js',
  TentIcon: './TentIcon.js',
  TentTreeIcon: './TentTreeIcon.js',
  TerminalIcon: './TerminalIcon.js',
  TerraceIcon: './TerraceIcon.js',
  TestTube01Icon: './TestTube01Icon.js',
  TestTube02Icon: './TestTube02Icon.js',
  TestTube03Icon: './TestTube03Icon.js',
  TestTubeDiagonalIcon: './TestTubeDiagonalIcon.js',
  TestTubeIcon: './TestTubeIcon.js',
  TestTubesIcon: './TestTubesIcon.js',
  TetrisIcon: './TetrisIcon.js',
  TextAlignCenterIcon: './TextAlignCenterIcon.js',
  TextAlignJustifyCenterIcon: './TextAlignJustifyCenterIcon.js',
  TextAlignJustifyLeftIcon: './TextAlignJustifyLeftIcon.js',
  TextAlignJustifyRightIcon: './TextAlignJustifyRightIcon.js',
  TextAlignLeft01Icon: './TextAlignLeft01Icon.js',
  TextAlignLeftIcon: './TextAlignLeftIcon.js',
  TextAlignRight01Icon: './TextAlignRight01Icon.js',
  TextAlignRightIcon: './TextAlignRightIcon.js',
  TextAllCapsIcon: './TextAllCapsIcon.js',
  TextBoldIcon: './TextBoldIcon.js',
  TextCenterlineCenterTopIcon: './TextCenterlineCenterTopIcon.js',
  TextCenterlineLeftIcon: './TextCenterlineLeftIcon.js',
  TextCenterlineMiddleIcon: './TextCenterlineMiddleIcon.js',
  TextCenterlineRightIcon: './TextCenterlineRightIcon.js',
  TextCheckIcon: './TextCheckIcon.js',
  TextCircleIcon: './TextCircleIcon.js',
  TextClearIcon: './TextClearIcon.js',
  TextColorIcon: './TextColorIcon.js',
  TextCreationIcon: './TextCreationIcon.js',
  TextFirstlineLeftIcon: './TextFirstlineLeftIcon.js',
  TextFirstlineRightIcon: './TextFirstlineRightIcon.js',
  TextFontIcon: './TextFontIcon.js',
  TextFootnoteIcon: './TextFootnoteIcon.js',
  TextIndent01Icon: './TextIndent01Icon.js',
  TextIndentLessIcon: './TextIndentLessIcon.js',
  TextIndentMoreIcon: './TextIndentMoreIcon.js',
  TextIndentIcon: './TextIndentIcon.js',
  TextItalicSlashIcon: './TextItalicSlashIcon.js',
  TextItalicIcon: './TextItalicIcon.js',
  TextKerningIcon: './TextKerningIcon.js',
  TextNumberSignIcon: './TextNumberSignIcon.js',
  TextSelectIcon: './TextSelectIcon.js',
  TextSelectionIcon: './TextSelectionIcon.js',
  TextSmallcapsIcon: './TextSmallcapsIcon.js',
  TextSquareIcon: './TextSquareIcon.js',
  TextStrikethroughIcon: './TextStrikethroughIcon.js',
  TextIcon: './TextIcon.js',
  TextSubscriptIcon: './TextSubscriptIcon.js',
  TextSuperscriptIcon: './TextSuperscriptIcon.js',
  TextTrackingIcon: './TextTrackingIcon.js',
  TextUnderlineIcon: './TextUnderlineIcon.js',
  TextVariableFrontIcon: './TextVariableFrontIcon.js',
  TextVerticalAlignmentIcon: './TextVerticalAlignmentIcon.js',
  TextWrapIcon: './TextWrapIcon.js',
  TheProphetsMosqueIcon: './TheProphetsMosqueIcon.js',
  TheaterIcon: './TheaterIcon.js',
  ThermometerColdIcon: './ThermometerColdIcon.js',
  ThermometerIcon: './ThermometerIcon.js',
  ThermometerWarmIcon: './ThermometerWarmIcon.js',
  ThreadIcon: './ThreadIcon.js',
  ThreadsEllipseIcon: './ThreadsEllipseIcon.js',
  ThreadsRectangleIcon: './ThreadsRectangleIcon.js',
  ThreadsIcon: './ThreadsIcon.js',
  ThreeCircleIcon: './ThreeCircleIcon.js',
  ThreeFinger01Icon: './ThreeFinger01Icon.js',
  ThreeFinger02Icon: './ThreeFinger02Icon.js',
  ThreeFinger03Icon: './ThreeFinger03Icon.js',
  ThreeFinger04Icon: './ThreeFinger04Icon.js',
  ThreeFinger05Icon: './ThreeFinger05Icon.js',
  ThreeSquareIcon: './ThreeSquareIcon.js',
  ThumbsDownEllipseIcon: './ThumbsDownEllipseIcon.js',
  ThumbsDownRectangleIcon: './ThumbsDownRectangleIcon.js',
  ThumbsDownIcon: './ThumbsDownIcon.js',
  ThumbsUpDownIcon: './ThumbsUpDownIcon.js',
  ThumbsUpEllipseIcon: './ThumbsUpEllipseIcon.js',
  ThumbsUpRectangleIcon: './ThumbsUpRectangleIcon.js',
  ThumbsUpIcon: './ThumbsUpIcon.js',
  TicTacToeIcon: './TicTacToeIcon.js',
  Tick01Icon: './Tick01Icon.js',
  Tick02Icon: './Tick02Icon.js',
  Tick03Icon: './Tick03Icon.js',
  Tick04Icon: './Tick04Icon.js',
  TickDouble01Icon: './TickDouble01Icon.js',
  TickDouble02Icon: './TickDouble02Icon.js',
  TickDouble03Icon: './TickDouble03Icon.js',
  TickDouble04Icon: './TickDouble04Icon.js',
  Ticket01Icon: './Ticket01Icon.js',
  Ticket02Icon: './Ticket02Icon.js',
  Ticket03Icon: './Ticket03Icon.js',
  TicketStarIcon: './TicketStarIcon.js',
  TieIcon: './TieIcon.js',
  Tiff01Icon: './Tiff01Icon.js',
  Tiff02Icon: './Tiff02Icon.js',
  TiktokIcon: './TiktokIcon.js',
  TiltifyIcon: './TiltifyIcon.js',
  Time01Icon: './Time01Icon.js',
  Time02Icon: './Time02Icon.js',
  Time03Icon: './Time03Icon.js',
  Time04Icon: './Time04Icon.js',
  TimeHalfPassIcon: './TimeHalfPassIcon.js',
  TimeManagementCircleIcon: './TimeManagementCircleIcon.js',
  TimeManagementIcon: './TimeManagementIcon.js',
  TimeQuarter02Icon: './TimeQuarter02Icon.js',
  TimeQuarterPassIcon: './TimeQuarterPassIcon.js',
  TimeQuarterIcon: './TimeQuarterIcon.js',
  TimeScheduleIcon: './TimeScheduleIcon.js',
  TimeSetting01Icon: './TimeSetting01Icon.js',
  TimeSetting02Icon: './TimeSetting02Icon.js',
  TimeSetting03Icon: './TimeSetting03Icon.js',
  TimeZoneIcon: './TimeZoneIcon.js',
  TimelineEventIcon: './TimelineEventIcon.js',
  TimelineListIcon: './TimelineListIcon.js',
  TimelineIcon: './TimelineIcon.js',
  Timer01Icon: './Timer01Icon.js',
  Timer02Icon: './Timer02Icon.js',
  TipsIcon: './TipsIcon.js',
  TireIcon: './TireIcon.js',
  Tired01Icon: './Tired01Icon.js',
  Tired02Icon: './Tired02Icon.js',
  TissuePaperIcon: './TissuePaperIcon.js',
  ToggleOffIcon: './ToggleOffIcon.js',
  ToggleOnIcon: './ToggleOnIcon.js',
  Toilet01Icon: './Toilet01Icon.js',
  Toilet02Icon: './Toilet02Icon.js',
  TokenCircleIcon: './TokenCircleIcon.js',
  TokenSquareIcon: './TokenSquareIcon.js',
  Tongue01Icon: './Tongue01Icon.js',
  TongueIcon: './TongueIcon.js',
  TongueWinkLeftIcon: './TongueWinkLeftIcon.js',
  TongueWinkRightIcon: './TongueWinkRightIcon.js',
  ToolCaseIcon: './ToolCaseIcon.js',
  ToolboxIcon: './ToolboxIcon.js',
  ToolsIcon: './ToolsIcon.js',
  Tornado01Icon: './Tornado01Icon.js',
  Tornado02Icon: './Tornado02Icon.js',
  TorriGateIcon: './TorriGateIcon.js',
  TorusIcon: './TorusIcon.js',
  Touch01Icon: './Touch01Icon.js',
  Touch02Icon: './Touch02Icon.js',
  Touch03Icon: './Touch03Icon.js',
  Touch04Icon: './Touch04Icon.js',
  Touch05Icon: './Touch05Icon.js',
  Touch06Icon: './Touch06Icon.js',
  Touch07Icon: './Touch07Icon.js',
  Touch08Icon: './Touch08Icon.js',
  Touch09Icon: './Touch09Icon.js',
  Touch10Icon: './Touch10Icon.js',
  TouchInteraction01Icon: './TouchInteraction01Icon.js',
  TouchInteraction02Icon: './TouchInteraction02Icon.js',
  TouchInteraction03Icon: './TouchInteraction03Icon.js',
  TouchInteraction04Icon: './TouchInteraction04Icon.js',
  TouchLocked01Icon: './TouchLocked01Icon.js',
  TouchLocked02Icon: './TouchLocked02Icon.js',
  TouchLocked03Icon: './TouchLocked03Icon.js',
  TouchLocked04Icon: './TouchLocked04Icon.js',
  Touchpad01Icon: './Touchpad01Icon.js',
  Touchpad02Icon: './Touchpad02Icon.js',
  Touchpad03Icon: './Touchpad03Icon.js',
  Touchpad04Icon: './Touchpad04Icon.js',
  TouchpadOffIcon: './TouchpadOffIcon.js',
  TowTruckIcon: './TowTruckIcon.js',
  TowelsIcon: './TowelsIcon.js',
  ToyBrickIcon: './ToyBrickIcon.js',
  ToyTrainIcon: './ToyTrainIcon.js',
  TractorIcon: './TractorIcon.js',
  TradeDownIcon: './TradeDownIcon.js',
  TradeMarkIcon: './TradeMarkIcon.js',
  TradeUpIcon: './TradeUpIcon.js',
  TrafficIncidentIcon: './TrafficIncidentIcon.js',
  TrafficJam01Icon: './TrafficJam01Icon.js',
  TrafficJam02Icon: './TrafficJam02Icon.js',
  TrafficLightIcon: './TrafficLightIcon.js',
  Train01Icon: './Train01Icon.js',
  Train02Icon: './Train02Icon.js',
  TrainTrackIcon: './TrainTrackIcon.js',
  TramIcon: './TramIcon.js',
  TrampolineIcon: './TrampolineIcon.js',
  TransactionHistoryIcon: './TransactionHistoryIcon.js',
  TransactionIcon: './TransactionIcon.js',
  TransitionBottomIcon: './TransitionBottomIcon.js',
  TransitionLeftIcon: './TransitionLeftIcon.js',
  TransitionRightIcon: './TransitionRightIcon.js',
  TransitionTopIcon: './TransitionTopIcon.js',
  TranslateIcon: './TranslateIcon.js',
  TranslationIcon: './TranslationIcon.js',
  TransmissionIcon: './TransmissionIcon.js',
  TransparencyIcon: './TransparencyIcon.js',
  TrapezoidLineHorizontalIcon: './TrapezoidLineHorizontalIcon.js',
  TrapezoidLineVerticalIcon: './TrapezoidLineVerticalIcon.js',
  TravelBagIcon: './TravelBagIcon.js',
  Treadmill01Icon: './Treadmill01Icon.js',
  Treadmill02Icon: './Treadmill02Icon.js',
  TreatmentIcon: './TreatmentIcon.js',
  Tree01Icon: './Tree01Icon.js',
  Tree02Icon: './Tree02Icon.js',
  Tree03Icon: './Tree03Icon.js',
  Tree04Icon: './Tree04Icon.js',
  Tree05Icon: './Tree05Icon.js',
  Tree06Icon: './Tree06Icon.js',
  Tree07Icon: './Tree07Icon.js',
  TreePalmIcon: './TreePalmIcon.js',
  TreesIcon: './TreesIcon.js',
  TrelloIcon: './TrelloIcon.js',
  TrendingUpDownIcon: './TrendingUpDownIcon.js',
  Triangle01Icon: './Triangle01Icon.js',
  Triangle02Icon: './Triangle02Icon.js',
  Triangle03Icon: './Triangle03Icon.js',
  TriangleDashIcon: './TriangleDashIcon.js',
  TriangleRightIcon: './TriangleRightIcon.js',
  TriangleIcon: './TriangleIcon.js',
  Trolley01Icon: './Trolley01Icon.js',
  Trolley02Icon: './Trolley02Icon.js',
  TropicalStormIcon: './TropicalStormIcon.js',
  TropicalStormTracks01Icon: './TropicalStormTracks01Icon.js',
  TropicalStormTracks02Icon: './TropicalStormTracks02Icon.js',
  TruckDeliveryIcon: './TruckDeliveryIcon.js',
  TruckMonsterIcon: './TruckMonsterIcon.js',
  TruckReturnIcon: './TruckReturnIcon.js',
  TruckIcon: './TruckIcon.js',
  TrulliIcon: './TrulliIcon.js',
  TsunamiIcon: './TsunamiIcon.js',
  TulipIcon: './TulipIcon.js',
  TumblrIcon: './TumblrIcon.js',
  TurkishLiraIcon: './TurkishLiraIcon.js',
  TurtleNeckIcon: './TurtleNeckIcon.js',
  Tv01Icon: './Tv01Icon.js',
  Tv02Icon: './Tv02Icon.js',
  TvFixIcon: './TvFixIcon.js',
  TvIssueIcon: './TvIssueIcon.js',
  TvSmartIcon: './TvSmartIcon.js',
  TwinTowerIcon: './TwinTowerIcon.js',
  TwitchIcon: './TwitchIcon.js',
  TwitterSquareIcon: './TwitterSquareIcon.js',
  TwitterIcon: './TwitterIcon.js',
  TwoCircleIcon: './TwoCircleIcon.js',
  TwoFactorAccessIcon: './TwoFactorAccessIcon.js',
  TwoFinger01Icon: './TwoFinger01Icon.js',
  TwoFinger02Icon: './TwoFinger02Icon.js',
  TwoFinger03Icon: './TwoFinger03Icon.js',
  TwoFinger04Icon: './TwoFinger04Icon.js',
  TwoFinger05Icon: './TwoFinger05Icon.js',
  TwoSquareIcon: './TwoSquareIcon.js',
  Txt01Icon: './Txt01Icon.js',
  Txt02Icon: './Txt02Icon.js',
  TypeCursorIcon: './TypeCursorIcon.js',
  Typescript01Icon: './Typescript01Icon.js',
  Typescript02Icon: './Typescript02Icon.js',
  Typescript03Icon: './Typescript03Icon.js',
  UberIcon: './UberIcon.js',
  Ufo01Icon: './Ufo01Icon.js',
  UfoIcon: './UfoIcon.js',
  UmbrellaDollarIcon: './UmbrellaDollarIcon.js',
  UmbrellaOffIcon: './UmbrellaOffIcon.js',
  UmbrellaIcon: './UmbrellaIcon.js',
  UnamusedIcon: './UnamusedIcon.js',
  Unarchive03Icon: './Unarchive03Icon.js',
  UnavailableIcon: './UnavailableIcon.js',
  Underpants01Icon: './Underpants01Icon.js',
  Underpants02Icon: './Underpants02Icon.js',
  Underpants03Icon: './Underpants03Icon.js',
  Undo02Icon: './Undo02Icon.js',
  Undo03Icon: './Undo03Icon.js',
  UndoIcon: './UndoIcon.js',
  UnfoldLessIcon: './UnfoldLessIcon.js',
  UnfoldMoreDownIcon: './UnfoldMoreDownIcon.js',
  UnfoldMoreIcon: './UnfoldMoreIcon.js',
  UnfoldMoreUpIcon: './UnfoldMoreUpIcon.js',
  UngroupItemsIcon: './UngroupItemsIcon.js',
  UngroupLayersIcon: './UngroupLayersIcon.js',
  UngroupIcon: './UngroupIcon.js',
  UnhappyIcon: './UnhappyIcon.js',
  UniversalAccessCircleIcon: './UniversalAccessCircleIcon.js',
  UniversalAccessIcon: './UniversalAccessIcon.js',
  UniversityIcon: './UniversityIcon.js',
  Unlink01Icon: './Unlink01Icon.js',
  Unlink02Icon: './Unlink02Icon.js',
  Unlink03Icon: './Unlink03Icon.js',
  Unlink04Icon: './Unlink04Icon.js',
  Unlink05Icon: './Unlink05Icon.js',
  Unlink06Icon: './Unlink06Icon.js',
  UnsplashIcon: './UnsplashIcon.js',
  Upload01Icon: './Upload01Icon.js',
  Upload02Icon: './Upload02Icon.js',
  Upload03Icon: './Upload03Icon.js',
  Upload04Icon: './Upload04Icon.js',
  Upload05Icon: './Upload05Icon.js',
  Upload06Icon: './Upload06Icon.js',
  UploadCircle01Icon: './UploadCircle01Icon.js',
  UploadCircle02Icon: './UploadCircle02Icon.js',
  UploadSquare01Icon: './UploadSquare01Icon.js',
  UploadSquare02Icon: './UploadSquare02Icon.js',
  UpworkIcon: './UpworkIcon.js',
  UsbBugsIcon: './UsbBugsIcon.js',
  UsbConnected01Icon: './UsbConnected01Icon.js',
  UsbConnected02Icon: './UsbConnected02Icon.js',
  UsbConnected03Icon: './UsbConnected03Icon.js',
  UsbErrorIcon: './UsbErrorIcon.js',
  UsbMemory01Icon: './UsbMemory01Icon.js',
  UsbMemory02Icon: './UsbMemory02Icon.js',
  UsbNotConnected01Icon: './UsbNotConnected01Icon.js',
  UsbNotConnected02Icon: './UsbNotConnected02Icon.js',
  UsbIcon: './UsbIcon.js',
  UsdtIcon: './UsdtIcon.js',
  User02Icon: './User02Icon.js',
  User03Icon: './User03Icon.js',
  UserAccountIcon: './UserAccountIcon.js',
  UserAdd01Icon: './UserAdd01Icon.js',
  UserAdd02Icon: './UserAdd02Icon.js',
  UserAiIcon: './UserAiIcon.js',
  UserArrowLeftRightIcon: './UserArrowLeftRightIcon.js',
  UserBlock01Icon: './UserBlock01Icon.js',
  UserBlock02Icon: './UserBlock02Icon.js',
  UserCheck01Icon: './UserCheck01Icon.js',
  UserCheck02Icon: './UserCheck02Icon.js',
  UserCircle02Icon: './UserCircle02Icon.js',
  UserCircleIcon: './UserCircleIcon.js',
  UserDollarIcon: './UserDollarIcon.js',
  UserEdit01Icon: './UserEdit01Icon.js',
  UserFullViewIcon: './UserFullViewIcon.js',
  UserGroup02Icon: './UserGroup02Icon.js',
  UserGroup03Icon: './UserGroup03Icon.js',
  UserGroupIcon: './UserGroupIcon.js',
  UserIdVerificationIcon: './UserIdVerificationIcon.js',
  UserListIcon: './UserListIcon.js',
  UserLock01Icon: './UserLock01Icon.js',
  UserLock02Icon: './UserLock02Icon.js',
  UserLove01Icon: './UserLove01Icon.js',
  UserLove02Icon: './UserLove02Icon.js',
  UserMinus01Icon: './UserMinus01Icon.js',
  UserMinus02Icon: './UserMinus02Icon.js',
  UserMultiple02Icon: './UserMultiple02Icon.js',
  UserMultiple03Icon: './UserMultiple03Icon.js',
  UserMultipleIcon: './UserMultipleIcon.js',
  UserQuestion01Icon: './UserQuestion01Icon.js',
  UserQuestion02Icon: './UserQuestion02Icon.js',
  UserRemove01Icon: './UserRemove01Icon.js',
  UserRemove02Icon: './UserRemove02Icon.js',
  UserRoadsideIcon: './UserRoadsideIcon.js',
  UserSearch01Icon: './UserSearch01Icon.js',
  UserSearch02Icon: './UserSearch02Icon.js',
  UserSettings01Icon: './UserSettings01Icon.js',
  UserSettings02Icon: './UserSettings02Icon.js',
  UserSharingIcon: './UserSharingIcon.js',
  UserShield01Icon: './UserShield01Icon.js',
  UserShield02Icon: './UserShield02Icon.js',
  UserSquareIcon: './UserSquareIcon.js',
  UserStar01Icon: './UserStar01Icon.js',
  UserStar02Icon: './UserStar02Icon.js',
  UserStatusIcon: './UserStatusIcon.js',
  UserStoryIcon: './UserStoryIcon.js',
  UserIcon: './UserIcon.js',
  UserSwitchIcon: './UserSwitchIcon.js',
  UserTime01Icon: './UserTime01Icon.js',
  UserTime02Icon: './UserTime02Icon.js',
  UserTime03Icon: './UserTime03Icon.js',
  UserUnlock01Icon: './UserUnlock01Icon.js',
  UserUnlock02Icon: './UserUnlock02Icon.js',
  UserWarning01Icon: './UserWarning01Icon.js',
  UserWarning02Icon: './UserWarning02Icon.js',
  UserWarning03Icon: './UserWarning03Icon.js',
  UtilityPoleIcon: './UtilityPoleIcon.js',
  Uv01Icon: './Uv01Icon.js',
  Uv02Icon: './Uv02Icon.js',
  Uv03Icon: './Uv03Icon.js',
  VaccineIcon: './VaccineIcon.js',
  VacuumCleanerIcon: './VacuumCleanerIcon.js',
  ValidationApprovalIcon: './ValidationApprovalIcon.js',
  ValidationIcon: './ValidationIcon.js',
  VanIcon: './VanIcon.js',
  VariableIcon: './VariableIcon.js',
  VectorSquareIcon: './VectorSquareIcon.js',
  VegetarianFoodIcon: './VegetarianFoodIcon.js',
  VenetianMaskIcon: './VenetianMaskIcon.js',
  VersusIcon: './VersusIcon.js',
  VerticalResizeIcon: './VerticalResizeIcon.js',
  VerticalScrollPointIcon: './VerticalScrollPointIcon.js',
  VestIcon: './VestIcon.js',
  ViberIcon: './ViberIcon.js',
  VibrateIcon: './VibrateIcon.js',
  VictoryFinger01Icon: './VictoryFinger01Icon.js',
  VictoryFinger02Icon: './VictoryFinger02Icon.js',
  VictoryFinger03Icon: './VictoryFinger03Icon.js',
  Video01Icon: './Video01Icon.js',
  Video02Icon: './Video02Icon.js',
  VideoAiIcon: './VideoAiIcon.js',
  VideoCameraAiIcon: './VideoCameraAiIcon.js',
  VideoConsoleIcon: './VideoConsoleIcon.js',
  VideoOffIcon: './VideoOffIcon.js',
  VideoReplayIcon: './VideoReplayIcon.js',
  VideotapeIcon: './VideotapeIcon.js',
  ViewAgendaIcon: './ViewAgendaIcon.js',
  ViewOffSlashIcon: './ViewOffSlashIcon.js',
  ViewOffIcon: './ViewOffIcon.js',
  ViewSidebarLeftIcon: './ViewSidebarLeftIcon.js',
  ViewSidebarRightIcon: './ViewSidebarRightIcon.js',
  ViewIcon: './ViewIcon.js',
  Villa01Icon: './Villa01Icon.js',
  Villa02Icon: './Villa02Icon.js',
  VimeoIcon: './VimeoIcon.js',
  VineSquareIcon: './VineSquareIcon.js',
  VineIcon: './VineIcon.js',
  VintageClockIcon: './VintageClockIcon.js',
  VirtualRealityVr01Icon: './VirtualRealityVr01Icon.js',
  VirtualRealityVr02Icon: './VirtualRealityVr02Icon.js',
  VisionIcon: './VisionIcon.js',
  VisualStudioCodeIcon: './VisualStudioCodeIcon.js',
  VkSquareIcon: './VkSquareIcon.js',
  VkIcon: './VkIcon.js',
  VoiceIdIcon: './VoiceIdIcon.js',
  VoiceIcon: './VoiceIcon.js',
  VolleyballIcon: './VolleyballIcon.js',
  VolumeHighIcon: './VolumeHighIcon.js',
  VolumeLowIcon: './VolumeLowIcon.js',
  VolumeMinusIcon: './VolumeMinusIcon.js',
  VolumeMute01Icon: './VolumeMute01Icon.js',
  VolumeMute02Icon: './VolumeMute02Icon.js',
  VolumeOffIcon: './VolumeOffIcon.js',
  VolumeUpIcon: './VolumeUpIcon.js',
  VomitingIcon: './VomitingIcon.js',
  VoucherIcon: './VoucherIcon.js',
  VrGlassesIcon: './VrGlassesIcon.js',
  VrIcon: './VrIcon.js',
  Vynil01Icon: './Vynil01Icon.js',
  Vynil02Icon: './Vynil02Icon.js',
  Vynil03Icon: './Vynil03Icon.js',
  WThreeSchoolsIcon: './WThreeSchoolsIcon.js',
  WaiterIcon: './WaiterIcon.js',
  WaitersIcon: './WaitersIcon.js',
  WalkingIcon: './WalkingIcon.js',
  WallLampIcon: './WallLampIcon.js',
  Wallet01Icon: './Wallet01Icon.js',
  Wallet02Icon: './Wallet02Icon.js',
  Wallet03Icon: './Wallet03Icon.js',
  Wallet04Icon: './Wallet04Icon.js',
  Wallet05Icon: './Wallet05Icon.js',
  WalletAdd01Icon: './WalletAdd01Icon.js',
  WalletAdd02Icon: './WalletAdd02Icon.js',
  WalletCardsIcon: './WalletCardsIcon.js',
  WalletDone01Icon: './WalletDone01Icon.js',
  WalletDone02Icon: './WalletDone02Icon.js',
  WalletNotFound01Icon: './WalletNotFound01Icon.js',
  WalletNotFound02Icon: './WalletNotFound02Icon.js',
  WalletRemove01Icon: './WalletRemove01Icon.js',
  WalletRemove02Icon: './WalletRemove02Icon.js',
  WalpaperIcon: './WalpaperIcon.js',
  WantedIcon: './WantedIcon.js',
  Wardrobe01Icon: './Wardrobe01Icon.js',
  Wardrobe02Icon: './Wardrobe02Icon.js',
  Wardrobe03Icon: './Wardrobe03Icon.js',
  Wardrobe04Icon: './Wardrobe04Icon.js',
  WarehouseIcon: './WarehouseIcon.js',
  WashingMachineIcon: './WashingMachineIcon.js',
  WashingtonMonumentIcon: './WashingtonMonumentIcon.js',
  WasteRestoreIcon: './WasteRestoreIcon.js',
  WasteIcon: './WasteIcon.js',
  Watch01Icon: './Watch01Icon.js',
  Watch02Icon: './Watch02Icon.js',
  WaterEnergyIcon: './WaterEnergyIcon.js',
  WaterPoloIcon: './WaterPoloIcon.js',
  WaterPumpIcon: './WaterPumpIcon.js',
  WaterfallDown01Icon: './WaterfallDown01Icon.js',
  WaterfallDown03Icon: './WaterfallDown03Icon.js',
  WaterfallUp01Icon: './WaterfallUp01Icon.js',
  WaterfallUp02Icon: './WaterfallUp02Icon.js',
  WatermelonIcon: './WatermelonIcon.js',
  WattpadSquareIcon: './WattpadSquareIcon.js',
  WattpadIcon: './WattpadIcon.js',
  Wav01Icon: './Wav01Icon.js',
  Wav02Icon: './Wav02Icon.js',
  WaveSquareIcon: './WaveSquareIcon.js',
  WaveIcon: './WaveIcon.js',
  WaveTriangleIcon: './WaveTriangleIcon.js',
  WavingHand01Icon: './WavingHand01Icon.js',
  WavingHand02Icon: './WavingHand02Icon.js',
  WazeIcon: './WazeIcon.js',
  WebDesign01Icon: './WebDesign01Icon.js',
  WebDesign02Icon: './WebDesign02Icon.js',
  WebProgrammingIcon: './WebProgrammingIcon.js',
  WebProtectionIcon: './WebProtectionIcon.js',
  WebSecurityIcon: './WebSecurityIcon.js',
  WebValidationIcon: './WebValidationIcon.js',
  WebflowEllipseIcon: './WebflowEllipseIcon.js',
  WebflowRectangleIcon: './WebflowRectangleIcon.js',
  WebflowIcon: './WebflowIcon.js',
  WebhookOffIcon: './WebhookOffIcon.js',
  WebhookIcon: './WebhookIcon.js',
  WechatIcon: './WechatIcon.js',
  WeddingIcon: './WeddingIcon.js',
  WeightScale01Icon: './WeightScale01Icon.js',
  WeightScaleIcon: './WeightScaleIcon.js',
  WeightIcon: './WeightIcon.js',
  WellnessIcon: './WellnessIcon.js',
  WhatsappBusinessIcon: './WhatsappBusinessIcon.js',
  WhatsappIcon: './WhatsappIcon.js',
  WheatOffIcon: './WheatOffIcon.js',
  WheatIcon: './WheatIcon.js',
  WheelchairIcon: './WheelchairIcon.js',
  WhiskIcon: './WhiskIcon.js',
  WhistleIcon: './WhistleIcon.js',
  WhiteboardIcon: './WhiteboardIcon.js',
  Wifi01Icon: './Wifi01Icon.js',
  Wifi02Icon: './Wifi02Icon.js',
  WifiCircleIcon: './WifiCircleIcon.js',
  WifiConnected01Icon: './WifiConnected01Icon.js',
  WifiConnected02Icon: './WifiConnected02Icon.js',
  WifiConnected03Icon: './WifiConnected03Icon.js',
  WifiDisconnected01Icon: './WifiDisconnected01Icon.js',
  WifiDisconnected02Icon: './WifiDisconnected02Icon.js',
  WifiDisconnected03Icon: './WifiDisconnected03Icon.js',
  WifiDisconnected04Icon: './WifiDisconnected04Icon.js',
  WifiError01Icon: './WifiError01Icon.js',
  WifiError02Icon: './WifiError02Icon.js',
  WifiFullSignalIcon: './WifiFullSignalIcon.js',
  WifiLocationIcon: './WifiLocationIcon.js',
  WifiLockIcon: './WifiLockIcon.js',
  WifiLowSignalIcon: './WifiLowSignalIcon.js',
  WifiMediumSignalIcon: './WifiMediumSignalIcon.js',
  WifiNoSignalIcon: './WifiNoSignalIcon.js',
  WifiOff01Icon: './WifiOff01Icon.js',
  WifiOff02Icon: './WifiOff02Icon.js',
  WifiPenIcon: './WifiPenIcon.js',
  WifiSquareIcon: './WifiSquareIcon.js',
  WifiSyncIcon: './WifiSyncIcon.js',
  WifiUnlockIcon: './WifiUnlockIcon.js',
  WikipediaIcon: './WikipediaIcon.js',
  WindPower01Icon: './WindPower01Icon.js',
  WindPower02Icon: './WindPower02Icon.js',
  WindPower03Icon: './WindPower03Icon.js',
  WindPowerIcon: './WindPowerIcon.js',
  WindSurfIcon: './WindSurfIcon.js',
  WindTurbineIcon: './WindTurbineIcon.js',
  WindowsNewIcon: './WindowsNewIcon.js',
  WindowsOldIcon: './WindowsOldIcon.js',
  WinkIcon: './WinkIcon.js',
  WinkingIcon: './WinkingIcon.js',
  WirelessCloudAccessIcon: './WirelessCloudAccessIcon.js',
  WirelessIcon: './WirelessIcon.js',
  WiseIcon: './WiseIcon.js',
  WomanIcon: './WomanIcon.js',
  WordpressIcon: './WordpressIcon.js',
  WorkAlertIcon: './WorkAlertIcon.js',
  WorkHistoryIcon: './WorkHistoryIcon.js',
  WorkIcon: './WorkIcon.js',
  WorkUpdateIcon: './WorkUpdateIcon.js',
  WorkflowCircle01Icon: './WorkflowCircle01Icon.js',
  WorkflowCircle02Icon: './WorkflowCircle02Icon.js',
  WorkflowCircle03Icon: './WorkflowCircle03Icon.js',
  WorkflowCircle04Icon: './WorkflowCircle04Icon.js',
  WorkflowCircle05Icon: './WorkflowCircle05Icon.js',
  WorkflowCircle06Icon: './WorkflowCircle06Icon.js',
  WorkflowSquare01Icon: './WorkflowSquare01Icon.js',
  WorkflowSquare02Icon: './WorkflowSquare02Icon.js',
  WorkflowSquare03Icon: './WorkflowSquare03Icon.js',
  WorkflowSquare04Icon: './WorkflowSquare04Icon.js',
  WorkflowSquare05Icon: './WorkflowSquare05Icon.js',
  WorkflowSquare06Icon: './WorkflowSquare06Icon.js',
  WorkflowSquare07Icon: './WorkflowSquare07Icon.js',
  WorkflowSquare08Icon: './WorkflowSquare08Icon.js',
  WorkflowSquare09Icon: './WorkflowSquare09Icon.js',
  WorkflowSquare10Icon: './WorkflowSquare10Icon.js',
  WorkoutBattleRopesIcon: './WorkoutBattleRopesIcon.js',
  WorkoutGymnasticsIcon: './WorkoutGymnasticsIcon.js',
  WorkoutKickingIcon: './WorkoutKickingIcon.js',
  WorkoutRunIcon: './WorkoutRunIcon.js',
  WorkoutSportIcon: './WorkoutSportIcon.js',
  WorkoutSquatsIcon: './WorkoutSquatsIcon.js',
  WorkoutStretchingIcon: './WorkoutStretchingIcon.js',
  WorkoutWarmUpIcon: './WorkoutWarmUpIcon.js',
  WorryIcon: './WorryIcon.js',
  WpsOfficeRectangleIcon: './WpsOfficeRectangleIcon.js',
  WpsOfficeIcon: './WpsOfficeIcon.js',
  Wrench01Icon: './Wrench01Icon.js',
  Wrench02Icon: './Wrench02Icon.js',
  WuduIcon: './WuduIcon.js',
  XRayIcon: './XRayIcon.js',
  XVariableCircleIcon: './XVariableCircleIcon.js',
  XVariableSquareIcon: './XVariableSquareIcon.js',
  XVariableIcon: './XVariableIcon.js',
  XingIcon: './XingIcon.js',
  Xls01Icon: './Xls01Icon.js',
  Xls02Icon: './Xls02Icon.js',
  Xml01Icon: './Xml01Icon.js',
  Xml02Icon: './Xml02Icon.js',
  Xsl01Icon: './Xsl01Icon.js',
  Xsl02Icon: './Xsl02Icon.js',
  YelpIcon: './YelpIcon.js',
  YenCircleIcon: './YenCircleIcon.js',
  YenReceiveIcon: './YenReceiveIcon.js',
  YenSendIcon: './YenSendIcon.js',
  YenSquareIcon: './YenSquareIcon.js',
  YenIcon: './YenIcon.js',
  Yoga01Icon: './Yoga01Icon.js',
  Yoga02Icon: './Yoga02Icon.js',
  Yoga03Icon: './Yoga03Icon.js',
  YogaBallIcon: './YogaBallIcon.js',
  YogaMatIcon: './YogaMatIcon.js',
  YogurtIcon: './YogurtIcon.js',
  YoutubeIcon: './YoutubeIcon.js',
  YurtIcon: './YurtIcon.js',
  ZakatIcon: './ZakatIcon.js',
  ZapIcon: './ZapIcon.js',
  ZeppelinIcon: './ZeppelinIcon.js',
  ZeroCircleIcon: './ZeroCircleIcon.js',
  ZeroSquareIcon: './ZeroSquareIcon.js',
  Zip01Icon: './Zip01Icon.js',
  Zip02Icon: './Zip02Icon.js',
  ZoomCircleIcon: './ZoomCircleIcon.js',
  ZoomInAreaIcon: './ZoomInAreaIcon.js',
  ZoomOutAreaIcon: './ZoomOutAreaIcon.js',
  ZoomSquareIcon: './ZoomSquareIcon.js',
  ZoomIcon: './ZoomIcon.js',
  ZshIcon: './ZshIcon.js',
  ZzzIcon: './ZzzIcon.js',
};

// In-memory cache
const iconCache = new Map<string, IconSvgObject>();

export async function loadIcon(iconName: string): Promise<IconSvgObject> {
  if (iconCache.has(iconName)) return iconCache.get(iconName)!;
  const p = paths[iconName];
  if (!p) throw new Error(`Icon not found: ${iconName}`);
  const mod = await import(p);
  const iconData = mod.default || mod;
  iconCache.set(iconName, iconData);
  return iconData;
}

export async function loadIcons(iconNames: string[]): Promise<IconSvgObject[]> {
  return Promise.all(iconNames.map(loadIcon));
}

export function iconExists(iconName: string): boolean {
  return !!paths[iconName];
}

export function clearIconCache(): void {
  iconCache.clear();
}

export function getCacheSize(): number {
  return iconCache.size;
}

// Style-specific aliases for multi-package usage
export const loadIconStrokeRounded = loadIcon;
export const loadIconsStrokeRounded = loadIcons;

export default { loadIcon, loadIcons, iconExists, clearIconCache, getCacheSize };
